import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../../components/Layout';
import { useModal } from '../../context/ModalContext';
import api from '../../utils/api';
import {
  FiDollarSign,
  FiBook,
  FiFileText,
  FiTrendingUp,
  FiCalendar,
  FiRefreshCw,
  FiPlus,
  FiChevronDown,
  FiChevronUp,
  FiDownload,
  FiRotateCcw,
  FiLock,
  FiUnlock,
  FiAlertTriangle,
  FiCheckCircle,
  FiFilter,
  FiX,
  FiList,
  FiPieChart,
  FiInbox,
  FiEdit3,
  FiPlay,
  FiSearch,
  FiArrowRight,
  FiLayers,
  FiActivity,
  FiEye,
  FiClock,
  FiSlash,
  FiRepeat,
  FiTarget,
  FiGlobe,
  FiUsers,
  FiShoppingCart,
  FiCreditCard,
  FiBarChart2,
  FiUploadCloud,
  FiDatabase
} from 'react-icons/fi';

// ─── Constants ────────────────────────────────────────────────
const TABS = {
  DASHBOARD: 'dashboard',
  JOURNALS: 'journals',
  LEDGER: 'ledger',
  EXPENSES: 'expenses',
  TRIAL_BALANCE: 'trial_balance',
  BALANCE_SHEET: 'balance_sheet',
  CASH_FLOW: 'cash_flow',
  PNL: 'pnl',
  TAX: 'tax',
  PERIODS: 'periods',
  COA: 'coa',
  OUTBOX: 'outbox',
  AUDIT: 'audit',
  BUDGET: 'budget',
  RECURRING: 'recurring',
  CURRENCY: 'currency',
  VENDORS: 'vendors',
  RECEIVABLES: 'receivables',
  PAYABLES: 'payables',
  RECONCILIATION: 'reconciliation',
  RATIOS: 'ratios',
  IMPORT_EXPORT: 'import_export',
};

const TAB_LIST = [
  { key: TABS.DASHBOARD,     label: 'Dashboard',     icon: FiTrendingUp },
  { key: TABS.JOURNALS,      label: 'Journals',      icon: FiBook },
  { key: TABS.LEDGER,        label: 'Ledger',        icon: FiSearch },
  { key: TABS.EXPENSES,      label: 'Expenses',      icon: FiDollarSign },
  { key: TABS.COA,           label: 'Chart of Accounts', icon: FiList },
  { key: TABS.TRIAL_BALANCE, label: 'Trial Balance', icon: FiFileText },
  { key: TABS.BALANCE_SHEET, label: 'Balance Sheet', icon: FiLayers },
  { key: TABS.CASH_FLOW,     label: 'Cash Flow',     icon: FiActivity },
  { key: TABS.PNL,           label: 'P&L',           icon: FiPieChart },
  { key: TABS.TAX,           label: 'Tax Summary',   icon: FiDollarSign },
  { key: TABS.PERIODS,       label: 'Periods',       icon: FiCalendar },
  { key: TABS.BUDGET,        label: 'Budgets',       icon: FiTarget },
  { key: TABS.RECURRING,     label: 'Recurring',     icon: FiRepeat },
  { key: TABS.CURRENCY,      label: 'Currencies',    icon: FiGlobe },
  { key: TABS.VENDORS,       label: 'Vendors',       icon: FiUsers },
  { key: TABS.RECEIVABLES,   label: 'Receivables',   icon: FiArrowRight },
  { key: TABS.PAYABLES,      label: 'Payables',      icon: FiShoppingCart },
  { key: TABS.RECONCILIATION, label: 'Bank Recon',   icon: FiCreditCard },
  { key: TABS.RATIOS,         label: 'Ratios',       icon: FiBarChart2 },
  { key: TABS.IMPORT_EXPORT, label: 'Import/Export', icon: FiDatabase },
  { key: TABS.OUTBOX,        label: 'Outbox',        icon: FiInbox },
  { key: TABS.AUDIT,         label: 'Audit Log',     icon: FiEye },
];

function formatCurrency(v) {
  const n = Number(v || 0);
  return n.toLocaleString('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 });
}

function formatDate(v) {
  if (!v) return '-';
  return new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatDateTime(v) {
  if (!v) return '-';
  const d = new Date(v.includes('Z') || v.includes('+') ? v : `${v}Z`);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata', day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

// ─── Badge helpers ────────────────────────────────────────────
function StatusBadge({ status }) {
  const colors = {
    posted:     'bg-green-100 text-green-800',
    reversed:   'bg-red-100 text-red-800',
    pending:    'bg-yellow-100 text-yellow-800',
    void:       'bg-gray-200 text-gray-600',
    open:       'bg-blue-100 text-blue-800',
    closing:    'bg-yellow-100 text-yellow-800',
    closed:     'bg-gray-200 text-gray-600',
    processing: 'bg-blue-100 text-blue-800',
    completed:  'bg-green-100 text-green-800',
    failed:     'bg-red-100 text-red-800',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-700'}`}>
      {status}
    </span>
  );
}

function AccountTypeBadge({ type }) {
  const colors = {
    asset:     'bg-blue-100 text-blue-800',
    liability: 'bg-orange-100 text-orange-800',
    equity:    'bg-purple-100 text-purple-800',
    revenue:   'bg-green-100 text-green-800',
    expense:   'bg-red-100 text-red-800',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${colors[type] || 'bg-gray-100 text-gray-700'}`}>
      {type}
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════
function Accounting() {
  const { alert: showAlert, confirm } = useModal();
  const [activeTab, setActiveTab] = useState(TABS.DASHBOARD);
  const [loading, setLoading] = useState(false);

  // Dashboard
  const [summary, setSummary] = useState(null);

  // Journals
  const [journals, setJournals] = useState([]);
  const [journalTotal, setJournalTotal] = useState(0);
  const [journalFilters, setJournalFilters] = useState({ source_type: '', status: '', from_date: '', to_date: '' });
  const [expandedJournal, setExpandedJournal] = useState(null);

  // Expenses
  const [expenses, setExpenses] = useState([]);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [expenseForm, setExpenseForm] = useState({ expense_date: '', account_id: '', payment_account_id: '', amount: '', description: '', vendor: '', vendor_id: '', currency_code: '' });

  // Chart of Accounts
  const [accounts, setAccounts] = useState([]);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [accountForm, setAccountForm] = useState({ account_code: '', account_name: '', account_type: 'expense', account_category: '', description: '' });

  // Trial Balance
  const [trialBalance, setTrialBalance] = useState({ trial_balance: [], totals: {} });
  const [tbFilters, setTbFilters] = useState({ from_date: '', to_date: '' });

  // P&L
  const [pnl, setPnl] = useState({ revenue: [], expenses: [], totals: {} });
  const [pnlFilters, setPnlFilters] = useState({ from_date: '', to_date: '' });

  // Tax Summary
  const [taxSummary, setTaxSummary] = useState({ tax_accounts: [], total_tax_payable: 0 });
  const [taxFilters, setTaxFilters] = useState({ from_date: '', to_date: '' });

  // Periods
  const [periods, setPeriods] = useState([]);

  // Outbox & Failures
  const [outboxEntries, setOutboxEntries] = useState([]);
  const [outboxTotal, setOutboxTotal] = useState(0);
  const [outboxFilter, setOutboxFilter] = useState('');
  const [postingFailures, setPostingFailures] = useState([]);
  const [failuresTotal, setFailuresTotal] = useState(0);
  const [failureFilter, setFailureFilter] = useState('false'); // show unresolved by default

  // Manual Journal
  const [showManualJournalModal, setShowManualJournalModal] = useState(false);
  const [manualJournalForm, setManualJournalForm] = useState({ entry_date: '', description: '', memo: '', currency_code: '', lines: [{ account_id: '', debit: '', credit: '', line_description: '' }, { account_id: '', debit: '', credit: '', line_description: '' }] });

  // Account Ledger
  const [ledgerAccountId, setLedgerAccountId] = useState('');
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [ledgerAccount, setLedgerAccount] = useState(null);
  const [ledgerTotals, setLedgerTotals] = useState({ total_debit: 0, total_credit: 0, balance: 0 });
  const [ledgerTotal, setLedgerTotal] = useState(0);
  const [ledgerFilters, setLedgerFilters] = useState({ from_date: '', to_date: '' });

  // Balance Sheet
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [bsDate, setBsDate] = useState('');

  // Cash Flow
  const [cashFlow, setCashFlow] = useState(null);
  const [cfFilters, setCfFilters] = useState({ from_date: '', to_date: '' });

  // Fiscal Years
  const [fiscalYears, setFiscalYears] = useState([]);
  const [showFYModal, setShowFYModal] = useState(false);
  const [fyForm, setFyForm] = useState({ label: '', start_date: '', end_date: '' });

  // Audit Log
  const [auditLog, setAuditLog] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditFilters, setAuditFilters] = useState({ action: '', entity_type: '', from_date: '', to_date: '', search: '' });

  // Aging Report
  const [aging, setAging] = useState(null);
  const [agingType, setAgingType] = useState('receivable');

  // Budgets
  const [budgets, setBudgets] = useState([]);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [budgetForm, setBudgetForm] = useState({ label: '', fiscal_year_id: '', period_start: '', period_end: '', notes: '' });
  const [selectedBudget, setSelectedBudget] = useState(null);
  const [budgetComparison, setBudgetComparison] = useState(null);
  const [showBudgetLineModal, setShowBudgetLineModal] = useState(false);
  const [budgetLineForm, setBudgetLineForm] = useState({ account_id: '', budgeted_amount: '', notes: '' });
  const [budgetLines, setBudgetLines] = useState([]);

  // Recurring Journals
  const [recurringJournals, setRecurringJournals] = useState([]);
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [recurringForm, setRecurringForm] = useState({ name: '', description: '', memo: '', frequency: 'monthly', next_run_date: '', end_date: '', line_template: [{ account_id: '', debit: '', credit: '', line_description: '' }, { account_id: '', debit: '', credit: '', line_description: '' }] });

  // Multi-Currency
  const [currencies, setCurrencies] = useState([]);
  const [exchangeRates, setExchangeRates] = useState([]);
  const [showCurrencyModal, setShowCurrencyModal] = useState(false);
  const [currencyForm, setCurrencyForm] = useState({ code: '', name: '', symbol: '', decimal_places: 2 });
  const [showRateModal, setShowRateModal] = useState(false);
  const [rateForm, setRateForm] = useState({ from_currency: '', to_currency: '', rate: '', effective_date: '', notes: '' });

  // Vendors
  const [vendors, setVendors] = useState([]);
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [vendorForm, setVendorForm] = useState({ name: '', display_name: '', email: '', phone: '', address: '', city: '', state: '', gstin: '', pan: '', default_expense_account_id: '', default_payment_account_id: '', payment_terms: '30', notes: '' });
  const [editingVendor, setEditingVendor] = useState(null);
  const [vendorSearch, setVendorSearch] = useState('');
  const [showInactiveVendors, setShowInactiveVendors] = useState(false);

  // Accounts Receivable
  const [arCustomers, setArCustomers] = useState([]);
  const [arInvoices, setArInvoices] = useState([]);
  const [arSummary, setArSummary] = useState(null);
  const [showARCustomerModal, setShowARCustomerModal] = useState(false);
  const [arCustomerForm, setArCustomerForm] = useState({ name: '', display_name: '', email: '', phone: '', address: '', city: '', state: '', gstin: '', pan: '', default_revenue_account_id: '', default_receivable_account_id: '', payment_terms: '30', credit_limit: '', notes: '' });
  const [editingARCustomer, setEditingARCustomer] = useState(null);
  const [arCustomerSearch, setArCustomerSearch] = useState('');
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({ customer_id: '', invoice_date: '', due_date: '', revenue_account_id: '', receivable_account_id: '', tax_account_id: '', notes: '' });
  const [invoiceLines, setInvoiceLines] = useState([{ description: '', quantity: 1, unit_price: '', tax_rate: '0', account_id: '' }]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentInvoice, setPaymentInvoice] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ amount: '', payment_date: '', payment_method: 'bank_transfer', reference_number: '', deposit_account_id: '', notes: '' });
  const [arInvoiceFilter, setArInvoiceFilter] = useState('');
  const [arView, setArView] = useState('invoices');

  // Accounts Payable
  const [apBills, setApBills] = useState([]);
  const [apSummary, setApSummary] = useState(null);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [showBillModal, setShowBillModal] = useState(false);
  const [billForm, setBillForm] = useState({ vendor_id: '', bill_date: '', due_date: '', expense_account_id: '', payable_account_id: '', tax_account_id: '', vendor_invoice_number: '', notes: '' });
  const [billLines, setBillLines] = useState([{ description: '', quantity: 1, unit_price: '', tax_rate: '0', account_id: '' }]);
  const [showBillPayModal, setShowBillPayModal] = useState(false);
  const [billPayInvoice, setBillPayInvoice] = useState(null);
  const [billPayForm, setBillPayForm] = useState({ amount: '', payment_date: '', payment_method: 'bank_transfer', reference_number: '', payment_account_id: '', notes: '' });
  const [apBillFilter, setApBillFilter] = useState('');
  const [apView, setApView] = useState('bills');
  const [showPOModal, setShowPOModal] = useState(false);
  const [poForm, setPoForm] = useState({ vendor_id: '', order_date: '', expected_date: '', notes: '' });
  const [poLines, setPoLines] = useState([{ description: '', quantity: 1, unit_price: '', tax_rate: '0', account_id: '' }]);

  // Bank Reconciliation
  const [bankAccounts, setBankAccounts] = useState([]);
  const [bankTransactions, setBankTransactions] = useState([]);
  const [reconciliations, setReconciliations] = useState([]);
  const [bankReconSummary, setBankReconSummary] = useState(null);
  const [showBankAccountModal, setShowBankAccountModal] = useState(false);
  const [bankAccountForm, setBankAccountForm] = useState({ account_name: '', bank_name: '', account_number: '', ifsc_code: '', account_type: 'checking', coa_account_id: '', currency: 'INR', opening_balance: '0' });
  const [showReconModal, setShowReconModal] = useState(false);
  const [reconForm, setReconForm] = useState({ bank_account_id: '', reconciliation_date: '', period_start: '', period_end: '', statement_balance: '', notes: '' });
  const [showImportModal, setShowImportModal] = useState(false);
  const [importForm, setImportForm] = useState({ bank_account_id: '', csvText: '' });
  const [reconView, setReconView] = useState('accounts');
  const [reconDetail, setReconDetail] = useState(null);
  const [selectedBankAccountFilter, setSelectedBankAccountFilter] = useState('');
  const [showManualTxnModal, setShowManualTxnModal] = useState(false);
  const [manualTxnForm, setManualTxnForm] = useState({ bank_account_id: '', transaction_date: '', description: '', reference: '', amount: '', category: '', payee: '' });

  // Financial Ratios
  const [financialRatios, setFinancialRatios] = useState(null);
  const [ratioDateRange, setRatioDateRange] = useState({ as_of_date: '', period_start: '', period_end: '' });

  // Data Import / Export
  const [importExportView, setImportExportView] = useState('export');
  const [importType, setImportType] = useState('chart_of_accounts');
  const [importCsvText, setImportCsvText] = useState('');
  const [importResult, setImportResult] = useState(null);
  const [importTemplates, setImportTemplates] = useState(null);
  const [exportLoading, setExportLoading] = useState('');

  // ─── Loaders ──────────────────────────────────────────────
  const fetchSummary = useCallback(async () => {
    try { const { data } = await api.get('/api/accounting/summary'); setSummary(data.summary); } catch (e) { console.error(e); }
  }, []);

  const fetchJournals = useCallback(async () => {
    try {
      const params = {};
      if (journalFilters.source_type) params.source_type = journalFilters.source_type;
      if (journalFilters.status) params.status = journalFilters.status;
      if (journalFilters.from_date) params.from_date = journalFilters.from_date;
      if (journalFilters.to_date) params.to_date = journalFilters.to_date;
      const { data } = await api.get('/api/accounting/journals', { params });
      setJournals(data.journals || []);
      setJournalTotal(data.total || 0);
    } catch (e) { console.error(e); }
  }, [journalFilters]);

  const fetchExpenses = useCallback(async () => {
    try { const { data } = await api.get('/api/accounting/expenses'); setExpenses(data.expenses || []); } catch (e) { console.error(e); }
  }, []);

  const fetchAccounts = useCallback(async () => {
    try { const { data } = await api.get('/api/accounting/coa'); setAccounts(data.accounts || []); } catch (e) { console.error(e); }
  }, []);

  const fetchTrialBalance = useCallback(async () => {
    try {
      const params = {};
      if (tbFilters.from_date) params.from_date = tbFilters.from_date;
      if (tbFilters.to_date) params.to_date = tbFilters.to_date;
      const { data } = await api.get('/api/accounting/trial-balance', { params });
      setTrialBalance(data);
    } catch (e) { console.error(e); }
  }, [tbFilters]);

  const fetchPnl = useCallback(async () => {
    try {
      const params = {};
      if (pnlFilters.from_date) params.from_date = pnlFilters.from_date;
      if (pnlFilters.to_date) params.to_date = pnlFilters.to_date;
      const { data } = await api.get('/api/accounting/pnl', { params });
      setPnl(data);
    } catch (e) { console.error(e); }
  }, [pnlFilters]);

  const fetchTaxSummary = useCallback(async () => {
    try {
      const params = {};
      if (taxFilters.from_date) params.from_date = taxFilters.from_date;
      if (taxFilters.to_date) params.to_date = taxFilters.to_date;
      const { data } = await api.get('/api/accounting/tax-summary', { params });
      setTaxSummary(data);
    } catch (e) { console.error(e); }
  }, [taxFilters]);

  const fetchPeriods = useCallback(async () => {
    try { const { data } = await api.get('/api/accounting/periods'); setPeriods(data.periods || []); } catch (e) { console.error(e); }
  }, []);

  const fetchOutbox = useCallback(async () => {
    try {
      const params = {};
      if (outboxFilter) params.status = outboxFilter;
      const { data } = await api.get('/api/accounting/outbox', { params });
      setOutboxEntries(data.entries || []);
      setOutboxTotal(data.total || 0);
    } catch (e) { console.error(e); }
  }, [outboxFilter]);

  const fetchFailures = useCallback(async () => {
    try {
      const params = {};
      if (failureFilter) params.resolved = failureFilter;
      const { data } = await api.get('/api/accounting/failures', { params });
      setPostingFailures(data.failures || []);
      setFailuresTotal(data.total || 0);
    } catch (e) { console.error(e); }
  }, [failureFilter]);

  const fetchLedger = useCallback(async () => {
    if (!ledgerAccountId) return;
    try {
      const params = {};
      if (ledgerFilters.from_date) params.from_date = ledgerFilters.from_date;
      if (ledgerFilters.to_date) params.to_date = ledgerFilters.to_date;
      const { data } = await api.get(`/api/accounting/ledger/${ledgerAccountId}`, { params });
      setLedgerAccount(data.account || null);
      setLedgerEntries(data.entries || []);
      setLedgerTotals(data.totals || { total_debit: 0, total_credit: 0, balance: 0 });
      setLedgerTotal(data.total || 0);
    } catch (e) { console.error(e); }
  }, [ledgerAccountId, ledgerFilters]);

  const fetchBalanceSheet = useCallback(async () => {
    try {
      const params = {};
      if (bsDate) params.as_of_date = bsDate;
      const { data } = await api.get('/api/accounting/balance-sheet', { params });
      setBalanceSheet(data);
    } catch (e) { console.error(e); }
  }, [bsDate]);

  const fetchCashFlow = useCallback(async () => {
    try {
      const params = {};
      if (cfFilters.from_date) params.from_date = cfFilters.from_date;
      if (cfFilters.to_date) params.to_date = cfFilters.to_date;
      const { data } = await api.get('/api/accounting/cash-flow', { params });
      setCashFlow(data);
    } catch (e) { console.error(e); }
  }, [cfFilters]);

  const fetchFiscalYears = useCallback(async () => {
    try {
      const { data } = await api.get('/api/accounting/fiscal-years');
      setFiscalYears(data.fiscal_years || []);
    } catch (e) { console.error(e); }
  }, []);

  const fetchAuditLog = useCallback(async () => {
    try {
      const params = {};
      if (auditFilters.action) params.action = auditFilters.action;
      if (auditFilters.entity_type) params.entity_type = auditFilters.entity_type;
      if (auditFilters.from_date) params.from_date = auditFilters.from_date;
      if (auditFilters.to_date) params.to_date = auditFilters.to_date;
      if (auditFilters.search) params.search = auditFilters.search;
      const { data } = await api.get('/api/accounting/audit-log', { params });
      setAuditLog(data.audit_log || []);
      setAuditTotal(data.total || 0);
    } catch (e) { console.error(e); }
  }, [auditFilters]);

  const fetchAging = useCallback(async () => {
    try {
      const { data } = await api.get('/api/accounting/aging', { params: { type: agingType } });
      setAging(data);
    } catch (e) { console.error(e); }
  }, [agingType]);

  const fetchBudgets = useCallback(async () => {
    try {
      const { data } = await api.get('/api/accounting/budgets');
      setBudgets(data.budgets || []);
    } catch (e) { console.error(e); }
  }, []);

  const fetchBudgetLines = useCallback(async (budgetId) => {
    try {
      const { data } = await api.get(`/api/accounting/budgets/${budgetId}/lines`);
      setBudgetLines(data.lines || []);
    } catch (e) { console.error(e); }
  }, []);

  const fetchBudgetVsActuals = useCallback(async (budgetId) => {
    try {
      const { data } = await api.get(`/api/accounting/budgets/${budgetId}/vs-actuals`);
      setBudgetComparison(data);
    } catch (e) { console.error(e); }
  }, []);

  const fetchRecurringJournals = useCallback(async () => {
    try {
      const { data } = await api.get('/api/accounting/recurring');
      setRecurringJournals(data.recurring_journals || []);
    } catch (e) { console.error(e); }
  }, []);

  const fetchCurrencies = useCallback(async () => {
    try {
      const { data } = await api.get('/api/accounting/currencies');
      setCurrencies(data.currencies || []);
    } catch (e) { console.error(e); }
  }, []);

  const fetchExchangeRates = useCallback(async () => {
    try {
      const { data } = await api.get('/api/accounting/exchange-rates');
      setExchangeRates(data.exchange_rates || []);
    } catch (e) { console.error(e); }
  }, []);

  const fetchVendors = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (showInactiveVendors) params.append('show_inactive', 'true');
      if (vendorSearch) params.append('search', vendorSearch);
      const { data } = await api.get(`/api/accounting/vendors?${params}`);
      setVendors(data.vendors || []);
    } catch (e) { console.error(e); }
  }, [showInactiveVendors, vendorSearch]);

  const fetchARCustomers = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (arCustomerSearch) params.append('search', arCustomerSearch);
      const { data } = await api.get(`/api/accounting/ar/customers?${params}`);
      setArCustomers(data.customers || []);
    } catch (e) { console.error(e); }
  }, [arCustomerSearch]);

  const fetchARInvoices = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (arInvoiceFilter) params.append('status', arInvoiceFilter);
      const { data } = await api.get(`/api/accounting/ar/invoices?${params}`);
      setArInvoices(data.invoices || []);
    } catch (e) { console.error(e); }
  }, [arInvoiceFilter]);

  const fetchARSummary = useCallback(async () => {
    try {
      const { data } = await api.get('/api/accounting/ar/summary');
      setArSummary(data.summary || null);
    } catch (e) { console.error(e); }
  }, []);

  const fetchAPBills = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (apBillFilter) params.append('status', apBillFilter);
      const { data } = await api.get(`/api/accounting/ap/bills?${params}`);
      setApBills(data.bills || []);
    } catch (e) { console.error(e); }
  }, [apBillFilter]);

  const fetchAPSummary = useCallback(async () => {
    try {
      const { data } = await api.get('/api/accounting/ap/summary');
      setApSummary(data.summary || null);
    } catch (e) { console.error(e); }
  }, []);

  const fetchPurchaseOrders = useCallback(async () => {
    try {
      const { data } = await api.get('/api/accounting/ap/purchase-orders');
      setPurchaseOrders(data.purchase_orders || []);
    } catch (e) { console.error(e); }
  }, []);

  const fetchBankAccounts = useCallback(async () => {
    try { const { data } = await api.get('/api/accounting/bank-accounts'); setBankAccounts(data || []); } catch (e) { console.error(e); }
  }, []);

  const fetchBankTransactions = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedBankAccountFilter) params.append('bank_account_id', selectedBankAccountFilter);
      const { data } = await api.get(`/api/accounting/bank-transactions?${params}`);
      setBankTransactions(data || []);
    } catch (e) { console.error(e); }
  }, [selectedBankAccountFilter]);

  const fetchReconciliations = useCallback(async () => {
    try { const { data } = await api.get('/api/accounting/reconciliations'); setReconciliations(data || []); } catch (e) { console.error(e); }
  }, []);

  const fetchBankReconSummary = useCallback(async () => {
    try { const { data } = await api.get('/api/accounting/bank-reconciliation-summary'); setBankReconSummary(data); } catch (e) { console.error(e); }
  }, []);

  const fetchFinancialRatios = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (ratioDateRange.as_of_date) params.append('as_of_date', ratioDateRange.as_of_date);
      if (ratioDateRange.period_start) params.append('period_start', ratioDateRange.period_start);
      if (ratioDateRange.period_end) params.append('period_end', ratioDateRange.period_end);
      const { data } = await api.get(`/api/accounting/financial-ratios?${params}`);
      setFinancialRatios(data);
    } catch (e) { console.error(e); }
  }, [ratioDateRange]);

  const fetchImportTemplates = useCallback(async () => {
    try {
      const { data } = await api.get('/api/accounting/import/templates');
      setImportTemplates(data.templates);
    } catch (e) { console.error(e); }
  }, []);

  // ─── Tab change data loader ───────────────────────────────
  useEffect(() => {
    setLoading(true);
    const loadTab = async () => {
      switch (activeTab) {
        case TABS.DASHBOARD: await fetchSummary(); break;
        case TABS.JOURNALS: await Promise.all([fetchJournals(), fetchCurrencies()]); break;
        case TABS.LEDGER: await fetchAccounts(); if (ledgerAccountId) await fetchLedger(); break;
        case TABS.EXPENSES: await Promise.all([fetchExpenses(), fetchAccounts(), fetchCurrencies(), fetchVendors()]); break;
        case TABS.COA: await fetchAccounts(); break;
        case TABS.TRIAL_BALANCE: await fetchTrialBalance(); break;
        case TABS.BALANCE_SHEET: await fetchBalanceSheet(); break;
        case TABS.CASH_FLOW: await fetchCashFlow(); break;
        case TABS.PNL: await fetchPnl(); break;
        case TABS.TAX: await fetchTaxSummary(); break;
        case TABS.PERIODS: await Promise.all([fetchPeriods(), fetchFiscalYears()]); break;
        case TABS.OUTBOX: await Promise.all([fetchOutbox(), fetchFailures()]); break;
        case TABS.AUDIT: await fetchAuditLog(); break;
        case TABS.BUDGET: await Promise.all([fetchBudgets(), fetchAccounts(), fetchFiscalYears()]); break;
        case TABS.RECURRING: await Promise.all([fetchRecurringJournals(), fetchAccounts()]); break;
        case TABS.CURRENCY: await Promise.all([fetchCurrencies(), fetchExchangeRates()]); break;
        case TABS.VENDORS: await Promise.all([fetchVendors(), fetchAccounts()]); break;
        case TABS.RECEIVABLES: await Promise.all([fetchARCustomers(), fetchARInvoices(), fetchARSummary(), fetchAccounts()]); break;
        case TABS.PAYABLES: await Promise.all([fetchAPBills(), fetchAPSummary(), fetchPurchaseOrders(), fetchVendors(), fetchAccounts()]); break;
        case TABS.RECONCILIATION: await Promise.all([fetchBankAccounts(), fetchBankTransactions(), fetchReconciliations(), fetchBankReconSummary(), fetchAccounts()]); break;
        case TABS.RATIOS: await fetchFinancialRatios(); break;
        case TABS.IMPORT_EXPORT: await fetchImportTemplates(); break;
        default: break;
      }
      setLoading(false);
    };
    loadTab();
  }, [activeTab, fetchSummary, fetchJournals, fetchExpenses, fetchAccounts, fetchTrialBalance, fetchBalanceSheet, fetchCashFlow, fetchPnl, fetchTaxSummary, fetchPeriods, fetchFiscalYears, fetchOutbox, fetchFailures, fetchLedger, ledgerAccountId, fetchAuditLog, fetchBudgets, fetchRecurringJournals, fetchCurrencies, fetchExchangeRates, fetchVendors, fetchARCustomers, fetchARInvoices, fetchARSummary, fetchAPBills, fetchAPSummary, fetchPurchaseOrders, fetchBankAccounts, fetchBankTransactions, fetchReconciliations, fetchBankReconSummary, fetchFinancialRatios, fetchImportTemplates]);

  // ─── Actions ──────────────────────────────────────────────
  const handleReverseJournal = async (journalId) => {
    const reason = window.prompt('Enter reason for reversal:');
    if (!reason || !reason.trim()) return;
    try {
      await api.post(`/api/accounting/journals/${journalId}/reverse`, { reason });
      showAlert('Journal reversed successfully', 'success');
      fetchJournals();
      fetchSummary();
    } catch (e) {
      showAlert(e.response?.data?.error || 'Failed to reverse journal', 'error');
    }
  };

  const handleCreateExpense = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/accounting/expenses', expenseForm);
      showAlert('Expense created and posted', 'success');
      setShowExpenseModal(false);
      setExpenseForm({ expense_date: '', account_id: '', payment_account_id: '', amount: '', description: '', vendor: '', vendor_id: '', currency_code: '' });
      fetchExpenses();
      fetchSummary();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to create expense', 'error');
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/accounting/coa', accountForm);
      showAlert('Account created', 'success');
      setShowAccountModal(false);
      setAccountForm({ account_code: '', account_name: '', account_type: 'expense', account_category: '', description: '' });
      fetchAccounts();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to create account', 'error');
    }
  };

  const handleClosePeriod = async (periodId) => {
    const ok = await confirm('Are you sure you want to close this period? This action cannot be undone.');
    if (!ok) return;
    try {
      await api.post(`/api/accounting/periods/${periodId}/close`);
      showAlert('Period closed successfully', 'success');
      fetchPeriods();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to close period', 'error');
    }
  };

  const handleOpenNextPeriod = async () => {
    try {
      await api.post('/api/accounting/periods/open-next');
      showAlert('New period opened', 'success');
      fetchPeriods();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to open next period', 'error');
    }
  };

  const handleCreateFiscalYear = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/accounting/fiscal-years', fyForm);
      showAlert('Fiscal year created', 'success');
      setShowFYModal(false);
      setFyForm({ label: '', start_date: '', end_date: '' });
      fetchFiscalYears();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to create fiscal year', 'error');
    }
  };

  const handleToggleFiscalYear = async (id, currentActive) => {
    try {
      await api.put(`/api/accounting/fiscal-years/${id}`, { is_active: !currentActive });
      showAlert(`Fiscal year ${currentActive ? 'deactivated' : 'activated'}`, 'success');
      fetchFiscalYears();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to update fiscal year', 'error');
    }
  };

  const handleRetryOutbox = async (entryId) => {
    try {
      await api.post(`/api/accounting/outbox/${entryId}/retry`);
      showAlert('Outbox entry retried', 'success');
      fetchOutbox();
      fetchFailures();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to retry entry', 'error');
    }
  };

  const handleResolveFailure = async (failureId) => {
    const notes = window.prompt('Resolution notes (optional):');
    if (notes === null) return; // cancelled
    try {
      await api.post(`/api/accounting/failures/${failureId}/resolve`, { resolution_notes: notes || 'Manually resolved' });
      showAlert('Failure resolved', 'success');
      fetchFailures();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to resolve', 'error');
    }
  };

  const handleVoidExpense = async (expenseId) => {
    const reason = window.prompt('Enter reason for voiding this expense:');
    if (!reason || !reason.trim()) return;
    try {
      await api.post(`/api/accounting/expenses/${expenseId}/void`, { reason });
      showAlert('Expense voided successfully', 'success');
      fetchExpenses();
      fetchSummary();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to void expense', 'error');
    }
  };

  // ─── Budget Handlers ─────────────────────────────────────
  const handleCreateBudget = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/accounting/budgets', budgetForm);
      showAlert('Budget created', 'success');
      setShowBudgetModal(false);
      setBudgetForm({ label: '', fiscal_year_id: '', period_start: '', period_end: '', notes: '' });
      fetchBudgets();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to create budget', 'error');
    }
  };

  const handleActivateBudget = async (budgetId, newStatus) => {
    try {
      await api.put(`/api/accounting/budgets/${budgetId}`, { status: newStatus });
      showAlert(`Budget ${newStatus}`, 'success');
      fetchBudgets();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to update budget', 'error');
    }
  };

  const handleSelectBudget = async (budget) => {
    setSelectedBudget(budget);
    await fetchBudgetLines(budget.id);
    await fetchBudgetVsActuals(budget.id);
  };

  const handleAddBudgetLine = async (e) => {
    e.preventDefault();
    if (!selectedBudget) return;
    try {
      await api.post(`/api/accounting/budgets/${selectedBudget.id}/lines`, budgetLineForm);
      showAlert('Budget line saved', 'success');
      setShowBudgetLineModal(false);
      setBudgetLineForm({ account_id: '', budgeted_amount: '', notes: '' });
      await fetchBudgetLines(selectedBudget.id);
      await fetchBudgetVsActuals(selectedBudget.id);
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to save budget line', 'error');
    }
  };

  // ─── Recurring Journal Handlers ───────────────────────────
  const handleCreateRecurring = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...recurringForm,
        line_template: recurringForm.line_template
          .filter(l => l.account_id && (Number(l.debit) > 0 || Number(l.credit) > 0))
          .map(l => ({ account_id: l.account_id, debit: Number(l.debit) || 0, credit: Number(l.credit) || 0, line_description: l.line_description || '' })),
      };
      await api.post('/api/accounting/recurring', payload);
      showAlert('Recurring journal created', 'success');
      setShowRecurringModal(false);
      setRecurringForm({ name: '', description: '', memo: '', frequency: 'monthly', next_run_date: '', end_date: '', line_template: [{ account_id: '', debit: '', credit: '', line_description: '' }, { account_id: '', debit: '', credit: '', line_description: '' }] });
      fetchRecurringJournals();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to create recurring journal', 'error');
    }
  };

  const handleExecuteRecurring = async (rjId) => {
    const ok = await confirm('Post this recurring journal entry now?');
    if (!ok) return;
    try {
      const { data } = await api.post(`/api/accounting/recurring/${rjId}/execute`);
      showAlert(data.message || 'Journal posted', 'success');
      fetchRecurringJournals();
      fetchJournals();
      fetchSummary();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to execute', 'error');
    }
  };

  const handleToggleRecurring = async (rjId, isActive) => {
    try {
      await api.put(`/api/accounting/recurring/${rjId}`, { is_active: !isActive });
      showAlert(isActive ? 'Recurring journal paused' : 'Recurring journal activated', 'success');
      fetchRecurringJournals();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to update', 'error');
    }
  };

  const addRecurringLine = () => {
    setRecurringForm(f => ({
      ...f,
      line_template: [...f.line_template, { account_id: '', debit: '', credit: '', line_description: '' }],
    }));
  };

  const removeRecurringLine = (idx) => {
    if (recurringForm.line_template.length <= 2) return;
    setRecurringForm(f => ({
      ...f,
      line_template: f.line_template.filter((_, i) => i !== idx),
    }));
  };

  const updateRecurringLine = (idx, field, value) => {
    setRecurringForm(f => ({
      ...f,
      line_template: f.line_template.map((l, i) => i === idx ? { ...l, [field]: value } : l),
    }));
  };

  // ── Multi-Currency Handlers ──
  const handleCreateCurrency = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/accounting/currencies', currencyForm);
      showAlert('Currency added', 'success');
      setShowCurrencyModal(false);
      setCurrencyForm({ code: '', name: '', symbol: '', decimal_places: 2 });
      fetchCurrencies();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to add currency', 'error');
    }
  };

  const handleToggleCurrency = async (cur) => {
    try {
      await api.put(`/api/accounting/currencies/${cur.id}`, { is_active: !cur.is_active });
      showAlert(cur.is_active ? 'Currency deactivated' : 'Currency activated', 'success');
      fetchCurrencies();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to update currency', 'error');
    }
  };

  const handleSetBaseCurrency = async (currencyCode) => {
    if (!window.confirm(`Set ${currencyCode} as the base currency? This affects all future transactions.`)) return;
    try {
      await api.post('/api/accounting/currencies/set-base', { currency_code: currencyCode });
      showAlert(`${currencyCode} set as base currency`, 'success');
      fetchCurrencies();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to set base currency', 'error');
    }
  };

  const handleSaveRate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/accounting/exchange-rates', rateForm);
      showAlert('Exchange rate saved', 'success');
      setShowRateModal(false);
      setRateForm({ from_currency: '', to_currency: '', rate: '', effective_date: '', notes: '' });
      fetchExchangeRates();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to save rate', 'error');
    }
  };

  const getActiveCurrencies = () => currencies.filter(c => c.is_active);
  const getBaseCurrency = () => currencies.find(c => c.is_base) || { code: 'INR', symbol: '₹' };

  // ── Vendor Handlers ──
  const handleCreateVendor = async (e) => {
    e.preventDefault();
    try {
      if (editingVendor) {
        await api.put(`/api/accounting/vendors/${editingVendor.id}`, vendorForm);
        showAlert('Vendor updated', 'success');
      } else {
        await api.post('/api/accounting/vendors', vendorForm);
        showAlert('Vendor created', 'success');
      }
      setShowVendorModal(false);
      setEditingVendor(null);
      setVendorForm({ name: '', display_name: '', email: '', phone: '', address: '', city: '', state: '', gstin: '', pan: '', default_expense_account_id: '', default_payment_account_id: '', payment_terms: '30', notes: '' });
      fetchVendors();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to save vendor', 'error');
    }
  };

  const handleEditVendor = (v) => {
    setEditingVendor(v);
    setVendorForm({
      name: v.name || '',
      display_name: v.display_name || '',
      email: v.email || '',
      phone: v.phone || '',
      address: v.address || '',
      city: v.city || '',
      state: v.state || '',
      gstin: v.gstin || '',
      pan: v.pan || '',
      default_expense_account_id: v.default_expense_account_id || '',
      default_payment_account_id: v.default_payment_account_id || '',
      payment_terms: String(v.payment_terms ?? 30),
      notes: v.notes || '',
    });
    setShowVendorModal(true);
  };

  const handleToggleVendor = async (v) => {
    try {
      await api.put(`/api/accounting/vendors/${v.id}`, { is_active: !v.is_active });
      showAlert(v.is_active ? 'Vendor deactivated' : 'Vendor activated', 'success');
      fetchVendors();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to update vendor', 'error');
    }
  };

  const handleVendorSelectForExpense = (vendorId) => {
    setExpenseForm(f => {
      const v = vendors.find(v => v.id === vendorId);
      return {
        ...f,
        vendor_id: vendorId,
        vendor: v?.name || '',
        account_id: v?.default_expense_account_id || f.account_id,
        payment_account_id: v?.default_payment_account_id || f.payment_account_id,
        currency_code: v?.default_currency_code || f.currency_code,
      };
    });
  };

  // ─── Accounts Receivable Handlers ─────────────────────────
  const handleSaveARCustomer = async (e) => {
    e.preventDefault();
    try {
      if (editingARCustomer) {
        await api.put(`/api/accounting/ar/customers/${editingARCustomer.id}`, arCustomerForm);
        showAlert('Customer updated', 'success');
      } else {
        await api.post('/api/accounting/ar/customers', arCustomerForm);
        showAlert('Customer created', 'success');
      }
      setShowARCustomerModal(false);
      setEditingARCustomer(null);
      setArCustomerForm({ name: '', display_name: '', email: '', phone: '', address: '', city: '', state: '', gstin: '', pan: '', default_revenue_account_id: '', default_receivable_account_id: '', payment_terms: '30', credit_limit: '', notes: '' });
      fetchARCustomers();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to save customer', 'error');
    }
  };

  const handleEditARCustomer = (c) => {
    setEditingARCustomer(c);
    setArCustomerForm({
      name: c.name || '', display_name: c.display_name || '', email: c.email || '', phone: c.phone || '',
      address: c.address || '', city: c.city || '', state: c.state || '', gstin: c.gstin || '', pan: c.pan || '',
      default_revenue_account_id: c.default_revenue_account_id || '', default_receivable_account_id: c.default_receivable_account_id || '',
      payment_terms: String(c.payment_terms ?? 30), credit_limit: String(c.credit_limit || ''), notes: c.notes || '',
    });
    setShowARCustomerModal(true);
  };

  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...invoiceForm,
        lines: invoiceLines.filter(l => l.description && l.unit_price),
      };
      await api.post('/api/accounting/ar/invoices', payload);
      showAlert('Invoice created', 'success');
      setShowInvoiceModal(false);
      setInvoiceForm({ customer_id: '', invoice_date: '', due_date: '', revenue_account_id: '', receivable_account_id: '', tax_account_id: '', notes: '' });
      setInvoiceLines([{ description: '', quantity: 1, unit_price: '', tax_rate: '0', account_id: '' }]);
      fetchARInvoices();
      fetchARSummary();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to create invoice', 'error');
    }
  };

  const handleSendInvoice = async (invoiceId) => {
    try {
      await api.post(`/api/accounting/ar/invoices/${invoiceId}/send`);
      showAlert('Invoice sent & posted to journal', 'success');
      fetchARInvoices();
      fetchARSummary();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to send invoice', 'error');
    }
  };

  const handleVoidInvoice = async (invoiceId) => {
    if (!window.confirm('Void this invoice? This cannot be undone.')) return;
    try {
      await api.post(`/api/accounting/ar/invoices/${invoiceId}/void`);
      showAlert('Invoice voided', 'success');
      fetchARInvoices();
      fetchARSummary();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to void invoice', 'error');
    }
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/accounting/ar/payments', { ...paymentForm, invoice_id: paymentInvoice.id });
      showAlert('Payment recorded', 'success');
      setShowPaymentModal(false);
      setPaymentInvoice(null);
      setPaymentForm({ amount: '', payment_date: '', payment_method: 'bank_transfer', reference_number: '', deposit_account_id: '', notes: '' });
      fetchARInvoices();
      fetchARSummary();
      fetchARCustomers();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to record payment', 'error');
    }
  };

  const openPaymentModal = (inv) => {
    setPaymentInvoice(inv);
    setPaymentForm({ amount: String(inv.balance_due), payment_date: new Date().toISOString().split('T')[0], payment_method: 'bank_transfer', reference_number: '', deposit_account_id: '', notes: '' });
    setShowPaymentModal(true);
  };

  const addInvoiceLine = () => setInvoiceLines(prev => [...prev, { description: '', quantity: 1, unit_price: '', tax_rate: '0', account_id: '' }]);
  const removeInvoiceLine = (idx) => setInvoiceLines(prev => prev.filter((_, i) => i !== idx));
  const updateInvoiceLine = (idx, field, value) => setInvoiceLines(prev => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));

  const invoiceSubtotal = invoiceLines.reduce((s, l) => s + (parseFloat(l.quantity) || 1) * (parseFloat(l.unit_price) || 0), 0);
  const invoiceTax = invoiceLines.reduce((s, l) => {
    const amt = (parseFloat(l.quantity) || 1) * (parseFloat(l.unit_price) || 0);
    return s + amt * (parseFloat(l.tax_rate) || 0) / 100;
  }, 0);
  const invoiceTotal = invoiceSubtotal + invoiceTax;

  const revenueAccounts = accounts.filter(a => a.account_type === 'revenue' && a.is_active);
  const assetAccounts = accounts.filter(a => a.account_type === 'asset' && a.is_active);
  const liabilityAccounts = accounts.filter(a => a.account_type === 'liability' && a.is_active);
  const expenseAccountsAll = accounts.filter(a => a.account_type === 'expense' && a.is_active);

  // ─── Accounts Payable Handlers ────────────────────────────
  const handleCreateBill = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...billForm, lines: billLines.filter(l => l.description && l.unit_price) };
      await api.post('/api/accounting/ap/bills', payload);
      showAlert('Bill created', 'success');
      setShowBillModal(false);
      setBillForm({ vendor_id: '', bill_date: '', due_date: '', expense_account_id: '', payable_account_id: '', tax_account_id: '', vendor_invoice_number: '', notes: '' });
      setBillLines([{ description: '', quantity: 1, unit_price: '', tax_rate: '0', account_id: '' }]);
      fetchAPBills(); fetchAPSummary();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to create bill', 'error');
    }
  };

  const handleApproveBill = async (billId) => {
    try {
      await api.post(`/api/accounting/ap/bills/${billId}/approve`);
      showAlert('Bill approved & posted to journal', 'success');
      fetchAPBills(); fetchAPSummary();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to approve bill', 'error');
    }
  };

  const handleVoidBill = async (billId) => {
    if (!window.confirm('Void this bill? This cannot be undone.')) return;
    try {
      await api.post(`/api/accounting/ap/bills/${billId}/void`);
      showAlert('Bill voided', 'success');
      fetchAPBills(); fetchAPSummary();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to void bill', 'error');
    }
  };

  const handleRecordBillPayment = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/accounting/ap/payments', { ...billPayForm, bill_id: billPayInvoice.id });
      showAlert('Payment recorded', 'success');
      setShowBillPayModal(false); setBillPayInvoice(null);
      setBillPayForm({ amount: '', payment_date: '', payment_method: 'bank_transfer', reference_number: '', payment_account_id: '', notes: '' });
      fetchAPBills(); fetchAPSummary();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to record payment', 'error');
    }
  };

  const openBillPayModal = (bill) => {
    setBillPayInvoice(bill);
    setBillPayForm({ amount: String(bill.balance_due), payment_date: new Date().toISOString().split('T')[0], payment_method: 'bank_transfer', reference_number: '', payment_account_id: '', notes: '' });
    setShowBillPayModal(true);
  };

  const handleCreatePO = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...poForm, lines: poLines.filter(l => l.description && l.unit_price) };
      await api.post('/api/accounting/ap/purchase-orders', payload);
      showAlert('Purchase order created', 'success');
      setShowPOModal(false);
      setPoForm({ vendor_id: '', order_date: '', expected_date: '', notes: '' });
      setPoLines([{ description: '', quantity: 1, unit_price: '', tax_rate: '0', account_id: '' }]);
      fetchPurchaseOrders();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to create PO', 'error');
    }
  };

  const handleUpdatePOStatus = async (poId, status) => {
    try {
      await api.put(`/api/accounting/ap/purchase-orders/${poId}/status`, { status });
      showAlert(`PO ${status}`, 'success');
      fetchPurchaseOrders();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to update PO', 'error');
    }
  };

  const addBillLine = () => setBillLines(prev => [...prev, { description: '', quantity: 1, unit_price: '', tax_rate: '0', account_id: '' }]);
  const removeBillLine = (idx) => setBillLines(prev => prev.filter((_, i) => i !== idx));
  const updateBillLine = (idx, field, value) => setBillLines(prev => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));

  const billSubtotal = billLines.reduce((s, l) => s + (parseFloat(l.quantity) || 1) * (parseFloat(l.unit_price) || 0), 0);
  const billTax = billLines.reduce((s, l) => {
    const amt = (parseFloat(l.quantity) || 1) * (parseFloat(l.unit_price) || 0);
    return s + amt * (parseFloat(l.tax_rate) || 0) / 100;
  }, 0);
  const billTotal = billSubtotal + billTax;

  const addPOLine = () => setPoLines(prev => [...prev, { description: '', quantity: 1, unit_price: '', tax_rate: '0', account_id: '' }]);
  const removePOLine = (idx) => setPoLines(prev => prev.filter((_, i) => i !== idx));
  const updatePOLine = (idx, field, value) => setPoLines(prev => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));

  // ── Bank Reconciliation Handlers ────────────────────────
  const handleCreateBankAccount = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/accounting/bank-accounts', bankAccountForm);
      showAlert('Bank account created', 'success');
      setShowBankAccountModal(false);
      setBankAccountForm({ account_name: '', bank_name: '', account_number: '', ifsc_code: '', account_type: 'checking', coa_account_id: '', currency: 'INR', opening_balance: '0' });
      fetchBankAccounts();
      fetchBankReconSummary();
    } catch (err) { showAlert(err.response?.data?.error || 'Failed to create bank account', 'error'); }
  };

  const handleToggleBankAccount = async (acct) => {
    try {
      await api.put(`/api/accounting/bank-accounts/${acct.id}`, { is_active: !acct.is_active });
      showAlert(`Bank account ${acct.is_active ? 'deactivated' : 'activated'}`, 'success');
      fetchBankAccounts();
    } catch (err) { showAlert(err.response?.data?.error || 'Failed', 'error'); }
  };

  const handleCreateManualTxn = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/accounting/bank-transactions', manualTxnForm);
      showAlert('Transaction created', 'success');
      setShowManualTxnModal(false);
      setManualTxnForm({ bank_account_id: '', transaction_date: '', description: '', reference: '', amount: '', category: '', payee: '' });
      fetchBankTransactions();
      fetchBankAccounts();
      fetchBankReconSummary();
    } catch (err) { showAlert(err.response?.data?.error || 'Failed', 'error'); }
  };

  const handleImportCSV = async (e) => {
    e.preventDefault();
    try {
      const lines = importForm.csvText.trim().split('\n');
      if (lines.length < 2) return showAlert('CSV must have header + data rows', 'error');
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      const transactions = lines.slice(1).map(line => {
        const vals = line.split(',').map(v => v.trim());
        const obj = {};
        headers.forEach((h, i) => { obj[h] = vals[i] || ''; });
        return {
          date: obj.date || obj.transaction_date || '',
          description: obj.description || obj.narrative || obj.details || '',
          reference: obj.reference || obj.ref || obj.cheque_no || '',
          amount: obj.amount || (Number(obj.credit || 0) - Number(obj.debit || 0)).toString(),
          balance: obj.balance || '',
          payee: obj.payee || '',
          category: obj.category || '',
        };
      }).filter(t => t.date && t.description);
      if (transactions.length === 0) return showAlert('No valid transactions found in CSV', 'error');
      const { data } = await api.post('/api/accounting/bank-transactions/import', { bank_account_id: importForm.bank_account_id, transactions });
      showAlert(`Imported ${data.imported} transactions`, 'success');
      setShowImportModal(false);
      setImportForm({ bank_account_id: '', csvText: '' });
      fetchBankTransactions();
      fetchBankAccounts();
      fetchBankReconSummary();
    } catch (err) { showAlert(err.response?.data?.error || 'Import failed', 'error'); }
  };

  const handleExcludeTxn = async (id) => {
    if (!window.confirm('Exclude this transaction from reconciliation?')) return;
    try {
      await api.post(`/api/accounting/bank-transactions/${id}/exclude`);
      showAlert('Transaction excluded', 'success');
      fetchBankTransactions();
    } catch (err) { showAlert(err.response?.data?.error || 'Failed', 'error'); }
  };

  const handleStartReconciliation = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post('/api/accounting/reconciliations', reconForm);
      showAlert('Reconciliation started', 'success');
      setShowReconModal(false);
      setReconForm({ bank_account_id: '', reconciliation_date: '', period_start: '', period_end: '', statement_balance: '', notes: '' });
      fetchReconciliations();
      // Open detail view
      handleOpenReconDetail(data.id);
    } catch (err) { showAlert(err.response?.data?.error || 'Failed', 'error'); }
  };

  const handleOpenReconDetail = async (id) => {
    try {
      const { data } = await api.get(`/api/accounting/reconciliations/${id}`);
      setReconDetail(data);
      setReconView('detail');
    } catch (err) { showAlert(err.response?.data?.error || 'Failed to load reconciliation', 'error'); }
  };

  const handleMatchTxn = async (bankTxnId, journalEntryId, journalLineId) => {
    try {
      await api.post('/api/accounting/reconciliation-matches', {
        reconciliation_id: reconDetail.reconciliation.id,
        bank_transaction_id: bankTxnId,
        journal_entry_id: journalEntryId || null,
        journal_line_id: journalLineId || null,
        match_type: 'manual',
      });
      showAlert('Transaction matched', 'success');
      handleOpenReconDetail(reconDetail.reconciliation.id);
    } catch (err) { showAlert(err.response?.data?.error || 'Match failed', 'error'); }
  };

  const handleUnmatchTxn = async (matchId) => {
    try {
      await api.delete(`/api/accounting/reconciliation-matches/${matchId}`);
      showAlert('Match removed', 'success');
      handleOpenReconDetail(reconDetail.reconciliation.id);
    } catch (err) { showAlert(err.response?.data?.error || 'Failed', 'error'); }
  };

  const handleAutoMatch = async () => {
    try {
      const { data } = await api.post('/api/accounting/reconciliation-auto-match', { reconciliation_id: reconDetail.reconciliation.id });
      showAlert(`Auto-matched ${data.auto_matched} transactions. ${data.unmatched_remaining} remaining.`, 'success');
      handleOpenReconDetail(reconDetail.reconciliation.id);
    } catch (err) { showAlert(err.response?.data?.error || 'Auto-match failed', 'error'); }
  };

  const handleCompleteRecon = async (id) => {
    if (!window.confirm('Complete this reconciliation? Matched transactions will be marked as reconciled.')) return;
    try {
      await api.post(`/api/accounting/reconciliations/${id}/complete`);
      showAlert('Reconciliation completed', 'success');
      setReconDetail(null);
      setReconView('reconciliations');
      fetchReconciliations();
      fetchBankAccounts();
      fetchBankTransactions();
      fetchBankReconSummary();
    } catch (err) { showAlert(err.response?.data?.error || 'Failed', 'error'); }
  };

  const handleVoidRecon = async (id) => {
    if (!window.confirm('Void this reconciliation? All matches will be removed.')) return;
    try {
      await api.post(`/api/accounting/reconciliations/${id}/void`);
      showAlert('Reconciliation voided', 'success');
      setReconDetail(null);
      setReconView('reconciliations');
      fetchReconciliations();
      fetchBankTransactions();
    } catch (err) { showAlert(err.response?.data?.error || 'Failed', 'error'); }
  };

  const handleCreateManualJournal = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        entry_date: manualJournalForm.entry_date || undefined,
        description: manualJournalForm.description,
        memo: manualJournalForm.memo || undefined,
        currency_code: manualJournalForm.currency_code || undefined,
        lines: manualJournalForm.lines
          .filter(l => l.account_id && (Number(l.debit) > 0 || Number(l.credit) > 0))
          .map(l => ({
            account_id: l.account_id,
            debit: Number(l.debit) || 0,
            credit: Number(l.credit) || 0,
            line_description: l.line_description || '',
          })),
      };
      await api.post('/api/accounting/journals/manual', payload);
      showAlert('Manual journal posted successfully', 'success');
      setShowManualJournalModal(false);
      setManualJournalForm({ entry_date: '', description: '', memo: '', currency_code: '', lines: [{ account_id: '', debit: '', credit: '', line_description: '' }, { account_id: '', debit: '', credit: '', line_description: '' }] });
      fetchJournals();
      fetchSummary();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to create manual journal', 'error');
    }
  };

  const addManualJournalLine = () => {
    setManualJournalForm(f => ({
      ...f,
      lines: [...f.lines, { account_id: '', debit: '', credit: '', line_description: '' }],
    }));
  };

  const removeManualJournalLine = (idx) => {
    if (manualJournalForm.lines.length <= 2) return;
    setManualJournalForm(f => ({
      ...f,
      lines: f.lines.filter((_, i) => i !== idx),
    }));
  };

  const updateManualJournalLine = (idx, field, value) => {
    setManualJournalForm(f => ({
      ...f,
      lines: f.lines.map((l, i) => i === idx ? { ...l, [field]: value } : l),
    }));
  };

  const exportCSV = (rows, filename) => {
    if (!rows || rows.length === 0) return;
    const headers = Object.keys(rows[0]);
    const csv = [
      headers.join(','),
      ...rows.map(r => headers.map(h => `"${(r[h] ?? '').toString().replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const exportPDF = (title, buildContent) => {
    const w = window.open('', '_blank', 'width=900,height=700');
    if (!w) return;
    const today = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    w.document.write(`<!DOCTYPE html><html><head><title>${title}</title><style>
      *{margin:0;padding:0;box-sizing:border-box}
      body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;color:#1a1a1a;padding:40px;font-size:13px;}
      .header{text-align:center;margin-bottom:24px;border-bottom:2px solid #111;padding-bottom:16px;}
      .header h1{font-size:20px;font-weight:700;margin-bottom:4px;}
      .header p{font-size:11px;color:#666;}
      table{width:100%;border-collapse:collapse;margin-bottom:20px;}
      th{background:#f5f5f5;text-align:left;padding:8px 12px;font-size:11px;font-weight:600;color:#555;border-bottom:2px solid #ddd;}
      th.right,td.right{text-align:right;}
      td{padding:6px 12px;border-bottom:1px solid #eee;}
      tr:last-child td{border-bottom:none;}
      .section-title{font-size:14px;font-weight:700;margin:20px 0 8px;padding:8px 12px;background:#f8f8f8;border-left:3px solid #333;}
      .total-row{font-weight:700;background:#f5f5f5;border-top:2px solid #333;}
      .total-row td{padding:10px 12px;}
      .summary-box{display:inline-block;border:1px solid #ddd;padding:12px 20px;margin:4px 8px;text-align:center;border-radius:4px;}
      .summary-box .label{font-size:10px;color:#666;text-transform:uppercase;}
      .summary-box .value{font-size:16px;font-weight:700;margin-top:2px;}
      .positive{color:#16a34a;} .negative{color:#dc2626;}
      .footer{margin-top:30px;text-align:center;font-size:10px;color:#999;border-top:1px solid #eee;padding-top:12px;}
      @media print{body{padding:20px;} .no-print{display:none;}}
    </style></head><body>
      <div class="no-print" style="text-align:right;margin-bottom:16px;"><button onclick="window.print()" style="padding:8px 20px;background:#111;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:13px;">🖨 Print / Save PDF</button></div>
      <div class="header"><h1>${title}</h1><p>Generated: ${today}</p></div>
      ${buildContent()}
      <div class="footer">ElyDine Accounting · Confidential</div>
    </body></html>`);
    w.document.close();
  };

  const fmtPDF = (v) => Number(v || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 });

  // ─── Expense accounts grouped ────────────────────────────
  const expenseAccounts = accounts.filter(a => a.account_type === 'expense' && a.is_active);
  const paymentAccounts = accounts.filter(a => ['asset'].includes(a.account_type) && a.is_active);

  // ═══════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════
  return (
    <Layout title="Accounting" subtitle="Double-entry financial management">
      {/* Tab Bar */}
      <div className="mb-6 overflow-x-auto">
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-xl min-w-max">
          {TAB_LIST.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center space-x-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                  isActive ? 'bg-white text-black shadow-sm' : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                <Icon size={15} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <FiRefreshCw className="animate-spin text-gray-400" size={28} />
        </div>
      ) : (
        <>
          {/* ═══════ DASHBOARD ═══════ */}
          {activeTab === TABS.DASHBOARD && summary && (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <SummaryCard icon={FiTrendingUp} label="Total Revenue" value={formatCurrency(summary.total_revenue)} color="green" />
                <SummaryCard icon={FiDollarSign} label="Total Expenses" value={formatCurrency(summary.total_expenses)} color="red" />
                <SummaryCard icon={FiFileText} label="Net Income" value={formatCurrency(summary.net_income)} color={summary.net_income >= 0 ? 'green' : 'red'} />
                <SummaryCard icon={FiBook} label="Posted Journals" value={summary.total_journals} color="blue" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <SummaryCard icon={FiRefreshCw} label="Pending Postings" value={summary.pending_postings} color={summary.pending_postings > 0 ? 'yellow' : 'gray'} />
                <SummaryCard icon={FiAlertTriangle} label="Failed Postings" value={summary.failed_postings} color={summary.failed_postings > 0 ? 'red' : 'gray'} />
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                  <p className="text-xs text-gray-500 mb-1">Current Period</p>
                  {summary.current_period ? (
                    <div>
                      <p className="text-lg font-semibold text-black">{summary.current_period.label}</p>
                      <p className="text-xs text-gray-400">{formatDate(summary.current_period.period_start)} – {formatDate(summary.current_period.period_end)}</p>
                      <StatusBadge status={summary.current_period.status} />
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400">No open period. <button onClick={handleOpenNextPeriod} className="text-blue-600 underline">Open one</button></p>
                  )}
                </div>
              </div>

              {/* Quick Links */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Quick Access</h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
                  {[
                    { label: 'Balance Sheet', icon: FiLayers, tab: TABS.BALANCE_SHEET, color: 'text-blue-600 bg-blue-50' },
                    { label: 'P&L Statement', icon: FiPieChart, tab: TABS.PNL, color: 'text-green-600 bg-green-50' },
                    { label: 'Cash Flow', icon: FiActivity, tab: TABS.CASH_FLOW, color: 'text-purple-600 bg-purple-50' },
                    { label: 'Trial Balance', icon: FiFileText, tab: TABS.TRIAL_BALANCE, color: 'text-orange-600 bg-orange-50' },
                    { label: 'Journals', icon: FiBook, tab: TABS.JOURNALS, color: 'text-indigo-600 bg-indigo-50' },
                    { label: 'Audit Log', icon: FiEye, tab: TABS.AUDIT, color: 'text-gray-600 bg-gray-100' },
                  ].map(link => (
                    <button
                      key={link.tab}
                      onClick={() => setActiveTab(link.tab)}
                      className="bg-white rounded-xl border border-gray-200 shadow-sm p-3 flex flex-col items-center gap-2 hover:shadow-md transition-shadow"
                    >
                      <div className={`p-2 rounded-lg ${link.color}`}><link.icon size={18} /></div>
                      <span className="text-xs font-medium text-gray-700">{link.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ═══════ JOURNALS ═══════ */}
          {activeTab === TABS.JOURNALS && (
            <div className="space-y-4">
              {/* Filters */}
              <div className="flex flex-wrap gap-3 items-end bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Source</label>
                  <select value={journalFilters.source_type} onChange={e => setJournalFilters(f => ({ ...f, source_type: e.target.value }))} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm">
                    <option value="">All</option>
                    <option value="bill_payment">Bill Payment</option>
                    <option value="inventory_receipt">Inventory Receipt</option>
                    <option value="stock_receipt">Stock Receipt</option>
                    <option value="expense">Expense</option>
                    <option value="reversal">Reversal</option>
                    <option value="manual">Manual</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Status</label>
                  <select value={journalFilters.status} onChange={e => setJournalFilters(f => ({ ...f, status: e.target.value }))} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm">
                    <option value="">All</option>
                    <option value="posted">Posted</option>
                    <option value="reversed">Reversed</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">From</label>
                  <input type="date" value={journalFilters.from_date} onChange={e => setJournalFilters(f => ({ ...f, from_date: e.target.value }))} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">To</label>
                  <input type="date" value={journalFilters.to_date} onChange={e => setJournalFilters(f => ({ ...f, to_date: e.target.value }))} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
                </div>
                <button onClick={fetchJournals} className="bg-black text-white px-4 py-1.5 rounded-lg text-sm flex items-center gap-1"><FiFilter size={14} /> Filter</button>
                <button onClick={() => { setJournalFilters({ source_type: '', status: '', from_date: '', to_date: '' }); }} className="text-gray-500 px-3 py-1.5 rounded-lg text-sm border border-gray-300 flex items-center gap-1"><FiX size={14} /> Clear</button>
                <button onClick={() => exportCSV(journals.map(j => ({ id: j.id, date: j.entry_date, description: j.description, source: j.source_type, status: j.status })), 'journals.csv')} className="text-gray-600 px-3 py-1.5 rounded-lg text-sm border border-gray-300 flex items-center gap-1 ml-auto"><FiDownload size={14} /> Export</button>
                <button onClick={() => { fetchAccounts(); setShowManualJournalModal(true); }} className="bg-black text-white px-3 py-1.5 rounded-lg text-sm flex items-center gap-1"><FiEdit3 size={14} /> Manual Journal</button>
              </div>

              <p className="text-xs text-gray-400">{journalTotal} journal(s) found</p>

              {/* Journal List */}
              <div className="space-y-2">
                {journals.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">No journal entries yet</div>
                ) : journals.map(j => (
                  <div key={j.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div
                      className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50 transition"
                      onClick={() => setExpandedJournal(expandedJournal === j.id ? null : j.id)}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="flex-shrink-0">
                          {expandedJournal === j.id ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-black truncate">{j.description}</p>
                          <p className="text-xs text-gray-400">{formatDate(j.entry_date)} · {j.source_type || 'manual'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <StatusBadge status={j.status} />
                        {j.status === 'posted' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleReverseJournal(j.id); }}
                            className="text-red-500 hover:text-red-700 p-1"
                            title="Reverse"
                          >
                            <FiRotateCcw size={15} />
                          </button>
                        )}
                      </div>
                    </div>
                    {/* Expanded lines */}
                    {expandedJournal === j.id && j.journal_lines && (
                      <div className="border-t border-gray-100 px-4 py-3 bg-gray-50">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-xs text-gray-400">
                              <th className="text-left py-1">Account</th>
                              <th className="text-right py-1">Debit</th>
                              <th className="text-right py-1">Credit</th>
                            </tr>
                          </thead>
                          <tbody>
                            {j.journal_lines.map(line => (
                              <tr key={line.id} className="border-b border-gray-100 last:border-0">
                                <td className="py-1.5">
                                  <span className="font-medium">{line.chart_of_accounts?.account_code}</span>{' '}
                                  <span className="text-gray-600">{line.chart_of_accounts?.account_name}</span>
                                </td>
                                <td className="text-right py-1.5 font-mono">{Number(line.debit) > 0 ? formatCurrency(line.debit) : '-'}</td>
                                <td className="text-right py-1.5 font-mono">{Number(line.credit) > 0 ? formatCurrency(line.credit) : '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {j.memo && <p className="text-xs text-gray-400 mt-2">Memo: {j.memo}</p>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ═══════ LEDGER ═══════ */}
          {activeTab === TABS.LEDGER && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-3 items-end bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-xs text-gray-500 mb-1">Select Account</label>
                  <select
                    value={ledgerAccountId}
                    onChange={e => setLedgerAccountId(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                  >
                    <option value="">Choose an account…</option>
                    {accounts.filter(a => a.is_active).map(a => (
                      <option key={a.id} value={a.id}>{a.account_code} – {a.account_name} ({a.account_type})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">From</label>
                  <input type="date" value={ledgerFilters.from_date} onChange={e => setLedgerFilters(f => ({ ...f, from_date: e.target.value }))} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">To</label>
                  <input type="date" value={ledgerFilters.to_date} onChange={e => setLedgerFilters(f => ({ ...f, to_date: e.target.value }))} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
                </div>
                <button onClick={fetchLedger} disabled={!ledgerAccountId} className="bg-black text-white px-4 py-1.5 rounded-lg text-sm flex items-center gap-1 disabled:opacity-40"><FiSearch size={14} /> Load</button>
                <button onClick={() => exportCSV(ledgerEntries.map(e => ({ date: e.entry_date, description: e.description, source: e.source_type, debit: e.debit, credit: e.credit, balance: e.running_balance })), `ledger-${ledgerAccount?.account_code || 'export'}.csv`)} className="text-gray-600 px-3 py-1.5 rounded-lg text-sm border border-gray-300 flex items-center gap-1 ml-auto"><FiDownload size={14} /> Export</button>
              </div>

              {ledgerAccount && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-1">
                    <div>
                      <h3 className="text-lg font-semibold text-black">{ledgerAccount.account_code} – {ledgerAccount.account_name}</h3>
                      <p className="text-xs text-gray-400"><AccountTypeBadge type={ledgerAccount.account_type} /> <span className="ml-1">{ledgerAccount.account_category}</span></p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-400">Balance</p>
                      <p className={`text-xl font-bold ${ledgerTotals.balance >= 0 ? 'text-black' : 'text-red-600'}`}>{formatCurrency(Math.abs(ledgerTotals.balance))}</p>
                      <p className="text-xs text-gray-400">{ledgerTotals.balance >= 0 ? 'Dr' : 'Cr'}</p>
                    </div>
                  </div>
                  <div className="flex gap-4 text-xs text-gray-500 mt-2">
                    <span>Total Debits: {formatCurrency(ledgerTotals.total_debit)}</span>
                    <span>Total Credits: {formatCurrency(ledgerTotals.total_credit)}</span>
                    <span>{ledgerTotal} transaction(s)</span>
                  </div>
                </div>
              )}

              {ledgerAccountId && ledgerEntries.length > 0 ? (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                      <tr>
                        <th className="px-4 py-2 text-left">Date</th>
                        <th className="px-4 py-2 text-left">Description</th>
                        <th className="px-4 py-2 text-left">Source</th>
                        <th className="px-4 py-2 text-right">Debit</th>
                        <th className="px-4 py-2 text-right">Credit</th>
                        <th className="px-4 py-2 text-right">Balance</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {ledgerEntries.map((e, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-gray-600">{formatDate(e.entry_date)}</td>
                          <td className="px-4 py-2 text-gray-800">{e.line_description || e.description}</td>
                          <td className="px-4 py-2"><span className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-600">{e.source_type || '-'}</span></td>
                          <td className="px-4 py-2 text-right font-mono">{parseFloat(e.debit) > 0 ? formatCurrency(e.debit) : '-'}</td>
                          <td className="px-4 py-2 text-right font-mono">{parseFloat(e.credit) > 0 ? formatCurrency(e.credit) : '-'}</td>
                          <td className={`px-4 py-2 text-right font-mono font-semibold ${parseFloat(e.running_balance) >= 0 ? 'text-black' : 'text-red-600'}`}>{formatCurrency(Math.abs(parseFloat(e.running_balance)))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : ledgerAccountId ? (
                <div className="text-center py-12 text-gray-400">No transactions for this account</div>
              ) : (
                <div className="text-center py-12 text-gray-400">Select an account to view its ledger</div>
              )}
            </div>
          )}

          {/* ═══════ EXPENSES ═══════ */}
          {activeTab === TABS.EXPENSES && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-black">Expense Entries</h3>
                <button onClick={() => setShowExpenseModal(true)} className="bg-black text-white px-4 py-2 rounded-lg text-sm flex items-center gap-1.5"><FiPlus size={15} /> New Expense</button>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-4 py-2.5 text-xs text-gray-500 font-medium">Date</th>
                      <th className="text-left px-4 py-2.5 text-xs text-gray-500 font-medium">Description</th>
                      <th className="text-left px-4 py-2.5 text-xs text-gray-500 font-medium">Vendor</th>
                      <th className="text-right px-4 py-2.5 text-xs text-gray-500 font-medium">Amount</th>
                      <th className="text-center px-4 py-2.5 text-xs text-gray-500 font-medium">Status</th>
                      <th className="text-center px-4 py-2.5 text-xs text-gray-500 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expenses.length === 0 ? (
                      <tr><td colSpan={6} className="text-center py-10 text-gray-400">No expenses recorded</td></tr>
                    ) : expenses.map(exp => (
                      <tr key={exp.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                        <td className="px-4 py-2.5">{formatDate(exp.expense_date)}</td>
                        <td className="px-4 py-2.5 text-black font-medium">{exp.description}</td>
                        <td className="px-4 py-2.5 text-gray-600">{exp.vendor || '-'}</td>
                        <td className="px-4 py-2.5 text-right font-mono">{formatCurrency(exp.amount)}</td>
                        <td className="px-4 py-2.5 text-center"><StatusBadge status={exp.status} /></td>
                        <td className="px-4 py-2.5 text-center">
                          {exp.status === 'posted' && (
                            <button onClick={() => handleVoidExpense(exp.id)} className="text-red-500 hover:text-red-700 text-xs flex items-center gap-1 mx-auto" title="Void Expense">
                              <FiSlash size={13} /> Void
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Expense Modal */}
              {showExpenseModal && (
                <ModalOverlay onClose={() => setShowExpenseModal(false)} title="New Expense">
                  <form onSubmit={handleCreateExpense} className="space-y-4">
                    <FormField label="Date">
                      <input type="date" required value={expenseForm.expense_date} onChange={e => setExpenseForm(f => ({ ...f, expense_date: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                    </FormField>
                    <FormField label="Expense Account">
                      <select required value={expenseForm.account_id} onChange={e => setExpenseForm(f => ({ ...f, account_id: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                        <option value="">Select expense account</option>
                        {expenseAccounts.map(a => <option key={a.id} value={a.id}>{a.account_code} – {a.account_name}</option>)}
                      </select>
                    </FormField>
                    <FormField label="Payment From">
                      <select required value={expenseForm.payment_account_id} onChange={e => setExpenseForm(f => ({ ...f, payment_account_id: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                        <option value="">Select payment account</option>
                        {paymentAccounts.map(a => <option key={a.id} value={a.id}>{a.account_code} – {a.account_name}</option>)}
                      </select>
                    </FormField>
                    <FormField label={`Amount (${expenseForm.currency_code ? currencies.find(c => c.code === expenseForm.currency_code)?.symbol || expenseForm.currency_code : getBaseCurrency().symbol})`}>
                      <input type="number" step="0.01" min="0.01" required value={expenseForm.amount} onChange={e => setExpenseForm(f => ({ ...f, amount: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                    </FormField>
                    <FormField label="Currency (optional)">
                      <select value={expenseForm.currency_code} onChange={e => setExpenseForm(f => ({ ...f, currency_code: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                        <option value="">Base ({getBaseCurrency().code})</option>
                        {getActiveCurrencies().filter(c => !c.is_base).map(c => <option key={c.id} value={c.code}>{c.code} – {c.name} ({c.symbol})</option>)}
                      </select>
                    </FormField>
                    <FormField label="Description">
                      <input type="text" required value={expenseForm.description} onChange={e => setExpenseForm(f => ({ ...f, description: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="e.g. Monthly rent payment" />
                    </FormField>
                    <FormField label="Vendor (optional)">
                      <select value={expenseForm.vendor_id} onChange={e => handleVendorSelectForExpense(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                        <option value="">No vendor</option>
                        {vendors.filter(v => v.is_active).map(v => <option key={v.id} value={v.id}>{v.display_name || v.name}</option>)}
                      </select>
                    </FormField>
                    <div className="flex justify-end gap-2 pt-2">
                      <button type="button" onClick={() => setShowExpenseModal(false)} className="px-4 py-2 rounded-lg border border-gray-300 text-sm">Cancel</button>
                      <button type="submit" className="bg-black text-white px-4 py-2 rounded-lg text-sm">Create & Post</button>
                    </div>
                  </form>
                </ModalOverlay>
              )}
            </div>
          )}

          {/* ═══════ CHART OF ACCOUNTS ═══════ */}
          {activeTab === TABS.COA && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-black">Chart of Accounts</h3>
                <div className="flex gap-2">
                  <button onClick={() => exportCSV(accounts.map(a => ({ code: a.account_code, name: a.account_name, type: a.account_type, category: a.account_category, system: a.is_system })), 'chart_of_accounts.csv')} className="text-gray-600 px-3 py-2 rounded-lg text-sm border border-gray-300 flex items-center gap-1"><FiDownload size={14} /> Export</button>
                  <button onClick={() => setShowAccountModal(true)} className="bg-black text-white px-4 py-2 rounded-lg text-sm flex items-center gap-1.5"><FiPlus size={15} /> Add Account</button>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-4 py-2.5 text-xs text-gray-500 font-medium">Code</th>
                      <th className="text-left px-4 py-2.5 text-xs text-gray-500 font-medium">Name</th>
                      <th className="text-left px-4 py-2.5 text-xs text-gray-500 font-medium">Type</th>
                      <th className="text-left px-4 py-2.5 text-xs text-gray-500 font-medium">Category</th>
                      <th className="text-center px-4 py-2.5 text-xs text-gray-500 font-medium">System</th>
                      <th className="text-center px-4 py-2.5 text-xs text-gray-500 font-medium">Active</th>
                      <th className="text-right px-4 py-2.5 text-xs text-gray-500 font-medium"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {accounts.length === 0 ? (
                      <tr><td colSpan={7} className="text-center py-10 text-gray-400">No accounts yet</td></tr>
                    ) : accounts.map(a => (
                      <tr key={a.id} className={`border-b border-gray-100 last:border-0 hover:bg-gray-50 ${!a.is_active ? 'opacity-50' : ''}`}>
                        <td className="px-4 py-2.5 font-mono font-medium">{a.account_code}</td>
                        <td className="px-4 py-2.5 text-black">{a.account_name}</td>
                        <td className="px-4 py-2.5"><AccountTypeBadge type={a.account_type} /></td>
                        <td className="px-4 py-2.5 text-gray-500 text-xs">{a.account_category || '-'}</td>
                        <td className="px-4 py-2.5 text-center">{a.is_system ? <FiLock size={14} className="inline text-gray-400" /> : '-'}</td>
                        <td className="px-4 py-2.5 text-center">{a.is_active ? <FiCheckCircle size={14} className="inline text-green-500" /> : <FiX size={14} className="inline text-red-400" />}</td>
                        <td className="px-4 py-2.5 text-right">
                          <button onClick={() => { setLedgerAccountId(a.id); setActiveTab(TABS.LEDGER); }} className="text-blue-600 hover:text-blue-800 text-xs flex items-center gap-1 ml-auto"><FiArrowRight size={12} /> Ledger</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Account Modal */}
              {showAccountModal && (
                <ModalOverlay onClose={() => setShowAccountModal(false)} title="New Account">
                  <form onSubmit={handleCreateAccount} className="space-y-4">
                    <FormField label="Account Code">
                      <input type="text" required value={accountForm.account_code} onChange={e => setAccountForm(f => ({ ...f, account_code: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="e.g. 6700" />
                    </FormField>
                    <FormField label="Account Name">
                      <input type="text" required value={accountForm.account_name} onChange={e => setAccountForm(f => ({ ...f, account_name: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="e.g. Cleaning Supplies" />
                    </FormField>
                    <FormField label="Account Type">
                      <select required value={accountForm.account_type} onChange={e => setAccountForm(f => ({ ...f, account_type: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                        <option value="asset">Asset</option>
                        <option value="liability">Liability</option>
                        <option value="equity">Equity</option>
                        <option value="revenue">Revenue</option>
                        <option value="expense">Expense</option>
                      </select>
                    </FormField>
                    <FormField label="Category (optional)">
                      <input type="text" value={accountForm.account_category} onChange={e => setAccountForm(f => ({ ...f, account_category: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="e.g. operating_expense" />
                    </FormField>
                    <FormField label="Description (optional)">
                      <input type="text" value={accountForm.description} onChange={e => setAccountForm(f => ({ ...f, description: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                    </FormField>
                    <div className="flex justify-end gap-2 pt-2">
                      <button type="button" onClick={() => setShowAccountModal(false)} className="px-4 py-2 rounded-lg border border-gray-300 text-sm">Cancel</button>
                      <button type="submit" className="bg-black text-white px-4 py-2 rounded-lg text-sm">Create Account</button>
                    </div>
                  </form>
                </ModalOverlay>
              )}
            </div>
          )}

          {/* ═══════ TRIAL BALANCE ═══════ */}
          {activeTab === TABS.TRIAL_BALANCE && (
            <div className="space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <h3 className="text-lg font-semibold text-black">Trial Balance</h3>
                <div className="flex items-center gap-2">
                  <input type="date" value={tbFilters.from_date} onChange={e => setTbFilters(f => ({ ...f, from_date: e.target.value }))} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
                  <span className="text-gray-400 text-sm">to</span>
                  <input type="date" value={tbFilters.to_date} onChange={e => setTbFilters(f => ({ ...f, to_date: e.target.value }))} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
                  <button onClick={fetchTrialBalance} className="bg-black text-white px-3 py-1.5 rounded-lg text-sm"><FiFilter size={14} /></button>
                  <button onClick={() => exportCSV(trialBalance.trial_balance, 'trial_balance.csv')} className="text-gray-600 px-3 py-1.5 rounded-lg text-sm border border-gray-300" title="Export CSV"><FiDownload size={14} /></button>
                  <button onClick={() => exportPDF('Trial Balance', () => {
                    const rows = (trialBalance.trial_balance || []).map(r => `<tr><td>${r.account_code}</td><td>${r.account_name}</td><td>${r.account_type}</td><td class="right">${Number(r.total_debit) > 0 ? fmtPDF(r.total_debit) : '-'}</td><td class="right">${Number(r.total_credit) > 0 ? fmtPDF(r.total_credit) : '-'}</td><td class="right">${fmtPDF(r.balance)}</td></tr>`).join('');
                    const t = trialBalance.totals || {};
                    return `<div style="text-align:center;margin-bottom:16px;"><div class="summary-box"><div class="label">Total Debits</div><div class="value">${fmtPDF(t.total_debits)}</div></div><div class="summary-box"><div class="label">Total Credits</div><div class="value">${fmtPDF(t.total_credits)}</div></div><div class="summary-box"><div class="label">Status</div><div class="value ${t.is_balanced ? 'positive' : 'negative'}">${t.is_balanced ? '✓ Balanced' : '✗ Unbalanced'}</div></div></div><table><thead><tr><th>Code</th><th>Account</th><th>Type</th><th class="right">Debit</th><th class="right">Credit</th><th class="right">Balance</th></tr></thead><tbody>${rows}</tbody></table>`;
                  })} className="text-gray-600 px-3 py-1.5 rounded-lg text-sm border border-gray-300" title="Export PDF"><FiFileText size={14} /></button>
                </div>
              </div>

              {/* Balance indicator */}
              {trialBalance.totals && (
                <div className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm ${trialBalance.totals.is_balanced ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                  {trialBalance.totals.is_balanced ? <FiCheckCircle size={16} /> : <FiAlertTriangle size={16} />}
                  <span>{trialBalance.totals.is_balanced ? 'Books are balanced' : `Imbalance: ${formatCurrency(trialBalance.totals.difference)}`}</span>
                  <span className="ml-auto">Total Debits: {formatCurrency(trialBalance.totals.total_debits)} | Total Credits: {formatCurrency(trialBalance.totals.total_credits)}</span>
                </div>
              )}

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-4 py-2.5 text-xs text-gray-500 font-medium">Code</th>
                      <th className="text-left px-4 py-2.5 text-xs text-gray-500 font-medium">Account</th>
                      <th className="text-left px-4 py-2.5 text-xs text-gray-500 font-medium">Type</th>
                      <th className="text-right px-4 py-2.5 text-xs text-gray-500 font-medium">Debit</th>
                      <th className="text-right px-4 py-2.5 text-xs text-gray-500 font-medium">Credit</th>
                      <th className="text-right px-4 py-2.5 text-xs text-gray-500 font-medium">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(trialBalance.trial_balance || []).length === 0 ? (
                      <tr><td colSpan={6} className="text-center py-10 text-gray-400">No data</td></tr>
                    ) : trialBalance.trial_balance.map((r, i) => (
                      <tr key={i} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                        <td className="px-4 py-2 font-mono">{r.account_code}</td>
                        <td className="px-4 py-2 text-black">{r.account_name}</td>
                        <td className="px-4 py-2"><AccountTypeBadge type={r.account_type} /></td>
                        <td className="px-4 py-2 text-right font-mono">{Number(r.total_debit) > 0 ? formatCurrency(r.total_debit) : '-'}</td>
                        <td className="px-4 py-2 text-right font-mono">{Number(r.total_credit) > 0 ? formatCurrency(r.total_credit) : '-'}</td>
                        <td className={`px-4 py-2 text-right font-mono font-medium ${Number(r.balance) < 0 ? 'text-red-600' : 'text-black'}`}>{formatCurrency(r.balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ═══════ BALANCE SHEET ═══════ */}
          {activeTab === TABS.BALANCE_SHEET && (
            <div className="space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <h3 className="text-lg font-semibold text-black">Balance Sheet</h3>
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-500">As of:</label>
                  <input type="date" value={bsDate} onChange={e => setBsDate(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
                  <button onClick={fetchBalanceSheet} className="bg-black text-white px-4 py-1.5 rounded-lg text-sm flex items-center gap-1"><FiRefreshCw size={14} /> Refresh</button>
                  {balanceSheet && <button onClick={() => exportPDF(`Balance Sheet — As of ${formatDate(balanceSheet.as_of_date)}`, () => {
                    const t = balanceSheet.totals;
                    const mkRows = (arr) => arr.map(a => `<tr><td>${a.account_code}</td><td>${a.account_name}</td><td class="right">${fmtPDF(a.balance)}</td></tr>`).join('');
                    return `<div style="text-align:center;margin-bottom:16px;"><div class="summary-box"><div class="label">Total Assets</div><div class="value">${fmtPDF(t.total_assets)}</div></div><div class="summary-box"><div class="label">Total Liabilities</div><div class="value">${fmtPDF(t.total_liabilities)}</div></div><div class="summary-box"><div class="label">Total Equity</div><div class="value">${fmtPDF(t.total_equity + t.retained_earnings)}</div></div></div><div class="section-title">Assets</div><table><thead><tr><th>Code</th><th>Account</th><th class="right">Balance</th></tr></thead><tbody>${mkRows(balanceSheet.assets)}<tr class="total-row"><td colspan="2">Total Assets</td><td class="right">${fmtPDF(t.total_assets)}</td></tr></tbody></table><div class="section-title">Liabilities</div><table><thead><tr><th>Code</th><th>Account</th><th class="right">Balance</th></tr></thead><tbody>${mkRows(balanceSheet.liabilities)}<tr class="total-row"><td colspan="2">Total Liabilities</td><td class="right">${fmtPDF(t.total_liabilities)}</td></tr></tbody></table><div class="section-title">Equity</div><table><thead><tr><th>Code</th><th>Account</th><th class="right">Balance</th></tr></thead><tbody>${mkRows(balanceSheet.equity)}<tr><td>—</td><td>Retained Earnings (Net Income)</td><td class="right">${fmtPDF(t.retained_earnings)}</td></tr><tr class="total-row"><td colspan="2">Total Equity</td><td class="right">${fmtPDF(t.total_equity + t.retained_earnings)}</td></tr></tbody></table>`;
                  })} className="text-gray-600 px-3 py-1.5 rounded-lg text-sm border border-gray-300" title="Export PDF"><FiFileText size={14} /></button>}
                </div>
              </div>

              {balanceSheet && (
                <>
                  <p className="text-xs text-gray-400">As of {formatDate(balanceSheet.as_of_date)}</p>

                  {/* Equation indicator */}
                  <div className={`p-3 rounded-lg text-sm flex items-center gap-2 ${balanceSheet.totals.is_balanced ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                    {balanceSheet.totals.is_balanced ? <FiCheckCircle size={16} /> : <FiAlertTriangle size={16} />}
                    <span className="font-medium">Assets {formatCurrency(balanceSheet.totals.total_assets)}</span>
                    <span>=</span>
                    <span className="font-medium">Liabilities {formatCurrency(balanceSheet.totals.total_liabilities)}</span>
                    <span>+</span>
                    <span className="font-medium">Equity {formatCurrency(balanceSheet.totals.total_equity + balanceSheet.totals.retained_earnings)}</span>
                    <span className="ml-auto text-xs">{balanceSheet.totals.is_balanced ? '✓ Balanced' : '✗ Unbalanced'}</span>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* LEFT: Assets */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                      <div className="px-4 py-3 bg-blue-50 border-b border-blue-100">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-semibold text-blue-800">ASSETS</h4>
                          <span className="text-sm font-bold text-blue-800">{formatCurrency(balanceSheet.totals.total_assets)}</span>
                        </div>
                      </div>
                      <table className="w-full text-sm">
                        <tbody className="divide-y divide-gray-100">
                          {balanceSheet.assets.map((a, i) => (
                            <tr key={i} className="hover:bg-gray-50">
                              <td className="px-4 py-2">
                                <span className="text-gray-400 mr-2">{a.account_code}</span>
                                <span className="text-gray-800">{a.account_name}</span>
                              </td>
                              <td className="px-4 py-2 text-right font-mono font-medium text-black">{formatCurrency(a.balance)}</td>
                            </tr>
                          ))}
                          {balanceSheet.assets.length === 0 && (
                            <tr><td colSpan={2} className="px-4 py-4 text-center text-gray-400 text-xs">No asset activity</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* RIGHT: Liabilities + Equity */}
                    <div className="space-y-4">
                      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="px-4 py-3 bg-orange-50 border-b border-orange-100">
                          <div className="flex items-center justify-between">
                            <h4 className="text-sm font-semibold text-orange-800">LIABILITIES</h4>
                            <span className="text-sm font-bold text-orange-800">{formatCurrency(balanceSheet.totals.total_liabilities)}</span>
                          </div>
                        </div>
                        <table className="w-full text-sm">
                          <tbody className="divide-y divide-gray-100">
                            {balanceSheet.liabilities.map((l, i) => (
                              <tr key={i} className="hover:bg-gray-50">
                                <td className="px-4 py-2">
                                  <span className="text-gray-400 mr-2">{l.account_code}</span>
                                  <span className="text-gray-800">{l.account_name}</span>
                                </td>
                                <td className="px-4 py-2 text-right font-mono font-medium text-black">{formatCurrency(l.balance)}</td>
                              </tr>
                            ))}
                            {balanceSheet.liabilities.length === 0 && (
                              <tr><td colSpan={2} className="px-4 py-4 text-center text-gray-400 text-xs">No liability activity</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="px-4 py-3 bg-purple-50 border-b border-purple-100">
                          <div className="flex items-center justify-between">
                            <h4 className="text-sm font-semibold text-purple-800">EQUITY</h4>
                            <span className="text-sm font-bold text-purple-800">{formatCurrency(balanceSheet.totals.total_equity + balanceSheet.totals.retained_earnings)}</span>
                          </div>
                        </div>
                        <table className="w-full text-sm">
                          <tbody className="divide-y divide-gray-100">
                            {balanceSheet.equity.map((e, i) => (
                              <tr key={i} className="hover:bg-gray-50">
                                <td className="px-4 py-2">
                                  <span className="text-gray-400 mr-2">{e.account_code}</span>
                                  <span className="text-gray-800">{e.account_name}</span>
                                </td>
                                <td className="px-4 py-2 text-right font-mono font-medium text-black">{formatCurrency(e.balance)}</td>
                              </tr>
                            ))}
                            <tr className="bg-purple-50">
                              <td className="px-4 py-2">
                                <span className="text-gray-400 mr-2">—</span>
                                <span className="text-purple-700 font-medium">Retained Earnings (Net Income)</span>
                              </td>
                              <td className="px-4 py-2 text-right font-mono font-bold text-purple-800">{formatCurrency(balanceSheet.totals.retained_earnings)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {!balanceSheet && (
                <div className="text-center py-12 text-gray-400">No data — post journal entries to see the balance sheet</div>
              )}
            </div>
          )}

          {/* ═══════ CASH FLOW ═══════ */}
          {activeTab === TABS.CASH_FLOW && (
            <div className="space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <h3 className="text-lg font-semibold text-black">Cash Flow Statement</h3>
                <div className="flex items-center gap-2">
                  <input type="date" value={cfFilters.from_date} onChange={e => setCfFilters(f => ({ ...f, from_date: e.target.value }))} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
                  <span className="text-gray-400 text-xs">to</span>
                  <input type="date" value={cfFilters.to_date} onChange={e => setCfFilters(f => ({ ...f, to_date: e.target.value }))} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
                  <button onClick={fetchCashFlow} className="bg-black text-white px-4 py-1.5 rounded-lg text-sm flex items-center gap-1"><FiRefreshCw size={14} /> Refresh</button>
                  {cashFlow && <button onClick={() => exportPDF('Cash Flow Statement', () => {
                    const t = cashFlow.totals;
                    const mkRows = (arr) => arr.map(r => `<tr><td>${r.account_code} — ${r.account_name}</td><td class="right ${parseFloat(r.amount) >= 0 ? 'positive' : 'negative'}">${fmtPDF(r.amount)}</td></tr>`).join('');
                    let html = `<div style="text-align:center;margin-bottom:16px;"><div class="summary-box"><div class="label">Cash from Operations</div><div class="value ${t.net_cash_from_operations >= 0 ? 'positive' : 'negative'}">${fmtPDF(t.net_cash_from_operations)}</div></div><div class="summary-box"><div class="label">Net Financing</div><div class="value">${fmtPDF(t.net_financing)}</div></div><div class="summary-box"><div class="label">Cash Balance</div><div class="value">${fmtPDF(t.total_cash_balance)}</div></div></div>`;
                    html += `<div class="section-title">Operating Activities — ${fmtPDF(t.net_operating)}</div><table><tbody>${mkRows(cashFlow.operating)}</tbody></table>`;
                    if (cashFlow.working_capital.length > 0) html += `<div class="section-title">Working Capital Changes — ${fmtPDF(t.net_working_capital)}</div><table><tbody>${mkRows(cashFlow.working_capital)}</tbody></table>`;
                    if (cashFlow.financing.length > 0) html += `<div class="section-title">Financing Activities — ${fmtPDF(t.net_financing)}</div><table><tbody>${mkRows(cashFlow.financing)}</tbody></table>`;
                    html += `<div class="section-title">Cash & Cash Equivalents — ${fmtPDF(t.total_cash_balance)}</div><table><tbody>${cashFlow.cash_accounts.map(r => `<tr><td>${r.account_code} — ${r.account_name}</td><td class="right">${fmtPDF(r.balance)}</td></tr>`).join('')}</tbody></table>`;
                    return html;
                  })} className="text-gray-600 px-3 py-1.5 rounded-lg text-sm border border-gray-300" title="Export PDF"><FiFileText size={14} /></button>}
                </div>
              </div>

              {cashFlow && (
                <>
                  {/* Net Change Summary */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <SummaryCard icon={FiTrendingUp} label="Cash from Operations" value={formatCurrency(cashFlow.totals.net_cash_from_operations)} color={cashFlow.totals.net_cash_from_operations >= 0 ? 'green' : 'red'} />
                    <SummaryCard icon={FiActivity} label="Net Financing" value={formatCurrency(cashFlow.totals.net_financing)} color={cashFlow.totals.net_financing >= 0 ? 'green' : 'red'} />
                    <SummaryCard icon={FiDollarSign} label="Cash Balance" value={formatCurrency(cashFlow.totals.total_cash_balance)} color="blue" />
                  </div>

                  {/* Operating Activities */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="px-4 py-3 bg-green-50 border-b border-green-100 flex items-center justify-between">
                      <h4 className="text-sm font-semibold text-green-800">Operating Activities</h4>
                      <span className="text-sm font-bold text-green-800">{formatCurrency(cashFlow.totals.net_operating)}</span>
                    </div>
                    <table className="w-full text-sm">
                      <tbody className="divide-y divide-gray-100">
                        {cashFlow.operating.map((r, i) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="px-4 py-2">
                              <span className="text-gray-400 mr-2">{r.account_code}</span>
                              <span className="text-gray-800">{r.account_name}</span>
                              <span className="ml-2 text-xs text-gray-400">({r.account_type})</span>
                            </td>
                            <td className={`px-4 py-2 text-right font-mono font-medium ${parseFloat(r.amount) >= 0 ? 'text-green-700' : 'text-red-600'}`}>{formatCurrency(r.amount)}</td>
                          </tr>
                        ))}
                        {cashFlow.operating.length === 0 && (
                          <tr><td colSpan={2} className="px-4 py-4 text-center text-gray-400 text-xs">No operating activity</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Working Capital Changes */}
                  {cashFlow.working_capital.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                      <div className="px-4 py-3 bg-yellow-50 border-b border-yellow-100 flex items-center justify-between">
                        <h4 className="text-sm font-semibold text-yellow-800">Working Capital Changes</h4>
                        <span className="text-sm font-bold text-yellow-800">{formatCurrency(cashFlow.totals.net_working_capital)}</span>
                      </div>
                      <table className="w-full text-sm">
                        <tbody className="divide-y divide-gray-100">
                          {cashFlow.working_capital.map((r, i) => (
                            <tr key={i} className="hover:bg-gray-50">
                              <td className="px-4 py-2">
                                <span className="text-gray-400 mr-2">{r.account_code}</span>
                                <span className="text-gray-800">{r.account_name}</span>
                              </td>
                              <td className={`px-4 py-2 text-right font-mono font-medium ${parseFloat(r.amount) >= 0 ? 'text-green-700' : 'text-red-600'}`}>{formatCurrency(r.amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Financing Activities */}
                  {cashFlow.financing.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                      <div className="px-4 py-3 bg-purple-50 border-b border-purple-100 flex items-center justify-between">
                        <h4 className="text-sm font-semibold text-purple-800">Financing / Equity Activities</h4>
                        <span className="text-sm font-bold text-purple-800">{formatCurrency(cashFlow.totals.net_financing)}</span>
                      </div>
                      <table className="w-full text-sm">
                        <tbody className="divide-y divide-gray-100">
                          {cashFlow.financing.map((r, i) => (
                            <tr key={i} className="hover:bg-gray-50">
                              <td className="px-4 py-2">
                                <span className="text-gray-400 mr-2">{r.account_code}</span>
                                <span className="text-gray-800">{r.account_name}</span>
                              </td>
                              <td className={`px-4 py-2 text-right font-mono font-medium ${parseFloat(r.amount) >= 0 ? 'text-green-700' : 'text-red-600'}`}>{formatCurrency(r.amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Cash Accounts Breakdown */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="px-4 py-3 bg-blue-50 border-b border-blue-100 flex items-center justify-between">
                      <h4 className="text-sm font-semibold text-blue-800">Cash & Cash Equivalents</h4>
                      <span className="text-sm font-bold text-blue-800">{formatCurrency(cashFlow.totals.total_cash_balance)}</span>
                    </div>
                    <table className="w-full text-sm">
                      <tbody className="divide-y divide-gray-100">
                        {cashFlow.cash_accounts.map((r, i) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="px-4 py-2">
                              <span className="text-gray-400 mr-2">{r.account_code}</span>
                              <span className="text-gray-800">{r.account_name}</span>
                            </td>
                            <td className="px-4 py-2 text-right font-mono font-medium text-blue-800">{formatCurrency(r.balance)}</td>
                          </tr>
                        ))}
                        {cashFlow.cash_accounts.length === 0 && (
                          <tr><td colSpan={2} className="px-4 py-4 text-center text-gray-400 text-xs">No cash account activity</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {!cashFlow && (
                <div className="text-center py-12 text-gray-400">No data — post journal entries to see cash flow</div>
              )}
            </div>
          )}

          {/* ═══════ P&L ═══════ */}
          {activeTab === TABS.PNL && (
            <div className="space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <h3 className="text-lg font-semibold text-black">Profit & Loss Statement</h3>
                <div className="flex items-center gap-2">
                  <input type="date" value={pnlFilters.from_date} onChange={e => setPnlFilters(f => ({ ...f, from_date: e.target.value }))} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
                  <span className="text-gray-400 text-sm">to</span>
                  <input type="date" value={pnlFilters.to_date} onChange={e => setPnlFilters(f => ({ ...f, to_date: e.target.value }))} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
                  <button onClick={fetchPnl} className="bg-black text-white px-3 py-1.5 rounded-lg text-sm"><FiFilter size={14} /></button>
                  <button onClick={() => exportCSV([...pnl.revenue, ...pnl.expenses], 'pnl.csv')} className="text-gray-600 px-3 py-1.5 rounded-lg text-sm border border-gray-300" title="Export CSV"><FiDownload size={14} /></button>
                  <button onClick={() => exportPDF('Profit & Loss Statement', () => {
                    const t = pnl.totals || {};
                    const mkRows = (arr) => arr.map(r => `<tr><td>${r.account_code}</td><td>${r.account_name}</td><td class="right">${fmtPDF(Math.abs(r.net_amount))}</td></tr>`).join('');
                    const ni = t.net_income || 0;
                    return `<div style="text-align:center;margin-bottom:16px;"><div class="summary-box"><div class="label">Total Revenue</div><div class="value positive">${fmtPDF(t.total_revenue)}</div></div><div class="summary-box"><div class="label">Total Expenses</div><div class="value negative">${fmtPDF(t.total_expenses)}</div></div><div class="summary-box"><div class="label">Net Income</div><div class="value ${ni >= 0 ? 'positive' : 'negative'}">${fmtPDF(ni)}</div></div></div><div class="section-title" style="border-left-color:#16a34a;">Revenue — ${fmtPDF(t.total_revenue)}</div><table><thead><tr><th>Code</th><th>Account</th><th class="right">Amount</th></tr></thead><tbody>${mkRows(pnl.revenue || [])}<tr class="total-row"><td colspan="2">Total Revenue</td><td class="right">${fmtPDF(t.total_revenue)}</td></tr></tbody></table><div class="section-title" style="border-left-color:#dc2626;">Expenses — ${fmtPDF(t.total_expenses)}</div><table><thead><tr><th>Code</th><th>Account</th><th class="right">Amount</th></tr></thead><tbody>${mkRows(pnl.expenses || [])}<tr class="total-row"><td colspan="2">Total Expenses</td><td class="right">${fmtPDF(t.total_expenses)}</td></tr></tbody></table><div style="margin-top:20px;padding:16px;background:${ni >= 0 ? '#f0fdf4' : '#fef2f2'};border:2px solid ${ni >= 0 ? '#16a34a' : '#dc2626'};border-radius:6px;text-align:center;"><div style="font-size:12px;color:#666;">NET INCOME</div><div style="font-size:24px;font-weight:700;color:${ni >= 0 ? '#16a34a' : '#dc2626'};">${fmtPDF(ni)}</div></div>`;
                  })} className="text-gray-600 px-3 py-1.5 rounded-lg text-sm border border-gray-300" title="Export PDF"><FiFileText size={14} /></button>
                </div>
              </div>

              {/* Revenue Section */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 bg-green-50 border-b border-green-100 flex justify-between items-center">
                  <h4 className="text-sm font-semibold text-green-800">Revenue</h4>
                  <span className="text-sm font-bold text-green-700">{formatCurrency(pnl.totals?.total_revenue)}</span>
                </div>
                <table className="w-full text-sm">
                  <tbody>
                    {(pnl.revenue || []).map((r, i) => (
                      <tr key={i} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                        <td className="px-4 py-2 font-mono text-gray-500">{r.account_code}</td>
                        <td className="px-4 py-2 text-black">{r.account_name}</td>
                        <td className="px-4 py-2 text-right font-mono font-medium text-green-700">{formatCurrency(r.net_amount)}</td>
                      </tr>
                    ))}
                    {(!pnl.revenue || pnl.revenue.length === 0) && (
                      <tr><td colSpan={3} className="text-center py-6 text-gray-400">No revenue entries</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Expenses Section */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 bg-red-50 border-b border-red-100 flex justify-between items-center">
                  <h4 className="text-sm font-semibold text-red-800">Expenses</h4>
                  <span className="text-sm font-bold text-red-700">{formatCurrency(pnl.totals?.total_expenses)}</span>
                </div>
                <table className="w-full text-sm">
                  <tbody>
                    {(pnl.expenses || []).map((r, i) => (
                      <tr key={i} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                        <td className="px-4 py-2 font-mono text-gray-500">{r.account_code}</td>
                        <td className="px-4 py-2 text-black">{r.account_name}</td>
                        <td className="px-4 py-2 text-right font-mono font-medium text-red-700">{formatCurrency(Math.abs(r.net_amount))}</td>
                      </tr>
                    ))}
                    {(!pnl.expenses || pnl.expenses.length === 0) && (
                      <tr><td colSpan={3} className="text-center py-6 text-gray-400">No expense entries</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Net Income */}
              <div className={`px-6 py-4 rounded-xl shadow-sm border ${(pnl.totals?.net_income || 0) >= 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                <div className="flex justify-between items-center">
                  <span className="text-base font-semibold text-black">Net Income</span>
                  <span className={`text-xl font-bold ${(pnl.totals?.net_income || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {formatCurrency(pnl.totals?.net_income)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* ═══════ TAX SUMMARY ═══════ */}
          {activeTab === TABS.TAX && (
            <div className="space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <h3 className="text-lg font-semibold text-black">Tax Summary</h3>
                <div className="flex items-center gap-2">
                  <input type="date" value={taxFilters.from_date} onChange={e => setTaxFilters(f => ({ ...f, from_date: e.target.value }))} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
                  <span className="text-gray-400 text-sm">to</span>
                  <input type="date" value={taxFilters.to_date} onChange={e => setTaxFilters(f => ({ ...f, to_date: e.target.value }))} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
                  <button onClick={fetchTaxSummary} className="bg-black text-white px-3 py-1.5 rounded-lg text-sm"><FiFilter size={14} /></button>
                  <button onClick={() => exportCSV(taxSummary.tax_accounts, 'tax_summary.csv')} className="text-gray-600 px-3 py-1.5 rounded-lg text-sm border border-gray-300"><FiDownload size={14} /></button>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-4 py-2.5 text-xs text-gray-500 font-medium">Account</th>
                      <th className="text-right px-4 py-2.5 text-xs text-gray-500 font-medium">Collected</th>
                      <th className="text-right px-4 py-2.5 text-xs text-gray-500 font-medium">Paid</th>
                      <th className="text-right px-4 py-2.5 text-xs text-gray-500 font-medium">Net Payable</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(taxSummary.tax_accounts || []).length === 0 ? (
                      <tr><td colSpan={4} className="text-center py-10 text-gray-400">No tax data</td></tr>
                    ) : taxSummary.tax_accounts.map((t, i) => (
                      <tr key={i} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                        <td className="px-4 py-2"><span className="font-mono">{t.account_code}</span> {t.account_name}</td>
                        <td className="px-4 py-2 text-right font-mono">{formatCurrency(t.total_collected)}</td>
                        <td className="px-4 py-2 text-right font-mono">{formatCurrency(t.total_paid)}</td>
                        <td className="px-4 py-2 text-right font-mono font-medium">{formatCurrency(t.net_payable)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50 border-t border-gray-200">
                    <tr>
                      <td className="px-4 py-2.5 font-semibold text-black" colSpan={3}>Total Tax Payable</td>
                      <td className="px-4 py-2.5 text-right font-mono font-bold text-black">{formatCurrency(taxSummary.total_tax_payable)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {/* ═══════ PERIODS & FISCAL YEARS ═══════ */}
          {activeTab === TABS.PERIODS && (
            <div className="space-y-6">
              {/* Fiscal Years Section */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-black">Fiscal Years</h3>
                  <button onClick={() => setShowFYModal(true)} className="bg-black text-white px-4 py-2 rounded-lg text-sm flex items-center gap-1.5"><FiPlus size={15} /> New Fiscal Year</button>
                </div>
                <div className="space-y-2">
                  {fiscalYears.length === 0 ? (
                    <div className="text-center py-6 text-gray-400 text-sm">No fiscal years defined yet. Create one to start tracking periods.</div>
                  ) : fiscalYears.map(fy => (
                    <div key={fy.id} className="bg-white rounded-xl shadow-sm border border-gray-200 px-4 py-3 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-black">{fy.label}</p>
                        <p className="text-xs text-gray-400">{formatDate(fy.start_date)} – {formatDate(fy.end_date)}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${fy.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'}`}>
                          {fy.is_active ? 'Active' : 'Inactive'}
                        </span>
                        <button
                          onClick={() => handleToggleFiscalYear(fy.id, fy.is_active)}
                          className={`text-xs px-3 py-1 rounded-lg border ${fy.is_active ? 'text-red-500 border-red-200 hover:bg-red-50' : 'text-green-600 border-green-200 hover:bg-green-50'}`}
                        >
                          {fy.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Accounting Periods Section */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-black">Accounting Periods</h3>
                  <button onClick={handleOpenNextPeriod} className="bg-black text-white px-4 py-2 rounded-lg text-sm flex items-center gap-1.5"><FiPlus size={15} /> Open Next Period</button>
                </div>
                <div className="space-y-2">
                  {periods.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">No periods created yet. Click "Open Next Period" to start.</div>
                  ) : periods.map(p => (
                    <div key={p.id} className="bg-white rounded-xl shadow-sm border border-gray-200 px-4 py-3 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-black">{p.label}</p>
                        <p className="text-xs text-gray-400">{formatDate(p.period_start)} – {formatDate(p.period_end)}</p>
                        {p.fiscal_years && <p className="text-xs text-gray-300">{p.fiscal_years.label}</p>}
                      </div>
                      <div className="flex items-center gap-3">
                        <StatusBadge status={p.status} />
                        {p.status === 'open' && (
                          <button
                            onClick={() => handleClosePeriod(p.id)}
                            className="text-red-500 hover:text-red-700 p-1 flex items-center gap-1 text-sm border border-red-200 rounded-lg px-3 py-1"
                            title="Close Period"
                          >
                            <FiLock size={14} /> Close
                          </button>
                        )}
                        {p.status === 'closed' && (
                          <span className="text-xs text-gray-400">{p.closed_at ? `Closed ${formatDateTime(p.closed_at)}` : ''}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Fiscal Year Modal */}
              {showFYModal && (
                <ModalOverlay onClose={() => setShowFYModal(false)} title="New Fiscal Year">
                  <form onSubmit={handleCreateFiscalYear} className="space-y-4">
                    <FormField label="Label *">
                      <input type="text" required value={fyForm.label} onChange={e => setFyForm(f => ({ ...f, label: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="e.g. FY 2025-26" />
                    </FormField>
                    <FormField label="Start Date *">
                      <input type="date" required value={fyForm.start_date} onChange={e => setFyForm(f => ({ ...f, start_date: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                    </FormField>
                    <FormField label="End Date *">
                      <input type="date" required value={fyForm.end_date} onChange={e => setFyForm(f => ({ ...f, end_date: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                    </FormField>
                    <div className="flex justify-end gap-2 pt-2">
                      <button type="button" onClick={() => setShowFYModal(false)} className="px-4 py-2 rounded-lg border border-gray-300 text-sm">Cancel</button>
                      <button type="submit" className="bg-black text-white px-4 py-2 rounded-lg text-sm">Create</button>
                    </div>
                  </form>
                </ModalOverlay>
              )}
            </div>
          )}

          {/* ═══════ OUTBOX & FAILURES ═══════ */}
          {activeTab === TABS.OUTBOX && (
            <div className="space-y-6">
              {/* Posting Outbox */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-black">Posting Outbox</h3>
                  <div className="flex items-center gap-2">
                    <select value={outboxFilter} onChange={e => { setOutboxFilter(e.target.value); }} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm">
                      <option value="">All Status</option>
                      <option value="pending">Pending</option>
                      <option value="processing">Processing</option>
                      <option value="completed">Completed</option>
                      <option value="failed">Failed</option>
                    </select>
                    <button onClick={fetchOutbox} className="bg-black text-white px-3 py-1.5 rounded-lg text-sm flex items-center gap-1"><FiRefreshCw size={14} /> Refresh</button>
                  </div>
                </div>
                <p className="text-xs text-gray-400">{outboxTotal} outbox entry(ies)</p>
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left px-4 py-2.5 text-xs text-gray-500 font-medium">Source</th>
                        <th className="text-left px-4 py-2.5 text-xs text-gray-500 font-medium">Created</th>
                        <th className="text-center px-4 py-2.5 text-xs text-gray-500 font-medium">Attempts</th>
                        <th className="text-center px-4 py-2.5 text-xs text-gray-500 font-medium">Status</th>
                        <th className="text-left px-4 py-2.5 text-xs text-gray-500 font-medium">Last Error</th>
                        <th className="text-center px-4 py-2.5 text-xs text-gray-500 font-medium">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {outboxEntries.length === 0 ? (
                        <tr><td colSpan={6} className="text-center py-10 text-gray-400">No outbox entries</td></tr>
                      ) : outboxEntries.map(entry => (
                        <tr key={entry.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                          <td className="px-4 py-2.5">
                            <p className="font-medium text-black">{entry.source_type}</p>
                            <p className="text-xs text-gray-400 font-mono">{entry.source_id?.slice(0, 8)}…</p>
                          </td>
                          <td className="px-4 py-2.5 text-gray-600">{formatDateTime(entry.created_at)}</td>
                          <td className="px-4 py-2.5 text-center">
                            <span className="font-mono">{entry.attempts}/{entry.max_attempts}</span>
                          </td>
                          <td className="px-4 py-2.5 text-center"><StatusBadge status={entry.status} /></td>
                          <td className="px-4 py-2.5 text-xs text-red-500 max-w-xs truncate">{entry.last_error || '-'}</td>
                          <td className="px-4 py-2.5 text-center">
                            {(entry.status === 'pending' || entry.status === 'failed') && (
                              <button onClick={() => handleRetryOutbox(entry.id)} className="text-blue-600 hover:text-blue-800 p-1" title="Retry">
                                <FiPlay size={15} />
                              </button>
                            )}
                            {entry.status === 'completed' && <FiCheckCircle className="text-green-500 inline" size={15} />}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Posting Failures (Dead Letter) */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-black flex items-center gap-2"><FiAlertTriangle className="text-red-500" size={18} /> Posting Failures</h3>
                  <div className="flex items-center gap-2">
                    <select value={failureFilter} onChange={e => { setFailureFilter(e.target.value); }} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm">
                      <option value="false">Unresolved</option>
                      <option value="true">Resolved</option>
                      <option value="">All</option>
                    </select>
                    <button onClick={fetchFailures} className="bg-black text-white px-3 py-1.5 rounded-lg text-sm flex items-center gap-1"><FiRefreshCw size={14} /> Refresh</button>
                  </div>
                </div>
                <p className="text-xs text-gray-400">{failuresTotal} failure(s)</p>
                <div className="space-y-2">
                  {postingFailures.length === 0 ? (
                    <div className="text-center py-10 text-gray-400 bg-white rounded-xl border border-gray-200">No posting failures</div>
                  ) : postingFailures.map(f => (
                    <div key={f.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-black">{f.source_type} <span className="text-gray-400 font-mono text-xs">({f.source_id?.slice(0, 8)}…)</span></p>
                          <p className="text-xs text-red-500 mt-1">{f.error_message}</p>
                          <p className="text-xs text-gray-400 mt-1">Created: {formatDateTime(f.created_at)}</p>
                          {f.resolved && <p className="text-xs text-green-600 mt-1">✓ Resolved: {f.resolution_notes} ({formatDateTime(f.resolved_at)})</p>}
                        </div>
                        <div className="flex items-center gap-2 ml-3">
                          {f.resolved ? (
                            <span className="text-green-600"><FiCheckCircle size={18} /></span>
                          ) : (
                            <button onClick={() => handleResolveFailure(f.id)} className="text-sm border border-green-300 text-green-700 hover:bg-green-50 px-3 py-1 rounded-lg flex items-center gap-1">
                              <FiCheckCircle size={14} /> Resolve
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ═══════ AUDIT LOG ═══════ */}
          {activeTab === TABS.AUDIT && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-black flex items-center gap-2"><FiEye size={18} /> Audit Log</h3>
                <div className="flex items-center gap-2">
                  <button onClick={() => exportCSV(auditLog, 'audit-log.csv')} className="border border-gray-300 px-3 py-1.5 rounded-lg text-sm flex items-center gap-1"><FiDownload size={14} /> Export</button>
                  <button onClick={fetchAuditLog} className="bg-black text-white px-3 py-1.5 rounded-lg text-sm flex items-center gap-1"><FiRefreshCw size={14} /> Refresh</button>
                </div>
              </div>

              {/* Audit Filters */}
              <div className="flex flex-wrap gap-2 items-center bg-gray-50 rounded-xl px-4 py-3">
                <FiFilter className="text-gray-400" size={14} />
                <input
                  type="text"
                  placeholder="Search actions…"
                  value={auditFilters.search}
                  onChange={e => setAuditFilters(f => ({ ...f, search: e.target.value }))}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-40"
                />
                <select value={auditFilters.entity_type} onChange={e => setAuditFilters(f => ({ ...f, entity_type: e.target.value }))} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm">
                  <option value="">All Entities</option>
                  <option value="journal_entry">Journal Entry</option>
                  <option value="expense_entry">Expense</option>
                  <option value="chart_of_accounts">Account</option>
                  <option value="accounting_period">Period</option>
                  <option value="fiscal_year">Fiscal Year</option>
                  <option value="outbox_entry">Outbox</option>
                  <option value="posting_failure">Failure</option>
                </select>
                <input type="date" value={auditFilters.from_date} onChange={e => setAuditFilters(f => ({ ...f, from_date: e.target.value }))} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
                <span className="text-gray-400 text-xs">to</span>
                <input type="date" value={auditFilters.to_date} onChange={e => setAuditFilters(f => ({ ...f, to_date: e.target.value }))} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
                {(auditFilters.search || auditFilters.entity_type || auditFilters.from_date || auditFilters.to_date) && (
                  <button onClick={() => setAuditFilters({ action: '', entity_type: '', from_date: '', to_date: '', search: '' })} className="text-red-500 text-xs flex items-center gap-1"><FiX size={12} /> Clear</button>
                )}
              </div>

              <p className="text-xs text-gray-400">{auditTotal} audit entry(ies)</p>

              {/* Audit Table */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-4 py-2.5 text-xs text-gray-500 font-medium">Timestamp</th>
                      <th className="text-left px-4 py-2.5 text-xs text-gray-500 font-medium">Action</th>
                      <th className="text-left px-4 py-2.5 text-xs text-gray-500 font-medium">Entity</th>
                      <th className="text-left px-4 py-2.5 text-xs text-gray-500 font-medium">Entity ID</th>
                      <th className="text-left px-4 py-2.5 text-xs text-gray-500 font-medium">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLog.length === 0 ? (
                      <tr><td colSpan={5} className="text-center py-10 text-gray-400">No audit entries found</td></tr>
                    ) : auditLog.map(entry => (
                      <tr key={entry.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                        <td className="px-4 py-2.5 text-gray-600 whitespace-nowrap">{formatDateTime(entry.created_at)}</td>
                        <td className="px-4 py-2.5">
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                            {entry.action?.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-gray-600 capitalize">{entry.entity_type?.replace(/_/g, ' ')}</td>
                        <td className="px-4 py-2.5 text-xs font-mono text-gray-500">{entry.entity_id?.slice(0, 8)}…</td>
                        <td className="px-4 py-2.5 text-xs text-gray-500 max-w-xs truncate">
                          {entry.details ? JSON.stringify(entry.details).slice(0, 80) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Aging Report Section */}
              <div className="mt-8 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-black flex items-center gap-2"><FiClock size={18} /> Aging Report</h3>
                  <div className="flex items-center gap-2">
                    <select value={agingType} onChange={e => setAgingType(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm">
                      <option value="receivable">Accounts Receivable</option>
                      <option value="payable">Accounts Payable</option>
                    </select>
                    <button onClick={fetchAging} className="bg-black text-white px-3 py-1.5 rounded-lg text-sm flex items-center gap-1"><FiRefreshCw size={14} /> Load</button>
                  </div>
                </div>

                {aging ? (
                  <div className="space-y-4">
                    {/* Aging Buckets */}
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 text-center">
                        <p className="text-xs text-gray-500 mb-1">Total Outstanding</p>
                        <p className="text-xl font-bold text-black">{formatCurrency(aging.total_outstanding)}</p>
                      </div>
                      <div className="bg-green-50 rounded-xl border border-green-200 p-4 text-center">
                        <p className="text-xs text-green-600 mb-1">Current (0–30d)</p>
                        <p className="text-lg font-bold text-green-700">{formatCurrency(aging.buckets?.current)}</p>
                      </div>
                      <div className="bg-yellow-50 rounded-xl border border-yellow-200 p-4 text-center">
                        <p className="text-xs text-yellow-600 mb-1">31–60 days</p>
                        <p className="text-lg font-bold text-yellow-700">{formatCurrency(aging.buckets?.days_30)}</p>
                      </div>
                      <div className="bg-orange-50 rounded-xl border border-orange-200 p-4 text-center">
                        <p className="text-xs text-orange-600 mb-1">61–90 days</p>
                        <p className="text-lg font-bold text-orange-700">{formatCurrency(aging.buckets?.days_60)}</p>
                      </div>
                      <div className="bg-red-50 rounded-xl border border-red-200 p-4 text-center">
                        <p className="text-xs text-red-600 mb-1">90+ days</p>
                        <p className="text-lg font-bold text-red-700">{formatCurrency(aging.buckets?.days_90_plus)}</p>
                      </div>
                    </div>

                    {/* Aging Detail Entries */}
                    {aging.entries && aging.entries.length > 0 && (
                      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                              <th className="text-left px-4 py-2.5 text-xs text-gray-500 font-medium">Date</th>
                              <th className="text-left px-4 py-2.5 text-xs text-gray-500 font-medium">Description</th>
                              <th className="text-left px-4 py-2.5 text-xs text-gray-500 font-medium">Source</th>
                              <th className="text-right px-4 py-2.5 text-xs text-gray-500 font-medium">Amount</th>
                              <th className="text-center px-4 py-2.5 text-xs text-gray-500 font-medium">Days</th>
                              <th className="text-center px-4 py-2.5 text-xs text-gray-500 font-medium">Bucket</th>
                            </tr>
                          </thead>
                          <tbody>
                            {aging.entries.map((e, i) => (
                              <tr key={i} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                                <td className="px-4 py-2.5">{formatDate(e.entry_date)}</td>
                                <td className="px-4 py-2.5 text-black font-medium">{e.description}</td>
                                <td className="px-4 py-2.5 text-gray-600 capitalize">{e.source_type}</td>
                                <td className="px-4 py-2.5 text-right font-mono">{formatCurrency(e.amount)}</td>
                                <td className="px-4 py-2.5 text-center">{e.days_outstanding}</td>
                                <td className="px-4 py-2.5 text-center">
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                    e.bucket === 'current' ? 'bg-green-100 text-green-800' :
                                    e.bucket === '31-60' ? 'bg-yellow-100 text-yellow-800' :
                                    e.bucket === '61-90' ? 'bg-orange-100 text-orange-800' :
                                    'bg-red-100 text-red-800'
                                  }`}>
                                    {e.bucket}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {aging.entries && aging.entries.length === 0 && (
                      <div className="text-center py-8 text-gray-400 bg-white rounded-xl border border-gray-200">
                        No outstanding {agingType === 'payable' ? 'payables' : 'receivables'} found
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-400 bg-white rounded-xl border border-gray-200">
                    Click "Load" to generate the {agingType === 'payable' ? 'Accounts Payable' : 'Accounts Receivable'} aging report
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ═══════ BUDGETS ═══════ */}
          {activeTab === TABS.BUDGET && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-black flex items-center gap-2"><FiTarget size={18} /> Budgets</h3>
                <button onClick={() => setShowBudgetModal(true)} className="bg-black text-white px-4 py-2 rounded-lg text-sm flex items-center gap-1.5"><FiPlus size={15} /> New Budget</button>
              </div>

              {/* Budget List */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {budgets.length === 0 ? (
                  <div className="col-span-2 text-center py-10 text-gray-400 bg-white rounded-xl border border-gray-200">No budgets created yet</div>
                ) : budgets.map(b => (
                  <div
                    key={b.id}
                    className={`bg-white rounded-xl shadow-sm border p-4 cursor-pointer transition-shadow hover:shadow-md ${selectedBudget?.id === b.id ? 'border-blue-500 ring-1 ring-blue-200' : 'border-gray-200'}`}
                    onClick={() => handleSelectBudget(b)}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm font-semibold text-black">{b.label}</p>
                        <p className="text-xs text-gray-400">{formatDate(b.period_start)} – {formatDate(b.period_end)}</p>
                        {b.fiscal_years && <p className="text-xs text-gray-300">{b.fiscal_years.label}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        <StatusBadge status={b.status} />
                        {b.status === 'draft' && (
                          <button onClick={(e) => { e.stopPropagation(); handleActivateBudget(b.id, 'active'); }} className="text-xs text-green-600 hover:text-green-800 border border-green-200 rounded px-2 py-0.5">Activate</button>
                        )}
                        {b.status === 'active' && (
                          <button onClick={(e) => { e.stopPropagation(); handleActivateBudget(b.id, 'closed'); }} className="text-xs text-red-500 hover:text-red-700 border border-red-200 rounded px-2 py-0.5">Close</button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Budget vs Actuals Comparison */}
              {selectedBudget && budgetComparison && (
                <div className="space-y-4 mt-6">
                  <div className="flex items-center justify-between">
                    <h4 className="text-base font-semibold text-black">Budget vs Actuals: {selectedBudget.label}</h4>
                    <div className="flex gap-2">
                      <button onClick={() => setShowBudgetLineModal(true)} className="border border-gray-300 px-3 py-1.5 rounded-lg text-sm flex items-center gap-1"><FiPlus size={14} /> Add Line</button>
                      <button onClick={() => exportCSV(budgetComparison.comparison, `budget-vs-actuals-${selectedBudget.label}.csv`)} className="border border-gray-300 px-3 py-1.5 rounded-lg text-sm flex items-center gap-1"><FiDownload size={14} /> Export</button>
                    </div>
                  </div>

                  {/* Totals Summary */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-blue-50 rounded-xl border border-blue-200 p-4 text-center">
                      <p className="text-xs text-blue-600 mb-1">Total Budgeted</p>
                      <p className="text-lg font-bold text-blue-700">{formatCurrency(budgetComparison.totals?.total_budgeted)}</p>
                    </div>
                    <div className="bg-green-50 rounded-xl border border-green-200 p-4 text-center">
                      <p className="text-xs text-green-600 mb-1">Total Actual</p>
                      <p className="text-lg font-bold text-green-700">{formatCurrency(budgetComparison.totals?.total_actual)}</p>
                    </div>
                    <div className={`rounded-xl border p-4 text-center ${budgetComparison.totals?.total_variance > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                      <p className={`text-xs mb-1 ${budgetComparison.totals?.total_variance > 0 ? 'text-red-600' : 'text-green-600'}`}>Total Variance</p>
                      <p className={`text-lg font-bold ${budgetComparison.totals?.total_variance > 0 ? 'text-red-700' : 'text-green-700'}`}>{formatCurrency(budgetComparison.totals?.total_variance)}</p>
                    </div>
                  </div>

                  {/* Line by Line Comparison */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="text-left px-4 py-2.5 text-xs text-gray-500 font-medium">Account</th>
                          <th className="text-right px-4 py-2.5 text-xs text-gray-500 font-medium">Budgeted</th>
                          <th className="text-right px-4 py-2.5 text-xs text-gray-500 font-medium">Actual</th>
                          <th className="text-right px-4 py-2.5 text-xs text-gray-500 font-medium">Variance</th>
                          <th className="text-right px-4 py-2.5 text-xs text-gray-500 font-medium">%</th>
                        </tr>
                      </thead>
                      <tbody>
                        {budgetComparison.comparison.length === 0 ? (
                          <tr><td colSpan={5} className="text-center py-10 text-gray-400">No budget lines. Add accounts to compare.</td></tr>
                        ) : budgetComparison.comparison.map((row, i) => (
                          <tr key={i} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                            <td className="px-4 py-2.5">
                              <span className="text-gray-500 text-xs mr-1">{row.account_code}</span>
                              <span className="text-black font-medium">{row.account_name}</span>
                            </td>
                            <td className="px-4 py-2.5 text-right font-mono">{formatCurrency(row.budgeted)}</td>
                            <td className="px-4 py-2.5 text-right font-mono">{formatCurrency(row.actual)}</td>
                            <td className={`px-4 py-2.5 text-right font-mono font-medium ${row.variance > 0 ? 'text-red-600' : row.variance < 0 ? 'text-green-600' : ''}`}>
                              {row.variance > 0 ? '+' : ''}{formatCurrency(row.variance)}
                            </td>
                            <td className={`px-4 py-2.5 text-right text-xs font-medium ${row.variance_pct > 10 ? 'text-red-600' : row.variance_pct < -10 ? 'text-green-600' : 'text-gray-500'}`}>
                              {row.variance_pct > 0 ? '+' : ''}{row.variance_pct}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Create Budget Modal */}
              {showBudgetModal && (
                <ModalOverlay onClose={() => setShowBudgetModal(false)} title="New Budget">
                  <form onSubmit={handleCreateBudget} className="space-y-4">
                    <FormField label="Label *">
                      <input type="text" required value={budgetForm.label} onChange={e => setBudgetForm(f => ({ ...f, label: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="e.g. Q1 2026 Budget" />
                    </FormField>
                    <FormField label="Fiscal Year">
                      <select value={budgetForm.fiscal_year_id} onChange={e => setBudgetForm(f => ({ ...f, fiscal_year_id: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                        <option value="">None</option>
                        {fiscalYears.map(fy => <option key={fy.id} value={fy.id}>{fy.label}</option>)}
                      </select>
                    </FormField>
                    <div className="grid grid-cols-2 gap-3">
                      <FormField label="Start Date *">
                        <input type="date" required value={budgetForm.period_start} onChange={e => setBudgetForm(f => ({ ...f, period_start: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      </FormField>
                      <FormField label="End Date *">
                        <input type="date" required value={budgetForm.period_end} onChange={e => setBudgetForm(f => ({ ...f, period_end: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      </FormField>
                    </div>
                    <FormField label="Notes">
                      <input type="text" value={budgetForm.notes} onChange={e => setBudgetForm(f => ({ ...f, notes: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                    </FormField>
                    <div className="flex justify-end gap-2 pt-2">
                      <button type="button" onClick={() => setShowBudgetModal(false)} className="px-4 py-2 rounded-lg border border-gray-300 text-sm">Cancel</button>
                      <button type="submit" className="bg-black text-white px-4 py-2 rounded-lg text-sm">Create Budget</button>
                    </div>
                  </form>
                </ModalOverlay>
              )}

              {/* Add Budget Line Modal */}
              {showBudgetLineModal && (
                <ModalOverlay onClose={() => setShowBudgetLineModal(false)} title="Add Budget Line">
                  <form onSubmit={handleAddBudgetLine} className="space-y-4">
                    <FormField label="Account *">
                      <select required value={budgetLineForm.account_id} onChange={e => setBudgetLineForm(f => ({ ...f, account_id: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                        <option value="">Select account</option>
                        {accounts.filter(a => a.is_active).map(a => <option key={a.id} value={a.id}>{a.account_code} – {a.account_name} ({a.account_type})</option>)}
                      </select>
                    </FormField>
                    <FormField label="Budgeted Amount (₹) *">
                      <input type="number" step="0.01" min="0" required value={budgetLineForm.budgeted_amount} onChange={e => setBudgetLineForm(f => ({ ...f, budgeted_amount: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                    </FormField>
                    <FormField label="Notes">
                      <input type="text" value={budgetLineForm.notes} onChange={e => setBudgetLineForm(f => ({ ...f, notes: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                    </FormField>
                    <div className="flex justify-end gap-2 pt-2">
                      <button type="button" onClick={() => setShowBudgetLineModal(false)} className="px-4 py-2 rounded-lg border border-gray-300 text-sm">Cancel</button>
                      <button type="submit" className="bg-black text-white px-4 py-2 rounded-lg text-sm">Save Line</button>
                    </div>
                  </form>
                </ModalOverlay>
              )}
            </div>
          )}

          {/* ═══════ RECURRING JOURNALS ═══════ */}
          {activeTab === TABS.RECURRING && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-black flex items-center gap-2"><FiRepeat size={18} /> Recurring Journals</h3>
                <button onClick={() => setShowRecurringModal(true)} className="bg-black text-white px-4 py-2 rounded-lg text-sm flex items-center gap-1.5"><FiPlus size={15} /> New Recurring</button>
              </div>

              <div className="space-y-3">
                {recurringJournals.length === 0 ? (
                  <div className="text-center py-10 text-gray-400 bg-white rounded-xl border border-gray-200">No recurring journals set up</div>
                ) : recurringJournals.map(rj => (
                  <div key={rj.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-black">{rj.name}</p>
                          {rj.is_active ? (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-600">Paused</span>
                          )}
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 capitalize">{rj.frequency}</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{rj.description}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                          <span className="flex items-center gap-1"><FiCalendar size={11} /> Next: {formatDate(rj.next_run_date)}</span>
                          {rj.last_run_date && <span>Last: {formatDate(rj.last_run_date)}</span>}
                          {rj.end_date && <span>Ends: {formatDate(rj.end_date)}</span>}
                          <span>{(rj.line_template || []).length} lines</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-3">
                        {rj.is_active && (
                          <button
                            onClick={() => handleExecuteRecurring(rj.id)}
                            className="text-sm border border-blue-300 text-blue-700 hover:bg-blue-50 px-3 py-1 rounded-lg flex items-center gap-1"
                            title="Execute now"
                          >
                            <FiPlay size={13} /> Post Now
                          </button>
                        )}
                        <button
                          onClick={() => handleToggleRecurring(rj.id, rj.is_active)}
                          className={`text-sm border px-3 py-1 rounded-lg flex items-center gap-1 ${rj.is_active ? 'border-yellow-300 text-yellow-700 hover:bg-yellow-50' : 'border-green-300 text-green-700 hover:bg-green-50'}`}
                        >
                          {rj.is_active ? <><FiLock size={13} /> Pause</> : <><FiUnlock size={13} /> Activate</>}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Create Recurring Journal Modal */}
              {showRecurringModal && (
                <ModalOverlay onClose={() => setShowRecurringModal(false)} title="New Recurring Journal">
                  <form onSubmit={handleCreateRecurring} className="space-y-4">
                    <FormField label="Name *">
                      <input type="text" required value={recurringForm.name} onChange={e => setRecurringForm(f => ({ ...f, name: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="e.g. Monthly Rent" />
                    </FormField>
                    <FormField label="Description *">
                      <input type="text" required value={recurringForm.description} onChange={e => setRecurringForm(f => ({ ...f, description: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="e.g. Monthly rent payment to landlord" />
                    </FormField>
                    <div className="grid grid-cols-2 gap-3">
                      <FormField label="Frequency *">
                        <select required value={recurringForm.frequency} onChange={e => setRecurringForm(f => ({ ...f, frequency: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                          <option value="weekly">Weekly</option>
                          <option value="biweekly">Biweekly</option>
                          <option value="monthly">Monthly</option>
                          <option value="quarterly">Quarterly</option>
                          <option value="yearly">Yearly</option>
                        </select>
                      </FormField>
                      <FormField label="Next Run Date *">
                        <input type="date" required value={recurringForm.next_run_date} onChange={e => setRecurringForm(f => ({ ...f, next_run_date: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      </FormField>
                    </div>
                    <FormField label="End Date (optional)">
                      <input type="date" value={recurringForm.end_date} onChange={e => setRecurringForm(f => ({ ...f, end_date: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                    </FormField>
                    <FormField label="Memo">
                      <input type="text" value={recurringForm.memo} onChange={e => setRecurringForm(f => ({ ...f, memo: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                    </FormField>

                    {/* Line Template */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">Journal Lines</label>
                        <button type="button" onClick={addRecurringLine} className="text-blue-600 text-xs flex items-center gap-1"><FiPlus size={12} /> Add Line</button>
                      </div>
                      <div className="space-y-2">
                        {recurringForm.line_template.map((line, idx) => (
                          <div key={idx} className="flex items-center gap-2 bg-gray-50 rounded-lg p-2">
                            <select value={line.account_id} onChange={e => updateRecurringLine(idx, 'account_id', e.target.value)} className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-xs" required>
                              <option value="">Select account…</option>
                              {accounts.filter(a => a.is_active).map(a => <option key={a.id} value={a.id}>{a.account_code} – {a.account_name}</option>)}
                            </select>
                            <input type="number" step="0.01" min="0" placeholder="Debit" value={line.debit} onChange={e => updateRecurringLine(idx, 'debit', e.target.value)} className="w-20 border border-gray-300 rounded px-2 py-1.5 text-xs text-right" />
                            <input type="number" step="0.01" min="0" placeholder="Credit" value={line.credit} onChange={e => updateRecurringLine(idx, 'credit', e.target.value)} className="w-20 border border-gray-300 rounded px-2 py-1.5 text-xs text-right" />
                            {recurringForm.line_template.length > 2 && (
                              <button type="button" onClick={() => removeRecurringLine(idx)} className="text-red-400 hover:text-red-600"><FiX size={14} /></button>
                            )}
                          </div>
                        ))}
                      </div>
                      {(() => {
                        const totalDr = recurringForm.line_template.reduce((s, l) => s + (Number(l.debit) || 0), 0);
                        const totalCr = recurringForm.line_template.reduce((s, l) => s + (Number(l.credit) || 0), 0);
                        const balanced = Math.abs(totalDr - totalCr) < 0.01;
                        return (
                          <div className={`mt-2 text-xs flex justify-between ${balanced ? 'text-green-600' : 'text-red-500'}`}>
                            <span>Dr: {formatCurrency(totalDr)}</span>
                            <span>Cr: {formatCurrency(totalCr)}</span>
                            <span>{balanced ? '✓ Balanced' : '✗ Unbalanced'}</span>
                          </div>
                        );
                      })()}
                    </div>

                    <div className="flex justify-end gap-2 pt-2">
                      <button type="button" onClick={() => setShowRecurringModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm">Cancel</button>
                      <button type="submit" className="bg-black text-white px-4 py-2 rounded-lg text-sm">Create</button>
                    </div>
                  </form>
                </ModalOverlay>
              )}
            </div>
          )}

          {/* ═══════ CURRENCIES ═══════ */}
          {activeTab === TABS.CURRENCY && (
            <div className="space-y-6">
              {/* Currency List */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-black">Currencies</h3>
                  <button onClick={() => setShowCurrencyModal(true)} className="flex items-center gap-1 bg-black text-white px-3 py-1.5 rounded-lg text-sm"><FiPlus size={14} /> Add Currency</button>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left px-4 py-3 font-medium text-gray-600">Code</th>
                        <th className="text-left px-4 py-3 font-medium text-gray-600">Name</th>
                        <th className="text-left px-4 py-3 font-medium text-gray-600">Symbol</th>
                        <th className="text-left px-4 py-3 font-medium text-gray-600">Decimals</th>
                        <th className="text-center px-4 py-3 font-medium text-gray-600">Base</th>
                        <th className="text-center px-4 py-3 font-medium text-gray-600">Active</th>
                        <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {currencies.map(cur => (
                        <tr key={cur.id} className={!cur.is_active ? 'opacity-50' : ''}>
                          <td className="px-4 py-3 font-mono font-semibold">{cur.code}</td>
                          <td className="px-4 py-3">{cur.name}</td>
                          <td className="px-4 py-3 text-lg">{cur.symbol}</td>
                          <td className="px-4 py-3">{cur.decimal_places}</td>
                          <td className="px-4 py-3 text-center">
                            {cur.is_base ? <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full font-medium">BASE</span> : (
                              <button onClick={() => handleSetBaseCurrency(cur.code)} className="text-xs text-blue-600 hover:underline">Set as Base</button>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button onClick={() => handleToggleCurrency(cur)} className={`text-xs px-2 py-0.5 rounded-full ${cur.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                              {cur.is_active ? 'Active' : 'Inactive'}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-right">
                            {cur.is_base && <span className="text-xs text-gray-400">Default</span>}
                          </td>
                        </tr>
                      ))}
                      {currencies.length === 0 && <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-400">No currencies configured</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Exchange Rates */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-black">Exchange Rates</h3>
                  <button onClick={() => setShowRateModal(true)} className="flex items-center gap-1 bg-black text-white px-3 py-1.5 rounded-lg text-sm"><FiPlus size={14} /> Add Rate</button>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left px-4 py-3 font-medium text-gray-600">From</th>
                        <th className="text-left px-4 py-3 font-medium text-gray-600">To</th>
                        <th className="text-right px-4 py-3 font-medium text-gray-600">Rate</th>
                        <th className="text-left px-4 py-3 font-medium text-gray-600">Effective Date</th>
                        <th className="text-left px-4 py-3 font-medium text-gray-600">Source</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {exchangeRates.map(r => (
                        <tr key={r.id}>
                          <td className="px-4 py-3 font-mono">{r.from_currency}</td>
                          <td className="px-4 py-3 font-mono">{r.to_currency}</td>
                          <td className="px-4 py-3 text-right font-mono">{Number(r.rate).toFixed(4)}</td>
                          <td className="px-4 py-3">{r.effective_date}</td>
                          <td className="px-4 py-3 text-gray-500">{r.source || 'manual'}</td>
                        </tr>
                      ))}
                      {exchangeRates.length === 0 && <tr><td colSpan="5" className="px-4 py-8 text-center text-gray-400">No exchange rates. Add rates for foreign currency transactions.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Add Currency Modal */}
              {showCurrencyModal && (
                <ModalOverlay title="Add Currency" onClose={() => setShowCurrencyModal(false)}>
                  <form onSubmit={handleCreateCurrency} className="space-y-4">
                    <FormField label="ISO Code (3 letters) *">
                      <input type="text" required maxLength={3} value={currencyForm.code} onChange={e => setCurrencyForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono" placeholder="e.g. USD" />
                    </FormField>
                    <FormField label="Currency Name *">
                      <input type="text" required value={currencyForm.name} onChange={e => setCurrencyForm(f => ({ ...f, name: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="e.g. US Dollar" />
                    </FormField>
                    <FormField label="Symbol *">
                      <input type="text" required value={currencyForm.symbol} onChange={e => setCurrencyForm(f => ({ ...f, symbol: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="e.g. $" />
                    </FormField>
                    <FormField label="Decimal Places">
                      <input type="number" min="0" max="4" value={currencyForm.decimal_places} onChange={e => setCurrencyForm(f => ({ ...f, decimal_places: parseInt(e.target.value) || 2 }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                    </FormField>
                    <div className="flex justify-end gap-2 pt-2">
                      <button type="button" onClick={() => setShowCurrencyModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm">Cancel</button>
                      <button type="submit" className="bg-black text-white px-4 py-2 rounded-lg text-sm">Add Currency</button>
                    </div>
                  </form>
                </ModalOverlay>
              )}

              {/* Add Exchange Rate Modal */}
              {showRateModal && (
                <ModalOverlay title="Add Exchange Rate" onClose={() => setShowRateModal(false)}>
                  <form onSubmit={handleSaveRate} className="space-y-4">
                    <FormField label="From Currency *">
                      <select required value={rateForm.from_currency} onChange={e => setRateForm(f => ({ ...f, from_currency: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                        <option value="">Select currency</option>
                        {getActiveCurrencies().map(c => <option key={c.id} value={c.code}>{c.code} – {c.name}</option>)}
                      </select>
                    </FormField>
                    <FormField label="To Currency *">
                      <select required value={rateForm.to_currency} onChange={e => setRateForm(f => ({ ...f, to_currency: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                        <option value="">Select currency</option>
                        {getActiveCurrencies().map(c => <option key={c.id} value={c.code}>{c.code} – {c.name}</option>)}
                      </select>
                    </FormField>
                    <FormField label="Exchange Rate *">
                      <input type="number" step="0.00000001" min="0.00000001" required value={rateForm.rate} onChange={e => setRateForm(f => ({ ...f, rate: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono" placeholder="e.g. 83.50" />
                      {rateForm.from_currency && rateForm.to_currency && rateForm.rate && (
                        <p className="text-xs text-gray-500 mt-1">1 {rateForm.from_currency} = {rateForm.rate} {rateForm.to_currency}</p>
                      )}
                    </FormField>
                    <FormField label="Effective Date *">
                      <input type="date" required value={rateForm.effective_date} onChange={e => setRateForm(f => ({ ...f, effective_date: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                    </FormField>
                    <FormField label="Source / Notes">
                      <input type="text" value={rateForm.notes} onChange={e => setRateForm(f => ({ ...f, notes: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="e.g. RBI rate, manual" />
                    </FormField>
                    <div className="flex justify-end gap-2 pt-2">
                      <button type="button" onClick={() => setShowRateModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm">Cancel</button>
                      <button type="submit" className="bg-black text-white px-4 py-2 rounded-lg text-sm">Save Rate</button>
                    </div>
                  </form>
                </ModalOverlay>
              )}
            </div>
          )}

          {/* ═══════ VENDORS ═══════ */}
          {activeTab === TABS.VENDORS && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-black">Vendors / Payees</h3>
                <button onClick={() => { setEditingVendor(null); setVendorForm({ name: '', display_name: '', email: '', phone: '', address: '', city: '', state: '', gstin: '', pan: '', default_expense_account_id: '', default_payment_account_id: '', payment_terms: '30', notes: '' }); setShowVendorModal(true); }} className="bg-black text-white px-4 py-2 rounded-lg text-sm flex items-center gap-1.5"><FiPlus size={15} /> Add Vendor</button>
              </div>

              {/* Search & Filters */}
              <div className="flex gap-3 items-center">
                <div className="relative flex-1 max-w-sm">
                  <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                  <input type="text" placeholder="Search vendors…" value={vendorSearch} onChange={e => setVendorSearch(e.target.value)} className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-600">
                  <input type="checkbox" checked={showInactiveVendors} onChange={e => setShowInactiveVendors(e.target.checked)} className="rounded" />
                  Show inactive
                </label>
              </div>

              {/* Vendor Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {vendors.map(v => (
                  <div key={v.id} className={`bg-white rounded-xl border border-gray-200 p-4 ${!v.is_active ? 'opacity-50' : ''}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="font-semibold text-black">{v.display_name || v.name}</h4>
                        {v.display_name && v.display_name !== v.name && <p className="text-xs text-gray-400">{v.name}</p>}
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => handleEditVendor(v)} className="text-gray-400 hover:text-gray-600 p-1" title="Edit"><FiEdit3 size={14} /></button>
                        <button onClick={() => handleToggleVendor(v)} className={`text-xs px-2 py-0.5 rounded-full ${v.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {v.is_active ? 'Active' : 'Inactive'}
                        </button>
                      </div>
                    </div>
                    <div className="space-y-1 text-xs text-gray-500">
                      {v.email && <p>✉ {v.email}</p>}
                      {v.phone && <p>☎ {v.phone}</p>}
                      {v.gstin && <p>GSTIN: {v.gstin}</p>}
                      {v.city && <p>📍 {v.city}{v.state ? `, ${v.state}` : ''}</p>}
                    </div>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                      <div>
                        <p className="text-xs text-gray-400">Total Paid</p>
                        <p className="font-semibold text-sm">{formatCurrency(v.total_paid || 0)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-400">Transactions</p>
                        <p className="font-semibold text-sm">{v.transaction_count || 0}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-400">Terms</p>
                        <p className="font-semibold text-sm">Net {v.payment_terms || 30}</p>
                      </div>
                    </div>
                    {v.default_expense_account && (
                      <p className="text-xs text-gray-400 mt-2">Default: {v.default_expense_account.account_code} – {v.default_expense_account.account_name}</p>
                    )}
                  </div>
                ))}
                {vendors.length === 0 && (
                  <div className="col-span-full text-center py-10 text-gray-400">No vendors found. Add your first vendor to start tracking payees.</div>
                )}
              </div>

              {/* Vendor Modal */}
              {showVendorModal && (
                <ModalOverlay title={editingVendor ? 'Edit Vendor' : 'Add Vendor'} onClose={() => { setShowVendorModal(false); setEditingVendor(null); }}>
                  <form onSubmit={handleCreateVendor} className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
                    <FormField label="Vendor Name *">
                      <input type="text" required value={vendorForm.name} onChange={e => setVendorForm(f => ({ ...f, name: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="e.g. ABC Supplies Pvt Ltd" />
                    </FormField>
                    <FormField label="Display Name">
                      <input type="text" value={vendorForm.display_name} onChange={e => setVendorForm(f => ({ ...f, display_name: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Short name (optional)" />
                    </FormField>
                    <div className="grid grid-cols-2 gap-3">
                      <FormField label="Email">
                        <input type="email" value={vendorForm.email} onChange={e => setVendorForm(f => ({ ...f, email: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      </FormField>
                      <FormField label="Phone">
                        <input type="text" value={vendorForm.phone} onChange={e => setVendorForm(f => ({ ...f, phone: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      </FormField>
                    </div>
                    <FormField label="Address">
                      <textarea value={vendorForm.address} onChange={e => setVendorForm(f => ({ ...f, address: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" rows={2} />
                    </FormField>
                    <div className="grid grid-cols-2 gap-3">
                      <FormField label="City">
                        <input type="text" value={vendorForm.city} onChange={e => setVendorForm(f => ({ ...f, city: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      </FormField>
                      <FormField label="State">
                        <input type="text" value={vendorForm.state} onChange={e => setVendorForm(f => ({ ...f, state: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      </FormField>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <FormField label="GSTIN">
                        <input type="text" value={vendorForm.gstin} onChange={e => setVendorForm(f => ({ ...f, gstin: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="e.g. 27AABCU9603R1ZM" />
                      </FormField>
                      <FormField label="PAN">
                        <input type="text" value={vendorForm.pan} onChange={e => setVendorForm(f => ({ ...f, pan: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="e.g. AABCU9603R" />
                      </FormField>
                    </div>
                    <FormField label="Default Expense Account">
                      <select value={vendorForm.default_expense_account_id} onChange={e => setVendorForm(f => ({ ...f, default_expense_account_id: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                        <option value="">None</option>
                        {expenseAccounts.map(a => <option key={a.id} value={a.id}>{a.account_code} – {a.account_name}</option>)}
                      </select>
                    </FormField>
                    <FormField label="Default Payment Account">
                      <select value={vendorForm.default_payment_account_id} onChange={e => setVendorForm(f => ({ ...f, default_payment_account_id: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                        <option value="">None</option>
                        {paymentAccounts.map(a => <option key={a.id} value={a.id}>{a.account_code} – {a.account_name}</option>)}
                      </select>
                    </FormField>
                    <FormField label="Payment Terms (days)">
                      <input type="number" min="0" value={vendorForm.payment_terms} onChange={e => setVendorForm(f => ({ ...f, payment_terms: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                    </FormField>
                    <FormField label="Notes">
                      <textarea value={vendorForm.notes} onChange={e => setVendorForm(f => ({ ...f, notes: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" rows={2} />
                    </FormField>
                    <div className="flex justify-end gap-2 pt-2">
                      <button type="button" onClick={() => { setShowVendorModal(false); setEditingVendor(null); }} className="px-4 py-2 border border-gray-300 rounded-lg text-sm">Cancel</button>
                      <button type="submit" className="bg-black text-white px-4 py-2 rounded-lg text-sm">{editingVendor ? 'Update' : 'Create'}</button>
                    </div>
                  </form>
                </ModalOverlay>
              )}
            </div>
          )}

          {/* ═══════ RECEIVABLES ═══════ */}
          {activeTab === TABS.RECEIVABLES && (
            <div className="space-y-6">
              {/* Summary Cards */}
              {arSummary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                    <div className="text-sm text-gray-500">Outstanding</div>
                    <div className="text-xl font-bold text-gray-900">{formatCurrency(arSummary.total_outstanding)}</div>
                    <div className="text-xs text-gray-400">{arSummary.open_invoices} open invoices</div>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                    <div className="text-sm text-gray-500">Overdue</div>
                    <div className="text-xl font-bold text-red-600">{formatCurrency(arSummary.total_overdue)}</div>
                    <div className="text-xs text-gray-400">{arSummary.overdue_invoices} overdue</div>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                    <div className="text-sm text-gray-500">Customers</div>
                    <div className="text-xl font-bold text-gray-900">{arSummary.active_customers}</div>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                    <div className="text-sm text-gray-500">Recent Payments</div>
                    <div className="text-xl font-bold text-green-600">{formatCurrency(arSummary.recent_payments_total)}</div>
                  </div>
                </div>
              )}

              {/* Sub-nav: Invoices / Customers */}
              <div className="flex items-center gap-4">
                <div className="flex bg-gray-100 rounded-lg p-0.5">
                  <button onClick={() => setArView('invoices')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${arView === 'invoices' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`}>Invoices</button>
                  <button onClick={() => setArView('customers')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${arView === 'customers' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`}>Customers</button>
                </div>
                <div className="flex-1" />
                {arView === 'invoices' && (
                  <>
                    <select value={arInvoiceFilter} onChange={e => setArInvoiceFilter(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
                      <option value="">All Statuses</option>
                      <option value="draft">Draft</option>
                      <option value="sent">Sent</option>
                      <option value="partially_paid">Partially Paid</option>
                      <option value="paid">Paid</option>
                      <option value="overdue">Overdue</option>
                      <option value="void">Void</option>
                    </select>
                    <button onClick={() => { setInvoiceForm({ customer_id: '', invoice_date: new Date().toISOString().split('T')[0], due_date: '', revenue_account_id: '', receivable_account_id: '', tax_account_id: '', notes: '' }); setInvoiceLines([{ description: '', quantity: 1, unit_price: '', tax_rate: '0', account_id: '' }]); setShowInvoiceModal(true); }} className="bg-black text-white px-4 py-2 rounded-lg text-sm flex items-center gap-1.5"><FiPlus size={15} /> New Invoice</button>
                  </>
                )}
                {arView === 'customers' && (
                  <>
                    <div className="relative">
                      <FiSearch className="absolute left-3 top-2.5 text-gray-400" size={15} />
                      <input type="text" value={arCustomerSearch} onChange={e => setArCustomerSearch(e.target.value)} placeholder="Search customers…" className="border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm w-48" />
                    </div>
                    <button onClick={() => { setEditingARCustomer(null); setArCustomerForm({ name: '', display_name: '', email: '', phone: '', address: '', city: '', state: '', gstin: '', pan: '', default_revenue_account_id: '', default_receivable_account_id: '', payment_terms: '30', credit_limit: '', notes: '' }); setShowARCustomerModal(true); }} className="bg-black text-white px-4 py-2 rounded-lg text-sm flex items-center gap-1.5"><FiPlus size={15} /> Add Customer</button>
                  </>
                )}
              </div>

              {/* Invoices View */}
              {arView === 'invoices' && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium">Invoice #</th>
                        <th className="px-4 py-3 text-left font-medium">Customer</th>
                        <th className="px-4 py-3 text-left font-medium">Date</th>
                        <th className="px-4 py-3 text-left font-medium">Due</th>
                        <th className="px-4 py-3 text-right font-medium">Total</th>
                        <th className="px-4 py-3 text-right font-medium">Balance</th>
                        <th className="px-4 py-3 text-center font-medium">Status</th>
                        <th className="px-4 py-3 text-center font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {arInvoices.map(inv => (
                        <tr key={inv.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-mono text-xs">{inv.invoice_number}</td>
                          <td className="px-4 py-3">{inv.customer?.display_name || inv.customer?.name || '-'}</td>
                          <td className="px-4 py-3 text-gray-500">{formatDate(inv.invoice_date)}</td>
                          <td className="px-4 py-3 text-gray-500">{formatDate(inv.due_date)}</td>
                          <td className="px-4 py-3 text-right font-medium">{formatCurrency(inv.total_amount)}</td>
                          <td className="px-4 py-3 text-right font-medium">{formatCurrency(inv.balance_due)}</td>
                          <td className="px-4 py-3 text-center"><StatusBadge status={inv.status} /></td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              {inv.status === 'draft' && (
                                <button onClick={() => handleSendInvoice(inv.id)} title="Send & Post" className="text-blue-600 hover:bg-blue-50 p-1 rounded"><FiPlay size={14} /></button>
                              )}
                              {['sent', 'partially_paid', 'overdue'].includes(inv.status) && (
                                <button onClick={() => openPaymentModal(inv)} title="Record Payment" className="text-green-600 hover:bg-green-50 p-1 rounded"><FiDollarSign size={14} /></button>
                              )}
                              {['draft', 'sent'].includes(inv.status) && parseFloat(inv.amount_paid) === 0 && (
                                <button onClick={() => handleVoidInvoice(inv.id)} title="Void" className="text-red-500 hover:bg-red-50 p-1 rounded"><FiSlash size={14} /></button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                      {arInvoices.length === 0 && (
                        <tr><td colSpan={8} className="text-center py-8 text-gray-400">No invoices found</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Customers View */}
              {arView === 'customers' && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium">Customer</th>
                        <th className="px-4 py-3 text-left font-medium">Email</th>
                        <th className="px-4 py-3 text-left font-medium">GSTIN</th>
                        <th className="px-4 py-3 text-right font-medium">Total Invoiced</th>
                        <th className="px-4 py-3 text-right font-medium">Outstanding</th>
                        <th className="px-4 py-3 text-center font-medium">Terms</th>
                        <th className="px-4 py-3 text-center font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {arCustomers.map(c => (
                        <tr key={c.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium">{c.display_name || c.name}</td>
                          <td className="px-4 py-3 text-gray-500">{c.email || '-'}</td>
                          <td className="px-4 py-3 text-gray-500 font-mono text-xs">{c.gstin || '-'}</td>
                          <td className="px-4 py-3 text-right">{formatCurrency(c.total_invoiced)}</td>
                          <td className="px-4 py-3 text-right font-medium">{parseFloat(c.outstanding_balance) > 0 ? <span className="text-red-600">{formatCurrency(c.outstanding_balance)}</span> : formatCurrency(0)}</td>
                          <td className="px-4 py-3 text-center text-gray-500">{c.payment_terms}d</td>
                          <td className="px-4 py-3 text-center">
                            <button onClick={() => handleEditARCustomer(c)} className="text-gray-500 hover:text-black p-1 rounded"><FiEdit3 size={14} /></button>
                          </td>
                        </tr>
                      ))}
                      {arCustomers.length === 0 && (
                        <tr><td colSpan={7} className="text-center py-8 text-gray-400">No customers yet</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* AR Customer Modal */}
              {showARCustomerModal && (
                <ModalOverlay title={editingARCustomer ? 'Edit Customer' : 'Add Customer'} onClose={() => { setShowARCustomerModal(false); setEditingARCustomer(null); }}>
                  <form onSubmit={handleSaveARCustomer} className="space-y-3">
                    <FormField label="Customer Name *">
                      <input type="text" required value={arCustomerForm.name} onChange={e => setArCustomerForm(f => ({ ...f, name: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="e.g. Hotel Grand" />
                    </FormField>
                    <FormField label="Display Name">
                      <input type="text" value={arCustomerForm.display_name} onChange={e => setArCustomerForm(f => ({ ...f, display_name: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                    </FormField>
                    <div className="grid grid-cols-2 gap-3">
                      <FormField label="Email">
                        <input type="email" value={arCustomerForm.email} onChange={e => setArCustomerForm(f => ({ ...f, email: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      </FormField>
                      <FormField label="Phone">
                        <input type="text" value={arCustomerForm.phone} onChange={e => setArCustomerForm(f => ({ ...f, phone: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      </FormField>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <FormField label="GSTIN">
                        <input type="text" value={arCustomerForm.gstin} onChange={e => setArCustomerForm(f => ({ ...f, gstin: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      </FormField>
                      <FormField label="PAN">
                        <input type="text" value={arCustomerForm.pan} onChange={e => setArCustomerForm(f => ({ ...f, pan: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      </FormField>
                    </div>
                    <FormField label="Address">
                      <input type="text" value={arCustomerForm.address} onChange={e => setArCustomerForm(f => ({ ...f, address: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                    </FormField>
                    <div className="grid grid-cols-2 gap-3">
                      <FormField label="City">
                        <input type="text" value={arCustomerForm.city} onChange={e => setArCustomerForm(f => ({ ...f, city: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      </FormField>
                      <FormField label="State">
                        <input type="text" value={arCustomerForm.state} onChange={e => setArCustomerForm(f => ({ ...f, state: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      </FormField>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <FormField label="Default Revenue Account">
                        <select value={arCustomerForm.default_revenue_account_id} onChange={e => setArCustomerForm(f => ({ ...f, default_revenue_account_id: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                          <option value="">— select —</option>
                          {revenueAccounts.map(a => <option key={a.id} value={a.id}>{a.code} – {a.name}</option>)}
                        </select>
                      </FormField>
                      <FormField label="Default Receivable Account">
                        <select value={arCustomerForm.default_receivable_account_id} onChange={e => setArCustomerForm(f => ({ ...f, default_receivable_account_id: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                          <option value="">— select —</option>
                          {assetAccounts.map(a => <option key={a.id} value={a.id}>{a.code} – {a.name}</option>)}
                        </select>
                      </FormField>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <FormField label="Payment Terms (days)">
                        <input type="number" min="0" value={arCustomerForm.payment_terms} onChange={e => setArCustomerForm(f => ({ ...f, payment_terms: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      </FormField>
                      <FormField label="Credit Limit">
                        <input type="number" min="0" step="0.01" value={arCustomerForm.credit_limit} onChange={e => setArCustomerForm(f => ({ ...f, credit_limit: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="0 = no limit" />
                      </FormField>
                    </div>
                    <FormField label="Notes">
                      <textarea value={arCustomerForm.notes} onChange={e => setArCustomerForm(f => ({ ...f, notes: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" rows={2} />
                    </FormField>
                    <div className="flex justify-end gap-2 pt-2">
                      <button type="button" onClick={() => { setShowARCustomerModal(false); setEditingARCustomer(null); }} className="px-4 py-2 border border-gray-300 rounded-lg text-sm">Cancel</button>
                      <button type="submit" className="bg-black text-white px-4 py-2 rounded-lg text-sm">{editingARCustomer ? 'Update' : 'Create'}</button>
                    </div>
                  </form>
                </ModalOverlay>
              )}

              {/* New Invoice Modal */}
              {showInvoiceModal && (
                <ModalOverlay title="Create Invoice" onClose={() => setShowInvoiceModal(false)}>
                  <form onSubmit={handleCreateInvoice} className="space-y-4">
                    <FormField label="Customer *">
                      <select required value={invoiceForm.customer_id} onChange={e => setInvoiceForm(f => ({ ...f, customer_id: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                        <option value="">— select customer —</option>
                        {arCustomers.map(c => <option key={c.id} value={c.id}>{c.display_name || c.name}</option>)}
                      </select>
                    </FormField>
                    <div className="grid grid-cols-2 gap-3">
                      <FormField label="Invoice Date">
                        <input type="date" value={invoiceForm.invoice_date} onChange={e => setInvoiceForm(f => ({ ...f, invoice_date: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      </FormField>
                      <FormField label="Due Date">
                        <input type="date" value={invoiceForm.due_date} onChange={e => setInvoiceForm(f => ({ ...f, due_date: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      </FormField>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <FormField label="Revenue Account">
                        <select value={invoiceForm.revenue_account_id} onChange={e => setInvoiceForm(f => ({ ...f, revenue_account_id: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                          <option value="">— default —</option>
                          {revenueAccounts.map(a => <option key={a.id} value={a.id}>{a.code} – {a.name}</option>)}
                        </select>
                      </FormField>
                      <FormField label="Receivable Account">
                        <select value={invoiceForm.receivable_account_id} onChange={e => setInvoiceForm(f => ({ ...f, receivable_account_id: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                          <option value="">— default —</option>
                          {assetAccounts.map(a => <option key={a.id} value={a.id}>{a.code} – {a.name}</option>)}
                        </select>
                      </FormField>
                      <FormField label="Tax Account">
                        <select value={invoiceForm.tax_account_id} onChange={e => setInvoiceForm(f => ({ ...f, tax_account_id: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                          <option value="">— none —</option>
                          {liabilityAccounts.map(a => <option key={a.id} value={a.id}>{a.code} – {a.name}</option>)}
                        </select>
                      </FormField>
                    </div>
                    {/* Line Items */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-gray-700">Line Items</label>
                        <button type="button" onClick={addInvoiceLine} className="text-xs text-blue-600 flex items-center gap-1"><FiPlus size={12} /> Add Line</button>
                      </div>
                      <div className="space-y-2">
                        {invoiceLines.map((line, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <input type="text" value={line.description} onChange={e => updateInvoiceLine(idx, 'description', e.target.value)} placeholder="Description" className="flex-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm" />
                            <input type="number" min="1" value={line.quantity} onChange={e => updateInvoiceLine(idx, 'quantity', e.target.value)} className="w-16 border border-gray-300 rounded-lg px-2 py-1.5 text-sm text-right" placeholder="Qty" />
                            <input type="number" step="0.01" value={line.unit_price} onChange={e => updateInvoiceLine(idx, 'unit_price', e.target.value)} placeholder="Price" className="w-24 border border-gray-300 rounded-lg px-2 py-1.5 text-sm text-right" />
                            <input type="number" step="0.01" min="0" max="100" value={line.tax_rate} onChange={e => updateInvoiceLine(idx, 'tax_rate', e.target.value)} placeholder="Tax %" className="w-20 border border-gray-300 rounded-lg px-2 py-1.5 text-sm text-right" />
                            {invoiceLines.length > 1 && (
                              <button type="button" onClick={() => removeInvoiceLine(idx)} className="text-red-400 hover:text-red-600 p-1"><FiX size={14} /></button>
                            )}
                          </div>
                        ))}
                      </div>
                      <div className="flex justify-end mt-3 text-sm space-x-6">
                        <span className="text-gray-500">Subtotal: <strong>{formatCurrency(invoiceSubtotal)}</strong></span>
                        <span className="text-gray-500">Tax: <strong>{formatCurrency(invoiceTax)}</strong></span>
                        <span className="text-gray-900 font-bold">Total: {formatCurrency(invoiceTotal)}</span>
                      </div>
                    </div>
                    <FormField label="Notes">
                      <textarea value={invoiceForm.notes} onChange={e => setInvoiceForm(f => ({ ...f, notes: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" rows={2} />
                    </FormField>
                    <div className="flex justify-end gap-2 pt-2">
                      <button type="button" onClick={() => setShowInvoiceModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm">Cancel</button>
                      <button type="submit" className="bg-black text-white px-4 py-2 rounded-lg text-sm">Create Invoice</button>
                    </div>
                  </form>
                </ModalOverlay>
              )}

              {/* Record Payment Modal */}
              {showPaymentModal && paymentInvoice && (
                <ModalOverlay title={`Record Payment — ${paymentInvoice.invoice_number}`} onClose={() => { setShowPaymentModal(false); setPaymentInvoice(null); }}>
                  <form onSubmit={handleRecordPayment} className="space-y-3">
                    <div className="bg-gray-50 rounded-lg p-3 text-sm">
                      <div className="flex justify-between"><span className="text-gray-500">Invoice Total</span><span className="font-medium">{formatCurrency(paymentInvoice.total_amount)}</span></div>
                      <div className="flex justify-between"><span className="text-gray-500">Already Paid</span><span>{formatCurrency(paymentInvoice.amount_paid)}</span></div>
                      <div className="flex justify-between font-bold"><span>Balance Due</span><span className="text-red-600">{formatCurrency(paymentInvoice.balance_due)}</span></div>
                    </div>
                    <FormField label="Payment Amount *">
                      <input type="number" required step="0.01" min="0.01" max={paymentInvoice.balance_due} value={paymentForm.amount} onChange={e => setPaymentForm(f => ({ ...f, amount: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                    </FormField>
                    <FormField label="Payment Date">
                      <input type="date" value={paymentForm.payment_date} onChange={e => setPaymentForm(f => ({ ...f, payment_date: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                    </FormField>
                    <div className="grid grid-cols-2 gap-3">
                      <FormField label="Method">
                        <select value={paymentForm.payment_method} onChange={e => setPaymentForm(f => ({ ...f, payment_method: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                          <option value="bank_transfer">Bank Transfer</option>
                          <option value="cash">Cash</option>
                          <option value="upi">UPI</option>
                          <option value="cheque">Cheque</option>
                          <option value="card">Card</option>
                          <option value="other">Other</option>
                        </select>
                      </FormField>
                      <FormField label="Reference #">
                        <input type="text" value={paymentForm.reference_number} onChange={e => setPaymentForm(f => ({ ...f, reference_number: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="e.g. UTR/Cheque No." />
                      </FormField>
                    </div>
                    <FormField label="Deposit Account *">
                      <select required value={paymentForm.deposit_account_id} onChange={e => setPaymentForm(f => ({ ...f, deposit_account_id: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                        <option value="">— select account —</option>
                        {assetAccounts.map(a => <option key={a.id} value={a.id}>{a.code} – {a.name}</option>)}
                      </select>
                    </FormField>
                    <FormField label="Notes">
                      <textarea value={paymentForm.notes} onChange={e => setPaymentForm(f => ({ ...f, notes: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" rows={2} />
                    </FormField>
                    <div className="flex justify-end gap-2 pt-2">
                      <button type="button" onClick={() => { setShowPaymentModal(false); setPaymentInvoice(null); }} className="px-4 py-2 border border-gray-300 rounded-lg text-sm">Cancel</button>
                      <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm">Record Payment</button>
                    </div>
                  </form>
                </ModalOverlay>
              )}
            </div>
          )}

          {/* ═══════ PAYABLES ═══════ */}
          {activeTab === TABS.PAYABLES && (
            <div className="space-y-6">
              {/* Summary Cards */}
              {apSummary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                    <div className="text-sm text-gray-500">Outstanding</div>
                    <div className="text-xl font-bold text-gray-900">{formatCurrency(apSummary.total_outstanding)}</div>
                    <div className="text-xs text-gray-400">{apSummary.open_bills} open bills</div>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                    <div className="text-sm text-gray-500">Overdue</div>
                    <div className="text-xl font-bold text-red-600">{formatCurrency(apSummary.total_overdue)}</div>
                    <div className="text-xs text-gray-400">{apSummary.overdue_bills} overdue</div>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                    <div className="text-sm text-gray-500">Open POs</div>
                    <div className="text-xl font-bold text-blue-600">{apSummary.open_purchase_orders}</div>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                    <div className="text-sm text-gray-500">Recent Payments</div>
                    <div className="text-xl font-bold text-green-600">{formatCurrency(apSummary.recent_payments_total)}</div>
                  </div>
                </div>
              )}

              {/* Sub-nav */}
              <div className="flex items-center gap-4">
                <div className="flex bg-gray-100 rounded-lg p-0.5">
                  <button onClick={() => setApView('bills')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${apView === 'bills' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`}>Bills</button>
                  <button onClick={() => setApView('po')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${apView === 'po' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`}>Purchase Orders</button>
                </div>
                <div className="flex-1" />
                {apView === 'bills' && (
                  <>
                    <select value={apBillFilter} onChange={e => setApBillFilter(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
                      <option value="">All Statuses</option>
                      <option value="draft">Draft</option>
                      <option value="pending">Pending</option>
                      <option value="partially_paid">Partially Paid</option>
                      <option value="paid">Paid</option>
                      <option value="overdue">Overdue</option>
                      <option value="void">Void</option>
                    </select>
                    <button onClick={() => { setBillForm({ vendor_id: '', bill_date: new Date().toISOString().split('T')[0], due_date: '', expense_account_id: '', payable_account_id: '', tax_account_id: '', vendor_invoice_number: '', notes: '' }); setBillLines([{ description: '', quantity: 1, unit_price: '', tax_rate: '0', account_id: '' }]); setShowBillModal(true); }} className="bg-black text-white px-4 py-2 rounded-lg text-sm flex items-center gap-1.5"><FiPlus size={15} /> New Bill</button>
                  </>
                )}
                {apView === 'po' && (
                  <button onClick={() => { setPoForm({ vendor_id: '', order_date: new Date().toISOString().split('T')[0], expected_date: '', notes: '' }); setPoLines([{ description: '', quantity: 1, unit_price: '', tax_rate: '0', account_id: '' }]); setShowPOModal(true); }} className="bg-black text-white px-4 py-2 rounded-lg text-sm flex items-center gap-1.5"><FiPlus size={15} /> New PO</button>
                )}
              </div>

              {/* Bills View */}
              {apView === 'bills' && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium">Bill #</th>
                        <th className="px-4 py-3 text-left font-medium">Vendor</th>
                        <th className="px-4 py-3 text-left font-medium">Date</th>
                        <th className="px-4 py-3 text-left font-medium">Due</th>
                        <th className="px-4 py-3 text-right font-medium">Total</th>
                        <th className="px-4 py-3 text-right font-medium">Balance</th>
                        <th className="px-4 py-3 text-center font-medium">Status</th>
                        <th className="px-4 py-3 text-center font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {apBills.map(bill => (
                        <tr key={bill.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-mono text-xs">{bill.bill_number}</td>
                          <td className="px-4 py-3">{bill.vendor?.display_name || bill.vendor?.name || '-'}</td>
                          <td className="px-4 py-3 text-gray-500">{formatDate(bill.bill_date)}</td>
                          <td className="px-4 py-3 text-gray-500">{formatDate(bill.due_date)}</td>
                          <td className="px-4 py-3 text-right font-medium">{formatCurrency(bill.total_amount)}</td>
                          <td className="px-4 py-3 text-right font-medium">{formatCurrency(bill.balance_due)}</td>
                          <td className="px-4 py-3 text-center"><StatusBadge status={bill.status} /></td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              {bill.status === 'draft' && (
                                <button onClick={() => handleApproveBill(bill.id)} title="Approve & Post" className="text-blue-600 hover:bg-blue-50 p-1 rounded"><FiCheckCircle size={14} /></button>
                              )}
                              {['pending', 'partially_paid', 'overdue'].includes(bill.status) && (
                                <button onClick={() => openBillPayModal(bill)} title="Record Payment" className="text-green-600 hover:bg-green-50 p-1 rounded"><FiDollarSign size={14} /></button>
                              )}
                              {['draft', 'pending'].includes(bill.status) && parseFloat(bill.amount_paid) === 0 && (
                                <button onClick={() => handleVoidBill(bill.id)} title="Void" className="text-red-500 hover:bg-red-50 p-1 rounded"><FiSlash size={14} /></button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                      {apBills.length === 0 && (
                        <tr><td colSpan={8} className="text-center py-8 text-gray-400">No bills found</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Purchase Orders View */}
              {apView === 'po' && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium">PO #</th>
                        <th className="px-4 py-3 text-left font-medium">Vendor</th>
                        <th className="px-4 py-3 text-left font-medium">Order Date</th>
                        <th className="px-4 py-3 text-left font-medium">Expected</th>
                        <th className="px-4 py-3 text-right font-medium">Total</th>
                        <th className="px-4 py-3 text-center font-medium">Status</th>
                        <th className="px-4 py-3 text-center font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {purchaseOrders.map(po => (
                        <tr key={po.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-mono text-xs">{po.po_number}</td>
                          <td className="px-4 py-3">{po.vendor?.display_name || po.vendor?.name || '-'}</td>
                          <td className="px-4 py-3 text-gray-500">{formatDate(po.order_date)}</td>
                          <td className="px-4 py-3 text-gray-500">{formatDate(po.expected_date)}</td>
                          <td className="px-4 py-3 text-right font-medium">{formatCurrency(po.total_amount)}</td>
                          <td className="px-4 py-3 text-center"><StatusBadge status={po.status} /></td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              {po.status === 'draft' && (
                                <button onClick={() => handleUpdatePOStatus(po.id, 'sent')} title="Mark Sent" className="text-blue-600 hover:bg-blue-50 p-1 rounded"><FiPlay size={14} /></button>
                              )}
                              {po.status === 'sent' && (
                                <button onClick={() => handleUpdatePOStatus(po.id, 'received')} title="Mark Received" className="text-green-600 hover:bg-green-50 p-1 rounded"><FiCheckCircle size={14} /></button>
                              )}
                              {['draft', 'sent'].includes(po.status) && (
                                <button onClick={() => handleUpdatePOStatus(po.id, 'cancelled')} title="Cancel" className="text-red-500 hover:bg-red-50 p-1 rounded"><FiSlash size={14} /></button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                      {purchaseOrders.length === 0 && (
                        <tr><td colSpan={7} className="text-center py-8 text-gray-400">No purchase orders</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* New Bill Modal */}
              {showBillModal && (
                <ModalOverlay title="Create Bill" onClose={() => setShowBillModal(false)}>
                  <form onSubmit={handleCreateBill} className="space-y-4">
                    <FormField label="Vendor *">
                      <select required value={billForm.vendor_id} onChange={e => setBillForm(f => ({ ...f, vendor_id: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                        <option value="">— select vendor —</option>
                        {vendors.map(v => <option key={v.id} value={v.id}>{v.display_name || v.name}</option>)}
                      </select>
                    </FormField>
                    <div className="grid grid-cols-2 gap-3">
                      <FormField label="Bill Date">
                        <input type="date" value={billForm.bill_date} onChange={e => setBillForm(f => ({ ...f, bill_date: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      </FormField>
                      <FormField label="Due Date">
                        <input type="date" value={billForm.due_date} onChange={e => setBillForm(f => ({ ...f, due_date: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      </FormField>
                    </div>
                    <FormField label="Vendor Invoice #">
                      <input type="text" value={billForm.vendor_invoice_number} onChange={e => setBillForm(f => ({ ...f, vendor_invoice_number: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Vendor's invoice reference" />
                    </FormField>
                    <div className="grid grid-cols-3 gap-3">
                      <FormField label="Expense Account">
                        <select value={billForm.expense_account_id} onChange={e => setBillForm(f => ({ ...f, expense_account_id: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                          <option value="">— default —</option>
                          {expenseAccountsAll.map(a => <option key={a.id} value={a.id}>{a.code} – {a.name}</option>)}
                        </select>
                      </FormField>
                      <FormField label="Payable Account">
                        <select value={billForm.payable_account_id} onChange={e => setBillForm(f => ({ ...f, payable_account_id: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                          <option value="">— select —</option>
                          {liabilityAccounts.map(a => <option key={a.id} value={a.id}>{a.code} – {a.name}</option>)}
                        </select>
                      </FormField>
                      <FormField label="Tax Account">
                        <select value={billForm.tax_account_id} onChange={e => setBillForm(f => ({ ...f, tax_account_id: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                          <option value="">— none —</option>
                          {assetAccounts.map(a => <option key={a.id} value={a.id}>{a.code} – {a.name}</option>)}
                        </select>
                      </FormField>
                    </div>
                    {/* Line Items */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-gray-700">Line Items</label>
                        <button type="button" onClick={addBillLine} className="text-xs text-blue-600 flex items-center gap-1"><FiPlus size={12} /> Add Line</button>
                      </div>
                      <div className="space-y-2">
                        {billLines.map((line, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <input type="text" value={line.description} onChange={e => updateBillLine(idx, 'description', e.target.value)} placeholder="Description" className="flex-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm" />
                            <input type="number" min="1" value={line.quantity} onChange={e => updateBillLine(idx, 'quantity', e.target.value)} className="w-16 border border-gray-300 rounded-lg px-2 py-1.5 text-sm text-right" placeholder="Qty" />
                            <input type="number" step="0.01" value={line.unit_price} onChange={e => updateBillLine(idx, 'unit_price', e.target.value)} placeholder="Price" className="w-24 border border-gray-300 rounded-lg px-2 py-1.5 text-sm text-right" />
                            <input type="number" step="0.01" min="0" max="100" value={line.tax_rate} onChange={e => updateBillLine(idx, 'tax_rate', e.target.value)} placeholder="Tax %" className="w-20 border border-gray-300 rounded-lg px-2 py-1.5 text-sm text-right" />
                            {billLines.length > 1 && (
                              <button type="button" onClick={() => removeBillLine(idx)} className="text-red-400 hover:text-red-600 p-1"><FiX size={14} /></button>
                            )}
                          </div>
                        ))}
                      </div>
                      <div className="flex justify-end mt-3 text-sm space-x-6">
                        <span className="text-gray-500">Subtotal: <strong>{formatCurrency(billSubtotal)}</strong></span>
                        <span className="text-gray-500">Tax: <strong>{formatCurrency(billTax)}</strong></span>
                        <span className="text-gray-900 font-bold">Total: {formatCurrency(billTotal)}</span>
                      </div>
                    </div>
                    <FormField label="Notes">
                      <textarea value={billForm.notes} onChange={e => setBillForm(f => ({ ...f, notes: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" rows={2} />
                    </FormField>
                    <div className="flex justify-end gap-2 pt-2">
                      <button type="button" onClick={() => setShowBillModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm">Cancel</button>
                      <button type="submit" className="bg-black text-white px-4 py-2 rounded-lg text-sm">Create Bill</button>
                    </div>
                  </form>
                </ModalOverlay>
              )}

              {/* Bill Payment Modal */}
              {showBillPayModal && billPayInvoice && (
                <ModalOverlay title={`Pay Bill — ${billPayInvoice.bill_number}`} onClose={() => { setShowBillPayModal(false); setBillPayInvoice(null); }}>
                  <form onSubmit={handleRecordBillPayment} className="space-y-3">
                    <div className="bg-gray-50 rounded-lg p-3 text-sm">
                      <div className="flex justify-between"><span className="text-gray-500">Bill Total</span><span className="font-medium">{formatCurrency(billPayInvoice.total_amount)}</span></div>
                      <div className="flex justify-between"><span className="text-gray-500">Already Paid</span><span>{formatCurrency(billPayInvoice.amount_paid)}</span></div>
                      <div className="flex justify-between font-bold"><span>Balance Due</span><span className="text-red-600">{formatCurrency(billPayInvoice.balance_due)}</span></div>
                    </div>
                    <FormField label="Payment Amount *">
                      <input type="number" required step="0.01" min="0.01" max={billPayInvoice.balance_due} value={billPayForm.amount} onChange={e => setBillPayForm(f => ({ ...f, amount: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                    </FormField>
                    <FormField label="Payment Date">
                      <input type="date" value={billPayForm.payment_date} onChange={e => setBillPayForm(f => ({ ...f, payment_date: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                    </FormField>
                    <div className="grid grid-cols-2 gap-3">
                      <FormField label="Method">
                        <select value={billPayForm.payment_method} onChange={e => setBillPayForm(f => ({ ...f, payment_method: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                          <option value="bank_transfer">Bank Transfer</option>
                          <option value="cash">Cash</option>
                          <option value="upi">UPI</option>
                          <option value="cheque">Cheque</option>
                          <option value="card">Card</option>
                          <option value="other">Other</option>
                        </select>
                      </FormField>
                      <FormField label="Reference #">
                        <input type="text" value={billPayForm.reference_number} onChange={e => setBillPayForm(f => ({ ...f, reference_number: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="e.g. UTR/Cheque No." />
                      </FormField>
                    </div>
                    <FormField label="Payment Account *">
                      <select required value={billPayForm.payment_account_id} onChange={e => setBillPayForm(f => ({ ...f, payment_account_id: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                        <option value="">— select account —</option>
                        {assetAccounts.map(a => <option key={a.id} value={a.id}>{a.code} – {a.name}</option>)}
                      </select>
                    </FormField>
                    <FormField label="Notes">
                      <textarea value={billPayForm.notes} onChange={e => setBillPayForm(f => ({ ...f, notes: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" rows={2} />
                    </FormField>
                    <div className="flex justify-end gap-2 pt-2">
                      <button type="button" onClick={() => { setShowBillPayModal(false); setBillPayInvoice(null); }} className="px-4 py-2 border border-gray-300 rounded-lg text-sm">Cancel</button>
                      <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm">Record Payment</button>
                    </div>
                  </form>
                </ModalOverlay>
              )}

              {/* New PO Modal */}
              {showPOModal && (
                <ModalOverlay title="Create Purchase Order" onClose={() => setShowPOModal(false)}>
                  <form onSubmit={handleCreatePO} className="space-y-4">
                    <FormField label="Vendor *">
                      <select required value={poForm.vendor_id} onChange={e => setPoForm(f => ({ ...f, vendor_id: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                        <option value="">— select vendor —</option>
                        {vendors.map(v => <option key={v.id} value={v.id}>{v.display_name || v.name}</option>)}
                      </select>
                    </FormField>
                    <div className="grid grid-cols-2 gap-3">
                      <FormField label="Order Date">
                        <input type="date" value={poForm.order_date} onChange={e => setPoForm(f => ({ ...f, order_date: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      </FormField>
                      <FormField label="Expected Delivery">
                        <input type="date" value={poForm.expected_date} onChange={e => setPoForm(f => ({ ...f, expected_date: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      </FormField>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-gray-700">Line Items</label>
                        <button type="button" onClick={addPOLine} className="text-xs text-blue-600 flex items-center gap-1"><FiPlus size={12} /> Add Line</button>
                      </div>
                      <div className="space-y-2">
                        {poLines.map((line, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <input type="text" value={line.description} onChange={e => updatePOLine(idx, 'description', e.target.value)} placeholder="Description" className="flex-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm" />
                            <input type="number" min="1" value={line.quantity} onChange={e => updatePOLine(idx, 'quantity', e.target.value)} className="w-16 border border-gray-300 rounded-lg px-2 py-1.5 text-sm text-right" placeholder="Qty" />
                            <input type="number" step="0.01" value={line.unit_price} onChange={e => updatePOLine(idx, 'unit_price', e.target.value)} placeholder="Price" className="w-24 border border-gray-300 rounded-lg px-2 py-1.5 text-sm text-right" />
                            <input type="number" step="0.01" min="0" max="100" value={line.tax_rate} onChange={e => updatePOLine(idx, 'tax_rate', e.target.value)} placeholder="Tax %" className="w-20 border border-gray-300 rounded-lg px-2 py-1.5 text-sm text-right" />
                            {poLines.length > 1 && (
                              <button type="button" onClick={() => removePOLine(idx)} className="text-red-400 hover:text-red-600 p-1"><FiX size={14} /></button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                    <FormField label="Notes">
                      <textarea value={poForm.notes} onChange={e => setPoForm(f => ({ ...f, notes: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" rows={2} />
                    </FormField>
                    <div className="flex justify-end gap-2 pt-2">
                      <button type="button" onClick={() => setShowPOModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm">Cancel</button>
                      <button type="submit" className="bg-black text-white px-4 py-2 rounded-lg text-sm">Create PO</button>
                    </div>
                  </form>
                </ModalOverlay>
              )}
            </div>
          )}
        </>
      )}

      {/* ═══════ BANK RECONCILIATION ═══════ */}
      {activeTab === TABS.RECONCILIATION && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-white p-4 rounded-xl border">
              <div className="text-xs text-gray-500 mb-1">Bank Accounts</div>
              <div className="text-xl font-bold">{bankReconSummary?.total_bank_accounts || 0}</div>
            </div>
            <div className="bg-white p-4 rounded-xl border">
              <div className="text-xs text-gray-500 mb-1">Total Balance</div>
              <div className="text-xl font-bold text-blue-700">{formatCurrency(bankReconSummary?.total_balance || 0)}</div>
            </div>
            <div className="bg-white p-4 rounded-xl border">
              <div className="text-xs text-gray-500 mb-1">Unmatched Txns</div>
              <div className="text-xl font-bold text-orange-600">{bankReconSummary?.unmatched_transactions || 0}</div>
            </div>
            <div className="bg-white p-4 rounded-xl border">
              <div className="text-xs text-gray-500 mb-1">In Progress</div>
              <div className="text-xl font-bold text-yellow-600">{bankReconSummary?.in_progress_reconciliations || 0}</div>
            </div>
            <div className="bg-white p-4 rounded-xl border">
              <div className="text-xs text-gray-500 mb-1">Completed</div>
              <div className="text-xl font-bold text-green-600">{bankReconSummary?.completed_reconciliations || 0}</div>
            </div>
          </div>

          {/* Sub Navigation */}
          {reconView !== 'detail' && (
            <div className="flex gap-2 mb-4 flex-wrap">
              {[{ k: 'accounts', l: 'Bank Accounts' }, { k: 'transactions', l: 'Transactions' }, { k: 'reconciliations', l: 'Reconciliations' }].map(v => (
                <button key={v.k} onClick={() => setReconView(v.k)} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${reconView === v.k ? 'bg-black text-white' : 'bg-white border text-gray-600 hover:bg-gray-50'}`}>{v.l}</button>
              ))}
              <div className="flex-1" />
              {reconView === 'accounts' && <button onClick={() => setShowBankAccountModal(true)} className="bg-black text-white px-3 py-1.5 rounded-lg text-sm flex items-center gap-1"><FiPlus size={14} /> New Account</button>}
              {reconView === 'transactions' && (
                <div className="flex gap-2">
                  <button onClick={() => setShowManualTxnModal(true)} className="bg-black text-white px-3 py-1.5 rounded-lg text-sm flex items-center gap-1"><FiPlus size={14} /> Manual Txn</button>
                  <button onClick={() => setShowImportModal(true)} className="bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm flex items-center gap-1"><FiDownload size={14} /> Import CSV</button>
                </div>
              )}
              {reconView === 'reconciliations' && <button onClick={() => setShowReconModal(true)} className="bg-black text-white px-3 py-1.5 rounded-lg text-sm flex items-center gap-1"><FiPlus size={14} /> New Reconciliation</button>}
            </div>
          )}

          {/* Bank Accounts View */}
          {reconView === 'accounts' && (
            <div className="bg-white rounded-xl border overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b"><tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Account Name</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Bank</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">A/C Number</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Type</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">CoA Link</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Balance</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Last Reconciled</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                  <th className="px-4 py-3" />
                </tr></thead>
                <tbody>
                  {bankAccounts.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-gray-400">No bank accounts yet</td></tr>}
                  {bankAccounts.map(a => (
                    <tr key={a.id} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{a.account_name}</td>
                      <td className="px-4 py-3 text-gray-500">{a.bank_name || '-'}</td>
                      <td className="px-4 py-3 text-gray-500">{a.account_number || '-'}</td>
                      <td className="px-4 py-3"><span className="capitalize">{a.account_type}</span></td>
                      <td className="px-4 py-3 text-gray-500">{a.chart_of_accounts ? `${a.chart_of_accounts.account_code} – ${a.chart_of_accounts.account_name}` : '-'}</td>
                      <td className="px-4 py-3 text-right font-medium">{formatCurrency(a.current_balance)}</td>
                      <td className="px-4 py-3 text-gray-500">{a.last_reconciled_date ? formatDate(a.last_reconciled_date) : 'Never'}</td>
                      <td className="px-4 py-3"><StatusBadge status={a.is_active ? 'active' : 'inactive'} /></td>
                      <td className="px-4 py-3">
                        <button onClick={() => handleToggleBankAccount(a)} className="text-xs text-gray-500 hover:text-gray-800">{a.is_active ? 'Deactivate' : 'Activate'}</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Transactions View */}
          {reconView === 'transactions' && (
            <div>
              <div className="mb-3 flex gap-2 items-center">
                <select value={selectedBankAccountFilter} onChange={e => setSelectedBankAccountFilter(e.target.value)} className="border rounded-lg px-3 py-1.5 text-sm">
                  <option value="">All Accounts</option>
                  {bankAccounts.filter(a => a.is_active).map(a => <option key={a.id} value={a.id}>{a.account_name}</option>)}
                </select>
                <button onClick={fetchBankTransactions} className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"><FiRefreshCw size={14} /> Refresh</button>
              </div>
              <div className="bg-white rounded-xl border overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b"><tr>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Date</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Description</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Reference</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Account</th>
                    <th className="text-right px-4 py-3 font-medium text-gray-600">Amount</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Matched To</th>
                    <th className="px-4 py-3" />
                  </tr></thead>
                  <tbody>
                    {bankTransactions.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-gray-400">No transactions</td></tr>}
                    {bankTransactions.map(t => (
                      <tr key={t.id} className="border-b last:border-0 hover:bg-gray-50">
                        <td className="px-4 py-3">{formatDate(t.transaction_date)}</td>
                        <td className="px-4 py-3">{t.description}</td>
                        <td className="px-4 py-3 text-gray-500">{t.reference || '-'}</td>
                        <td className="px-4 py-3 text-gray-500">{t.bank_accounts?.account_name || '-'}</td>
                        <td className={`px-4 py-3 text-right font-medium ${Number(t.amount) >= 0 ? 'text-green-700' : 'text-red-700'}`}>{formatCurrency(t.amount)}</td>
                        <td className="px-4 py-3"><StatusBadge status={t.status} /></td>
                        <td className="px-4 py-3 text-gray-500">{t.journal_entries ? `#${t.journal_entries.entry_number}` : '-'}</td>
                        <td className="px-4 py-3">
                          {t.status === 'unmatched' && <button onClick={() => handleExcludeTxn(t.id)} className="text-xs text-gray-400 hover:text-red-600"><FiSlash size={14} /></button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Reconciliations List */}
          {reconView === 'reconciliations' && (
            <div className="bg-white rounded-xl border overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b"><tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Date</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Bank Account</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Period</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Statement Bal.</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Book Bal.</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Difference</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-600">Matched</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                  <th className="px-4 py-3" />
                </tr></thead>
                <tbody>
                  {reconciliations.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-gray-400">No reconciliations yet</td></tr>}
                  {reconciliations.map(r => (
                    <tr key={r.id} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="px-4 py-3">{formatDate(r.reconciliation_date)}</td>
                      <td className="px-4 py-3">{r.bank_accounts?.account_name || '-'}</td>
                      <td className="px-4 py-3 text-gray-500">{formatDate(r.period_start)} – {formatDate(r.period_end)}</td>
                      <td className="px-4 py-3 text-right">{formatCurrency(r.statement_balance)}</td>
                      <td className="px-4 py-3 text-right">{formatCurrency(r.book_balance)}</td>
                      <td className={`px-4 py-3 text-right font-medium ${Math.abs(Number(r.difference)) < 0.01 ? 'text-green-700' : 'text-red-700'}`}>{formatCurrency(r.difference)}</td>
                      <td className="px-4 py-3 text-center">{r.matched_count}</td>
                      <td className="px-4 py-3"><StatusBadge status={r.status === 'in_progress' ? 'pending' : r.status} /></td>
                      <td className="px-4 py-3 flex gap-2">
                        <button onClick={() => handleOpenReconDetail(r.id)} className="text-blue-600 hover:text-blue-800 text-xs flex items-center gap-1"><FiEye size={14} /> View</button>
                        {r.status === 'in_progress' && (
                          <>
                            <button onClick={() => handleCompleteRecon(r.id)} className="text-green-600 hover:text-green-800 text-xs"><FiCheckCircle size={14} /></button>
                            <button onClick={() => handleVoidRecon(r.id)} className="text-red-500 hover:text-red-700 text-xs"><FiSlash size={14} /></button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Reconciliation Detail View */}
          {reconView === 'detail' && reconDetail && (
            <div>
              <div className="flex items-center gap-3 mb-4">
                <button onClick={() => { setReconView('reconciliations'); setReconDetail(null); }} className="text-sm text-blue-600 hover:text-blue-800">← Back to Reconciliations</button>
                <h3 className="text-lg font-bold">Reconciliation Detail</h3>
                <StatusBadge status={reconDetail.reconciliation.status === 'in_progress' ? 'pending' : reconDetail.reconciliation.status} />
              </div>

              {/* Header Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white p-4 rounded-xl border">
                  <div className="text-xs text-gray-500 mb-1">Bank Account</div>
                  <div className="font-medium">{reconDetail.reconciliation.bank_accounts?.account_name}</div>
                </div>
                <div className="bg-white p-4 rounded-xl border">
                  <div className="text-xs text-gray-500 mb-1">Statement Balance</div>
                  <div className="font-bold text-blue-700">{formatCurrency(reconDetail.reconciliation.statement_balance)}</div>
                </div>
                <div className="bg-white p-4 rounded-xl border">
                  <div className="text-xs text-gray-500 mb-1">Book Balance</div>
                  <div className="font-bold">{formatCurrency(reconDetail.reconciliation.book_balance)}</div>
                </div>
                <div className="bg-white p-4 rounded-xl border">
                  <div className="text-xs text-gray-500 mb-1">Difference</div>
                  <div className={`font-bold ${Math.abs(Number(reconDetail.reconciliation.difference)) < 0.01 ? 'text-green-700' : 'text-red-700'}`}>{formatCurrency(reconDetail.reconciliation.difference)}</div>
                </div>
              </div>

              {reconDetail.reconciliation.status === 'in_progress' && (
                <div className="flex gap-2 mb-4">
                  <button onClick={handleAutoMatch} className="bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm flex items-center gap-1"><FiPlay size={14} /> Auto-Match</button>
                  <button onClick={() => handleCompleteRecon(reconDetail.reconciliation.id)} className="bg-green-600 text-white px-3 py-1.5 rounded-lg text-sm flex items-center gap-1"><FiCheckCircle size={14} /> Complete</button>
                  <button onClick={() => handleVoidRecon(reconDetail.reconciliation.id)} className="bg-red-100 text-red-700 px-3 py-1.5 rounded-lg text-sm flex items-center gap-1"><FiSlash size={14} /> Void</button>
                </div>
              )}

              {/* Matches */}
              {reconDetail.matches.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-semibold mb-2 text-green-700">✓ Matched Transactions ({reconDetail.matches.length})</h4>
                  <div className="bg-white rounded-xl border overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-green-50 border-b"><tr>
                        <th className="text-left px-4 py-2 font-medium text-gray-600">Bank Txn Date</th>
                        <th className="text-left px-4 py-2 font-medium text-gray-600">Description</th>
                        <th className="text-right px-4 py-2 font-medium text-gray-600">Amount</th>
                        <th className="text-left px-4 py-2 font-medium text-gray-600">Journal #</th>
                        <th className="text-left px-4 py-2 font-medium text-gray-600">Match Type</th>
                        <th className="px-4 py-2" />
                      </tr></thead>
                      <tbody>
                        {reconDetail.matches.map(m => (
                          <tr key={m.id} className="border-b last:border-0">
                            <td className="px-4 py-2">{formatDate(m.bank_transactions?.transaction_date)}</td>
                            <td className="px-4 py-2">{m.bank_transactions?.description}</td>
                            <td className={`px-4 py-2 text-right font-medium ${Number(m.bank_transactions?.amount) >= 0 ? 'text-green-700' : 'text-red-700'}`}>{formatCurrency(m.bank_transactions?.amount)}</td>
                            <td className="px-4 py-2 text-gray-500">{m.journal_entries ? `#${m.journal_entries.entry_number}` : 'N/A'}</td>
                            <td className="px-4 py-2"><span className="capitalize text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700">{m.match_type}</span></td>
                            <td className="px-4 py-2">
                              {reconDetail.reconciliation.status === 'in_progress' && (
                                <button onClick={() => handleUnmatchTxn(m.id)} className="text-xs text-red-500 hover:text-red-700"><FiX size={14} /></button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Unmatched Bank Transactions */}
              {reconDetail.unmatched_bank_transactions.length > 0 && reconDetail.reconciliation.status === 'in_progress' && (
                <div className="mb-6">
                  <h4 className="font-semibold mb-2 text-orange-600">⚠ Unmatched Bank Transactions ({reconDetail.unmatched_bank_transactions.length})</h4>
                  <div className="bg-white rounded-xl border overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-orange-50 border-b"><tr>
                        <th className="text-left px-4 py-2 font-medium text-gray-600">Date</th>
                        <th className="text-left px-4 py-2 font-medium text-gray-600">Description</th>
                        <th className="text-left px-4 py-2 font-medium text-gray-600">Reference</th>
                        <th className="text-right px-4 py-2 font-medium text-gray-600">Amount</th>
                        <th className="text-left px-4 py-2 font-medium text-gray-600">Match to Journal</th>
                      </tr></thead>
                      <tbody>
                        {reconDetail.unmatched_bank_transactions.map(t => (
                          <tr key={t.id} className="border-b last:border-0">
                            <td className="px-4 py-2">{formatDate(t.transaction_date)}</td>
                            <td className="px-4 py-2">{t.description}</td>
                            <td className="px-4 py-2 text-gray-500">{t.reference || '-'}</td>
                            <td className={`px-4 py-2 text-right font-medium ${Number(t.amount) >= 0 ? 'text-green-700' : 'text-red-700'}`}>{formatCurrency(t.amount)}</td>
                            <td className="px-4 py-2">
                              <select
                                className="border rounded px-2 py-1 text-xs w-full"
                                defaultValue=""
                                onChange={(e) => {
                                  if (e.target.value) {
                                    const [jeId, jlId] = e.target.value.split('|');
                                    handleMatchTxn(t.id, jeId, jlId);
                                  }
                                }}
                              >
                                <option value="">— Select journal line —</option>
                                {(reconDetail.unmatched_journal_lines || []).map(jl => (
                                  <option key={jl.id} value={`${jl.journal_entries.id}|${jl.id}`}>
                                    #{jl.journal_entries.entry_number} | {jl.journal_entries.description} | D:{formatCurrency(jl.debit)} C:{formatCurrency(jl.credit)}
                                  </option>
                                ))}
                              </select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Unmatched Journal Lines */}
              {(reconDetail.unmatched_journal_lines || []).length > 0 && reconDetail.reconciliation.status === 'in_progress' && (
                <div>
                  <h4 className="font-semibold mb-2 text-gray-600">📖 Unmatched Book Entries ({reconDetail.unmatched_journal_lines.length})</h4>
                  <div className="bg-white rounded-xl border overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b"><tr>
                        <th className="text-left px-4 py-2 font-medium text-gray-600">Journal #</th>
                        <th className="text-left px-4 py-2 font-medium text-gray-600">Date</th>
                        <th className="text-left px-4 py-2 font-medium text-gray-600">Description</th>
                        <th className="text-right px-4 py-2 font-medium text-gray-600">Debit</th>
                        <th className="text-right px-4 py-2 font-medium text-gray-600">Credit</th>
                      </tr></thead>
                      <tbody>
                        {reconDetail.unmatched_journal_lines.map(jl => (
                          <tr key={jl.id} className="border-b last:border-0">
                            <td className="px-4 py-2 text-gray-500">#{jl.journal_entries?.entry_number}</td>
                            <td className="px-4 py-2">{formatDate(jl.journal_entries?.entry_date)}</td>
                            <td className="px-4 py-2">{jl.journal_entries?.description}</td>
                            <td className="px-4 py-2 text-right">{Number(jl.debit) > 0 ? formatCurrency(jl.debit) : '-'}</td>
                            <td className="px-4 py-2 text-right">{Number(jl.credit) > 0 ? formatCurrency(jl.credit) : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Modals ── */}
          {/* New Bank Account Modal */}
          {showBankAccountModal && (
            <ModalOverlay title="New Bank Account" onClose={() => setShowBankAccountModal(false)}>
              <form onSubmit={handleCreateBankAccount} className="space-y-3">
                <FormField label="Account Name *">
                  <input value={bankAccountForm.account_name} onChange={e => setBankAccountForm(p => ({ ...p, account_name: e.target.value }))} className="border rounded-lg px-3 py-2 w-full text-sm" required />
                </FormField>
                <div className="grid grid-cols-2 gap-3">
                  <FormField label="Bank Name">
                    <input value={bankAccountForm.bank_name} onChange={e => setBankAccountForm(p => ({ ...p, bank_name: e.target.value }))} className="border rounded-lg px-3 py-2 w-full text-sm" />
                  </FormField>
                  <FormField label="Account Number">
                    <input value={bankAccountForm.account_number} onChange={e => setBankAccountForm(p => ({ ...p, account_number: e.target.value }))} className="border rounded-lg px-3 py-2 w-full text-sm" placeholder="Last 4 digits" />
                  </FormField>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <FormField label="IFSC Code">
                    <input value={bankAccountForm.ifsc_code} onChange={e => setBankAccountForm(p => ({ ...p, ifsc_code: e.target.value }))} className="border rounded-lg px-3 py-2 w-full text-sm" />
                  </FormField>
                  <FormField label="Account Type">
                    <select value={bankAccountForm.account_type} onChange={e => setBankAccountForm(p => ({ ...p, account_type: e.target.value }))} className="border rounded-lg px-3 py-2 w-full text-sm">
                      <option value="checking">Checking</option>
                      <option value="savings">Savings</option>
                      <option value="credit_card">Credit Card</option>
                      <option value="cash">Cash</option>
                      <option value="other">Other</option>
                    </select>
                  </FormField>
                </div>
                <FormField label="Linked CoA Account">
                  <select value={bankAccountForm.coa_account_id} onChange={e => setBankAccountForm(p => ({ ...p, coa_account_id: e.target.value }))} className="border rounded-lg px-3 py-2 w-full text-sm">
                    <option value="">— None —</option>
                    {assetAccounts.map(a => <option key={a.id} value={a.id}>{a.account_code} – {a.account_name}</option>)}
                  </select>
                </FormField>
                <FormField label="Opening Balance">
                  <input type="number" step="0.01" value={bankAccountForm.opening_balance} onChange={e => setBankAccountForm(p => ({ ...p, opening_balance: e.target.value }))} className="border rounded-lg px-3 py-2 w-full text-sm" />
                </FormField>
                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={() => setShowBankAccountModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm">Cancel</button>
                  <button type="submit" className="bg-black text-white px-4 py-2 rounded-lg text-sm">Create Account</button>
                </div>
              </form>
            </ModalOverlay>
          )}

          {/* Manual Transaction Modal */}
          {showManualTxnModal && (
            <ModalOverlay title="Manual Bank Transaction" onClose={() => setShowManualTxnModal(false)}>
              <form onSubmit={handleCreateManualTxn} className="space-y-3">
                <FormField label="Bank Account *">
                  <select value={manualTxnForm.bank_account_id} onChange={e => setManualTxnForm(p => ({ ...p, bank_account_id: e.target.value }))} className="border rounded-lg px-3 py-2 w-full text-sm" required>
                    <option value="">Select…</option>
                    {bankAccounts.filter(a => a.is_active).map(a => <option key={a.id} value={a.id}>{a.account_name}</option>)}
                  </select>
                </FormField>
                <div className="grid grid-cols-2 gap-3">
                  <FormField label="Date *">
                    <input type="date" value={manualTxnForm.transaction_date} onChange={e => setManualTxnForm(p => ({ ...p, transaction_date: e.target.value }))} className="border rounded-lg px-3 py-2 w-full text-sm" required />
                  </FormField>
                  <FormField label="Amount *">
                    <input type="number" step="0.01" value={manualTxnForm.amount} onChange={e => setManualTxnForm(p => ({ ...p, amount: e.target.value }))} className="border rounded-lg px-3 py-2 w-full text-sm" placeholder="Positive=deposit, Negative=withdrawal" required />
                  </FormField>
                </div>
                <FormField label="Description *">
                  <input value={manualTxnForm.description} onChange={e => setManualTxnForm(p => ({ ...p, description: e.target.value }))} className="border rounded-lg px-3 py-2 w-full text-sm" required />
                </FormField>
                <div className="grid grid-cols-2 gap-3">
                  <FormField label="Reference">
                    <input value={manualTxnForm.reference} onChange={e => setManualTxnForm(p => ({ ...p, reference: e.target.value }))} className="border rounded-lg px-3 py-2 w-full text-sm" />
                  </FormField>
                  <FormField label="Payee">
                    <input value={manualTxnForm.payee} onChange={e => setManualTxnForm(p => ({ ...p, payee: e.target.value }))} className="border rounded-lg px-3 py-2 w-full text-sm" />
                  </FormField>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={() => setShowManualTxnModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm">Cancel</button>
                  <button type="submit" className="bg-black text-white px-4 py-2 rounded-lg text-sm">Add Transaction</button>
                </div>
              </form>
            </ModalOverlay>
          )}

          {/* Import CSV Modal */}
          {showImportModal && (
            <ModalOverlay title="Import Bank Transactions (CSV)" onClose={() => setShowImportModal(false)}>
              <form onSubmit={handleImportCSV} className="space-y-3">
                <FormField label="Bank Account *">
                  <select value={importForm.bank_account_id} onChange={e => setImportForm(p => ({ ...p, bank_account_id: e.target.value }))} className="border rounded-lg px-3 py-2 w-full text-sm" required>
                    <option value="">Select…</option>
                    {bankAccounts.filter(a => a.is_active).map(a => <option key={a.id} value={a.id}>{a.account_name}</option>)}
                  </select>
                </FormField>
                <FormField label="CSV Data *">
                  <textarea value={importForm.csvText} onChange={e => setImportForm(p => ({ ...p, csvText: e.target.value }))} className="border rounded-lg px-3 py-2 w-full text-sm font-mono" rows={10} placeholder={"date,description,amount,reference,balance\n2026-02-01,Supplier Payment,-5000,CHQ001,45000\n2026-02-02,Customer Deposit,10000,UPI123,55000"} required />
                </FormField>
                <p className="text-xs text-gray-500">Expected columns: date, description, amount, reference, balance, payee, category. Header row required.</p>
                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={() => setShowImportModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm">Cancel</button>
                  <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm">Import</button>
                </div>
              </form>
            </ModalOverlay>
          )}

          {/* New Reconciliation Modal */}
          {showReconModal && (
            <ModalOverlay title="Start Reconciliation" onClose={() => setShowReconModal(false)}>
              <form onSubmit={handleStartReconciliation} className="space-y-3">
                <FormField label="Bank Account *">
                  <select value={reconForm.bank_account_id} onChange={e => setReconForm(p => ({ ...p, bank_account_id: e.target.value }))} className="border rounded-lg px-3 py-2 w-full text-sm" required>
                    <option value="">Select…</option>
                    {bankAccounts.filter(a => a.is_active).map(a => <option key={a.id} value={a.id}>{a.account_name}</option>)}
                  </select>
                </FormField>
                <FormField label="Reconciliation Date *">
                  <input type="date" value={reconForm.reconciliation_date} onChange={e => setReconForm(p => ({ ...p, reconciliation_date: e.target.value }))} className="border rounded-lg px-3 py-2 w-full text-sm" required />
                </FormField>
                <div className="grid grid-cols-2 gap-3">
                  <FormField label="Period Start *">
                    <input type="date" value={reconForm.period_start} onChange={e => setReconForm(p => ({ ...p, period_start: e.target.value }))} className="border rounded-lg px-3 py-2 w-full text-sm" required />
                  </FormField>
                  <FormField label="Period End *">
                    <input type="date" value={reconForm.period_end} onChange={e => setReconForm(p => ({ ...p, period_end: e.target.value }))} className="border rounded-lg px-3 py-2 w-full text-sm" required />
                  </FormField>
                </div>
                <FormField label="Statement Ending Balance *">
                  <input type="number" step="0.01" value={reconForm.statement_balance} onChange={e => setReconForm(p => ({ ...p, statement_balance: e.target.value }))} className="border rounded-lg px-3 py-2 w-full text-sm" required />
                </FormField>
                <FormField label="Notes">
                  <textarea value={reconForm.notes} onChange={e => setReconForm(p => ({ ...p, notes: e.target.value }))} className="border rounded-lg px-3 py-2 w-full text-sm" rows={2} />
                </FormField>
                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={() => setShowReconModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm">Cancel</button>
                  <button type="submit" className="bg-black text-white px-4 py-2 rounded-lg text-sm">Start Reconciliation</button>
                </div>
              </form>
            </ModalOverlay>
          )}
        </>
      )}

      {/* ═══════ FINANCIAL RATIOS ═══════ */}
      {activeTab === TABS.RATIOS && (
        <div>
          {/* Date Range Filter */}
          <div className="bg-white p-4 rounded-xl border mb-6 flex flex-wrap gap-3 items-end">
            <FormField label="Balance Sheet As-Of">
              <input type="date" value={ratioDateRange.as_of_date} onChange={e => setRatioDateRange(p => ({ ...p, as_of_date: e.target.value }))} className="border rounded-lg px-3 py-2 text-sm" />
            </FormField>
            <FormField label="P&L Period Start">
              <input type="date" value={ratioDateRange.period_start} onChange={e => setRatioDateRange(p => ({ ...p, period_start: e.target.value }))} className="border rounded-lg px-3 py-2 text-sm" />
            </FormField>
            <FormField label="P&L Period End">
              <input type="date" value={ratioDateRange.period_end} onChange={e => setRatioDateRange(p => ({ ...p, period_end: e.target.value }))} className="border rounded-lg px-3 py-2 text-sm" />
            </FormField>
            <button onClick={fetchFinancialRatios} className="bg-black text-white px-4 py-2 rounded-lg text-sm flex items-center gap-1"><FiRefreshCw size={14} /> Refresh</button>
          </div>

          {financialRatios ? (
            <>
              {/* Underlying Data Summary */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                {[
                  { label: 'Total Assets', value: financialRatios.underlying?.total_assets, color: 'blue' },
                  { label: 'Total Liabilities', value: financialRatios.underlying?.total_liabilities, color: 'red' },
                  { label: 'Total Equity', value: financialRatios.underlying?.total_equity, color: 'purple' },
                  { label: 'Total Revenue', value: financialRatios.underlying?.total_revenue, color: 'green' },
                  { label: 'Net Income', value: financialRatios.underlying?.net_income, color: financialRatios.underlying?.net_income >= 0 ? 'green' : 'red' },
                ].map((c, i) => (
                  <div key={i} className="bg-white p-4 rounded-xl border">
                    <div className="text-xs text-gray-500 mb-1">{c.label}</div>
                    <div className={`text-lg font-bold text-${c.color}-700`}>{formatCurrency(c.value || 0)}</div>
                  </div>
                ))}
              </div>

              {/* Ratio Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                {/* Liquidity Ratios */}
                <div className="bg-white rounded-xl border">
                  <div className="px-4 py-3 border-b bg-blue-50 rounded-t-xl">
                    <h3 className="font-semibold text-blue-800 flex items-center gap-2"><FiActivity size={16} /> Liquidity Ratios</h3>
                    <p className="text-xs text-blue-600 mt-0.5">Ability to meet short-term obligations</p>
                  </div>
                  <div className="p-4 space-y-3">
                    {(() => {
                      const liq = financialRatios.ratios?.liquidity || {};
                      return [
                        { name: 'Current Ratio', value: liq.current_ratio, format: 'ratio', benchmark: '≥ 1.5', good: v => v >= 1.5 },
                        { name: 'Quick Ratio', value: liq.quick_ratio, format: 'ratio', benchmark: '≥ 1.0', good: v => v >= 1.0 },
                        { name: 'Cash Ratio', value: liq.cash_ratio, format: 'ratio', benchmark: '≥ 0.5', good: v => v >= 0.5 },
                        { name: 'Working Capital', value: liq.working_capital, format: 'currency', benchmark: '> 0', good: v => v > 0 },
                      ].map((r, i) => (
                        <div key={i} className="flex justify-between items-center py-2 border-b last:border-0">
                          <div>
                            <div className="font-medium text-sm">{r.name}</div>
                            <div className="text-xs text-gray-400">Benchmark: {r.benchmark}</div>
                          </div>
                          <div className="text-right">
                            <div className={`text-lg font-bold ${r.value != null && r.good(r.value) ? 'text-green-700' : r.value != null ? 'text-red-600' : 'text-gray-400'}`}>
                              {r.value != null ? (r.format === 'currency' ? formatCurrency(r.value) : r.value.toFixed(2)) : 'N/A'}
                            </div>
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>

                {/* Profitability Ratios */}
                <div className="bg-white rounded-xl border">
                  <div className="px-4 py-3 border-b bg-green-50 rounded-t-xl">
                    <h3 className="font-semibold text-green-800 flex items-center gap-2"><FiTrendingUp size={16} /> Profitability Ratios</h3>
                    <p className="text-xs text-green-600 mt-0.5">Earnings relative to revenue and capital</p>
                  </div>
                  <div className="p-4 space-y-3">
                    {(() => {
                      const prof = financialRatios.ratios?.profitability || {};
                      return [
                        { name: 'Gross Profit Margin', value: prof.gross_profit_margin, benchmark: '≥ 60%', good: v => v >= 0.6 },
                        { name: 'Operating Profit Margin', value: prof.operating_profit_margin, benchmark: '≥ 10%', good: v => v >= 0.1 },
                        { name: 'Net Profit Margin', value: prof.net_profit_margin, benchmark: '≥ 5%', good: v => v >= 0.05 },
                        { name: 'Return on Assets', value: prof.return_on_assets, benchmark: '≥ 5%', good: v => v >= 0.05 },
                        { name: 'Return on Equity', value: prof.return_on_equity, benchmark: '≥ 15%', good: v => v >= 0.15 },
                      ].map((r, i) => (
                        <div key={i} className="flex justify-between items-center py-2 border-b last:border-0">
                          <div>
                            <div className="font-medium text-sm">{r.name}</div>
                            <div className="text-xs text-gray-400">Benchmark: {r.benchmark}</div>
                          </div>
                          <div className="text-right">
                            <div className={`text-lg font-bold ${r.value != null && r.good(r.value) ? 'text-green-700' : r.value != null ? 'text-red-600' : 'text-gray-400'}`}>
                              {r.value != null ? `${(r.value * 100).toFixed(1)}%` : 'N/A'}
                            </div>
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>

                {/* Leverage Ratios */}
                <div className="bg-white rounded-xl border">
                  <div className="px-4 py-3 border-b bg-orange-50 rounded-t-xl">
                    <h3 className="font-semibold text-orange-800 flex items-center gap-2"><FiLayers size={16} /> Leverage / Solvency</h3>
                    <p className="text-xs text-orange-600 mt-0.5">Capital structure and long-term solvency</p>
                  </div>
                  <div className="p-4 space-y-3">
                    {(() => {
                      const lev = financialRatios.ratios?.leverage || {};
                      return [
                        { name: 'Debt-to-Equity', value: lev.debt_to_equity, benchmark: '≤ 2.0', good: v => v <= 2.0 },
                        { name: 'Debt-to-Assets', value: lev.debt_to_assets, benchmark: '≤ 0.6', good: v => v <= 0.6 },
                        { name: 'Equity Ratio', value: lev.equity_ratio, benchmark: '≥ 0.4', good: v => v >= 0.4 },
                      ].map((r, i) => (
                        <div key={i} className="flex justify-between items-center py-2 border-b last:border-0">
                          <div>
                            <div className="font-medium text-sm">{r.name}</div>
                            <div className="text-xs text-gray-400">Benchmark: {r.benchmark}</div>
                          </div>
                          <div className="text-right">
                            <div className={`text-lg font-bold ${r.value != null && r.good(r.value) ? 'text-green-700' : r.value != null ? 'text-red-600' : 'text-gray-400'}`}>
                              {r.value != null ? r.value.toFixed(2) : 'N/A'}
                            </div>
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>

                {/* Efficiency Ratios */}
                <div className="bg-white rounded-xl border">
                  <div className="px-4 py-3 border-b bg-purple-50 rounded-t-xl">
                    <h3 className="font-semibold text-purple-800 flex items-center gap-2"><FiRepeat size={16} /> Efficiency / Activity</h3>
                    <p className="text-xs text-purple-600 mt-0.5">How effectively assets and liabilities are managed</p>
                  </div>
                  <div className="p-4 space-y-3">
                    {(() => {
                      const eff = financialRatios.ratios?.efficiency || {};
                      return [
                        { name: 'Receivable Turnover', value: eff.receivable_turnover, unit: 'x', benchmark: '≥ 10x' },
                        { name: 'Days Sales Outstanding', value: eff.days_sales_outstanding, unit: ' days', benchmark: '≤ 30 days' },
                        { name: 'Payable Turnover', value: eff.payable_turnover, unit: 'x', benchmark: '≥ 8x' },
                        { name: 'Days Payable Outstanding', value: eff.days_payable_outstanding, unit: ' days', benchmark: '≤ 45 days' },
                        { name: 'Asset Turnover', value: eff.asset_turnover, unit: 'x', benchmark: '≥ 1.5x' },
                        { name: 'Inventory Turnover', value: eff.inventory_turnover, unit: 'x', benchmark: '≥ 20x' },
                      ].map((r, i) => (
                        <div key={i} className="flex justify-between items-center py-2 border-b last:border-0">
                          <div>
                            <div className="font-medium text-sm">{r.name}</div>
                            <div className="text-xs text-gray-400">Benchmark: {r.benchmark}</div>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-gray-800">
                              {r.value != null ? `${r.value.toFixed(1)}${r.unit}` : 'N/A'}
                            </div>
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>

                {/* Restaurant KPIs */}
                <div className="bg-white rounded-xl border md:col-span-2">
                  <div className="px-4 py-3 border-b bg-yellow-50 rounded-t-xl">
                    <h3 className="font-semibold text-yellow-800 flex items-center gap-2"><FiTarget size={16} /> Restaurant KPIs</h3>
                    <p className="text-xs text-yellow-600 mt-0.5">Industry-specific performance indicators</p>
                  </div>
                  <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                    {(() => {
                      const rest = financialRatios.ratios?.restaurant || {};
                      return [
                        { name: 'Food Cost %', value: rest.food_cost_percentage, benchmark: '≤ 35%', good: v => v <= 0.35 },
                        { name: 'Expense Ratio', value: rest.expense_ratio, benchmark: '≤ 90%', good: v => v <= 0.9 },
                        { name: 'Operating Expense Ratio', value: rest.operating_expense_ratio, benchmark: '≤ 65%', good: v => v <= 0.65 },
                        { name: 'Revenue/Day', value: rest.revenue_per_day, isCurrency: true, benchmark: 'Varies' },
                      ].map((r, i) => (
                        <div key={i} className="text-center p-3 bg-gray-50 rounded-lg">
                          <div className="text-xs text-gray-500 mb-1">{r.name}</div>
                          <div className={`text-2xl font-bold ${r.value != null && r.good ? (r.good(r.value) ? 'text-green-700' : 'text-red-600') : 'text-gray-800'}`}>
                            {r.value != null ? (r.isCurrency ? formatCurrency(r.value) : `${(r.value * 100).toFixed(1)}%`) : 'N/A'}
                          </div>
                          <div className="text-xs text-gray-400 mt-1">{r.benchmark}</div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              </div>

              {/* Underlying Data */}
              <details className="mt-6">
                <summary className="text-sm text-gray-500 cursor-pointer hover:text-gray-700">View Underlying Data</summary>
                <div className="mt-3 bg-white rounded-xl border overflow-x-auto">
                  <table className="w-full text-sm">
                    <tbody>
                      {financialRatios.underlying && Object.entries(financialRatios.underlying).map(([key, val]) => (
                        <tr key={key} className="border-b last:border-0 hover:bg-gray-50">
                          <td className="px-4 py-2 text-gray-500 capitalize">{key.replace(/_/g, ' ')}</td>
                          <td className="px-4 py-2 text-right font-medium">{formatCurrency(val)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            </>
          ) : (
            <div className="text-center py-12 text-gray-400">
              <FiBarChart2 size={48} className="mx-auto mb-3 opacity-50" />
              <p>No financial data available. Post journal entries to see ratios.</p>
            </div>
          )}
        </div>
      )}

      {/* ═══════ IMPORT / EXPORT TAB ═══════ */}
      {activeTab === TABS.IMPORT_EXPORT && (
        <div className="space-y-6">
          {/* Sub-nav */}
          <div className="flex gap-2">
            {['export', 'import'].map(v => (
              <button key={v} onClick={() => { setImportExportView(v); setImportResult(null); }} className={`px-4 py-2 rounded-lg text-sm font-medium ${importExportView === v ? 'bg-black text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {v === 'export' ? '📤 Export Data' : '📥 Import Data'}
              </button>
            ))}
          </div>

          {/* ── EXPORT VIEW ── */}
          {importExportView === 'export' && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <h4 className="font-semibold text-blue-900 mb-1">Export Accounting Data</h4>
                <p className="text-sm text-blue-700">Download your data as CSV or JSON files. CSV is suitable for spreadsheets, JSON for programmatic use.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { key: 'coa', label: 'Chart of Accounts', desc: 'All accounts with codes, types, and categories', icon: FiList, color: 'blue' },
                  { key: 'journals', label: 'Journal Entries', desc: 'All journal entries with line details', icon: FiBook, color: 'purple' },
                  { key: 'expenses', label: 'Expenses', desc: 'All expense records with account details', icon: FiDollarSign, color: 'red' },
                  { key: 'trial-balance', label: 'Trial Balance', desc: 'Account balances with debits and credits', icon: FiFileText, color: 'green' },
                  { key: 'vendors', label: 'Vendors', desc: 'Vendor list with contact and payment info', icon: FiUsers, color: 'orange' },
                  { key: 'ar-invoices', label: 'AR Invoices', desc: 'All receivable invoices with customer info', icon: FiArrowRight, color: 'teal' },
                  { key: 'ap-bills', label: 'AP Bills', desc: 'All payable bills with vendor details', icon: FiShoppingCart, color: 'yellow' },
                  { key: 'bank-transactions', label: 'Bank Transactions', desc: 'All bank transactions with reconciliation status', icon: FiCreditCard, color: 'indigo' },
                ].map(item => {
                  const colors = { blue: 'bg-blue-50 border-blue-200', purple: 'bg-purple-50 border-purple-200', red: 'bg-red-50 border-red-200', green: 'bg-green-50 border-green-200', orange: 'bg-orange-50 border-orange-200', teal: 'bg-teal-50 border-teal-200', yellow: 'bg-yellow-50 border-yellow-200', indigo: 'bg-indigo-50 border-indigo-200' };
                  const iconColors = { blue: 'text-blue-600', purple: 'text-purple-600', red: 'text-red-600', green: 'text-green-600', orange: 'text-orange-600', teal: 'text-teal-600', yellow: 'text-yellow-600', indigo: 'text-indigo-600' };
                  return (
                    <div key={item.key} className={`rounded-xl border p-4 ${colors[item.color]}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <item.icon size={18} className={iconColors[item.color]} />
                        <h4 className="font-semibold text-gray-800">{item.label}</h4>
                      </div>
                      <p className="text-xs text-gray-500 mb-3">{item.desc}</p>
                      <div className="flex gap-2">
                        <button
                          disabled={exportLoading === item.key + '_csv'}
                          onClick={async () => {
                            setExportLoading(item.key + '_csv');
                            try {
                              const resp = await api.get(`/api/accounting/export/${item.key}`, { responseType: 'blob' });
                              const url = window.URL.createObjectURL(new Blob([resp.data]));
                              const a = document.createElement('a'); a.href = url; a.download = `${item.key.replace(/-/g, '_')}.csv`; a.click();
                              window.URL.revokeObjectURL(url);
                            } catch (e) { showAlert('Export failed: ' + (e.response?.data?.error || e.message), 'error'); }
                            setExportLoading('');
                          }}
                          className="flex-1 bg-white border border-gray-300 text-gray-700 px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-gray-50 flex items-center justify-center gap-1"
                        >
                          <FiDownload size={12} /> {exportLoading === item.key + '_csv' ? 'Downloading...' : 'CSV'}
                        </button>
                        <button
                          disabled={exportLoading === item.key + '_json'}
                          onClick={async () => {
                            setExportLoading(item.key + '_json');
                            try {
                              const resp = await api.get(`/api/accounting/export/${item.key}?format=json`);
                              const blob = new Blob([JSON.stringify(resp.data, null, 2)], { type: 'application/json' });
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement('a'); a.href = url; a.download = `${item.key.replace(/-/g, '_')}.json`; a.click();
                              window.URL.revokeObjectURL(url);
                            } catch (e) { showAlert('Export failed: ' + (e.response?.data?.error || e.message), 'error'); }
                            setExportLoading('');
                          }}
                          className="flex-1 bg-white border border-gray-300 text-gray-700 px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-gray-50 flex items-center justify-center gap-1"
                        >
                          <FiDownload size={12} /> {exportLoading === item.key + '_json' ? 'Downloading...' : 'JSON'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── IMPORT VIEW ── */}
          {importExportView === 'import' && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <h4 className="font-semibold text-amber-900 mb-1">Import Data from CSV</h4>
                <p className="text-sm text-amber-700">Upload CSV data to bulk-create records. Download a template first to see the expected format. Existing records with conflicting keys will be skipped.</p>
              </div>

              {/* Import type selector */}
              <div className="flex flex-wrap gap-2">
                {[
                  { key: 'chart_of_accounts', label: 'Chart of Accounts', icon: FiList },
                  { key: 'expenses', label: 'Expenses', icon: FiDollarSign },
                  { key: 'vendors', label: 'Vendors', icon: FiUsers },
                ].map(item => (
                  <button key={item.key} onClick={() => { setImportType(item.key); setImportCsvText(''); setImportResult(null); }}
                    className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5 ${importType === item.key ? 'bg-black text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                    <item.icon size={14} /> {item.label}
                  </button>
                ))}
              </div>

              {/* Template info */}
              {importTemplates && importTemplates[importType] && (
                <div className="bg-white rounded-xl border p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-gray-800">Template: {importTemplates[importType].filename}</h4>
                    <button
                      onClick={() => {
                        const t = importTemplates[importType];
                        const content = t.headers + '\n' + t.sample_row;
                        const blob = new Blob([content], { type: 'text/csv' });
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a'); a.href = url; a.download = t.filename; a.click();
                        window.URL.revokeObjectURL(url);
                      }}
                      className="bg-blue-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-blue-700 flex items-center gap-1"
                    >
                      <FiDownload size={12} /> Download Template
                    </button>
                  </div>
                  <div className="text-xs space-y-1">
                    <p><span className="font-medium text-gray-700">Required columns:</span> <span className="text-red-600">{importTemplates[importType].required.join(', ')}</span></p>
                    <p><span className="font-medium text-gray-700">Notes:</span> <span className="text-gray-500">{importTemplates[importType].notes}</span></p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-2 overflow-x-auto">
                    <p className="text-xs font-mono text-gray-500">{importTemplates[importType].headers}</p>
                    <p className="text-xs font-mono text-gray-600">{importTemplates[importType].sample_row}</p>
                  </div>
                </div>
              )}

              {/* CSV input */}
              <div className="bg-white rounded-xl border p-4 space-y-3">
                <h4 className="font-semibold text-gray-800">Paste CSV Data</h4>
                <textarea
                  value={importCsvText}
                  onChange={e => setImportCsvText(e.target.value)}
                  rows={10}
                  placeholder={`Paste your CSV content here...\n\nOr use the file picker below to load from a file.`}
                  className="w-full border border-gray-300 rounded-lg p-3 text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <div className="flex items-center gap-3">
                  <label className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-200 cursor-pointer flex items-center gap-1.5">
                    <FiUploadCloud size={14} /> Load from File
                    <input type="file" accept=".csv,.txt" className="hidden" onChange={e => {
                      const file = e.target.files[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onload = ev => setImportCsvText(ev.target.result);
                      reader.readAsText(file);
                      e.target.value = '';
                    }} />
                  </label>
                  <button
                    disabled={!importCsvText.trim()}
                    onClick={async () => {
                      const importPaths = { chart_of_accounts: 'coa', expenses: 'expenses', vendors: 'vendors' };
                      try {
                        setImportResult(null);
                        const { data } = await api.post(`/api/accounting/import/${importPaths[importType]}`, { csv_data: importCsvText });
                        setImportResult(data);
                        if (data.imported > 0) {
                          showAlert(`Successfully imported ${data.imported} records`, 'success');
                          // Refresh related data
                          if (importType === 'chart_of_accounts') fetchAccounts();
                          if (importType === 'expenses') fetchExpenses();
                          if (importType === 'vendors') fetchVendors();
                        }
                      } catch (e) {
                        setImportResult({ error: e.response?.data?.error || e.message, imported: 0, skipped: 0, errors: [] });
                        showAlert('Import failed: ' + (e.response?.data?.error || e.message), 'error');
                      }
                    }}
                    className="bg-green-600 text-white px-4 py-1.5 rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                  >
                    <FiUploadCloud size={14} /> Import Data
                  </button>
                  {importCsvText && (
                    <button onClick={() => { setImportCsvText(''); setImportResult(null); }} className="text-gray-400 hover:text-gray-600"><FiX size={16} /></button>
                  )}
                </div>
              </div>

              {/* Import results */}
              {importResult && (
                <div className={`rounded-xl border p-4 ${importResult.error && !importResult.imported ? 'bg-red-50 border-red-200' : importResult.skipped > 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    {importResult.error && !importResult.imported ? <FiAlertTriangle className="text-red-600" /> : importResult.skipped > 0 ? <FiAlertTriangle className="text-amber-600" /> : <FiCheckCircle className="text-green-600" />}
                    Import Results
                  </h4>
                  {importResult.error && !importResult.imported && <p className="text-sm text-red-700">{importResult.error}</p>}
                  {importResult.message && <p className="text-sm text-gray-700 mb-2">{importResult.message}</p>}
                  <div className="flex gap-4 text-sm">
                    <span className="text-green-700 font-medium">✓ Imported: {importResult.imported || 0}</span>
                    <span className="text-amber-700 font-medium">⚠ Skipped: {importResult.skipped || 0}</span>
                  </div>
                  {importResult.errors && importResult.errors.length > 0 && (
                    <details className="mt-2">
                      <summary className="text-xs text-gray-600 cursor-pointer hover:text-gray-800">Show {importResult.errors.length} error(s)</summary>
                      <ul className="mt-1 space-y-0.5 max-h-40 overflow-y-auto">
                        {importResult.errors.map((e, i) => <li key={i} className="text-xs text-red-600 font-mono">• {e}</li>)}
                      </ul>
                    </details>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {showManualJournalModal && (
        <ModalOverlay title="Create Manual Journal" onClose={() => setShowManualJournalModal(false)}>
          <form onSubmit={handleCreateManualJournal} className="space-y-4">
            <FormField label="Entry Date">
              <input type="date" value={manualJournalForm.entry_date} onChange={e => setManualJournalForm(f => ({ ...f, entry_date: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </FormField>
            <FormField label="Description *">
              <input type="text" required value={manualJournalForm.description} onChange={e => setManualJournalForm(f => ({ ...f, description: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="e.g. Month-end adjustment" />
            </FormField>
            <FormField label="Memo">
              <input type="text" value={manualJournalForm.memo} onChange={e => setManualJournalForm(f => ({ ...f, memo: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Optional notes" />
            </FormField>
            <FormField label="Currency (optional)">
              <select value={manualJournalForm.currency_code} onChange={e => setManualJournalForm(f => ({ ...f, currency_code: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">Base ({getBaseCurrency().code})</option>
                {getActiveCurrencies().filter(c => !c.is_base).map(c => <option key={c.id} value={c.code}>{c.code} – {c.name} ({c.symbol})</option>)}
              </select>
            </FormField>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">Journal Lines</label>
                <button type="button" onClick={addManualJournalLine} className="text-blue-600 text-xs flex items-center gap-1"><FiPlus size={12} /> Add Line</button>
              </div>
              <div className="space-y-2">
                {manualJournalForm.lines.map((line, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-gray-50 rounded-lg p-2">
                    <select value={line.account_id} onChange={e => updateManualJournalLine(idx, 'account_id', e.target.value)} className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-xs" required>
                      <option value="">Select account…</option>
                      {accounts.filter(a => a.is_active).map(a => <option key={a.id} value={a.id}>{a.account_code} – {a.account_name}</option>)}
                    </select>
                    <input type="number" step="0.01" min="0" placeholder="Debit" value={line.debit} onChange={e => updateManualJournalLine(idx, 'debit', e.target.value)} className="w-20 border border-gray-300 rounded px-2 py-1.5 text-xs text-right" />
                    <input type="number" step="0.01" min="0" placeholder="Credit" value={line.credit} onChange={e => updateManualJournalLine(idx, 'credit', e.target.value)} className="w-20 border border-gray-300 rounded px-2 py-1.5 text-xs text-right" />
                    {manualJournalForm.lines.length > 2 && (
                      <button type="button" onClick={() => removeManualJournalLine(idx)} className="text-red-400 hover:text-red-600"><FiX size={14} /></button>
                    )}
                  </div>
                ))}
              </div>
              {(() => {
                const totalDr = manualJournalForm.lines.reduce((s, l) => s + (Number(l.debit) || 0), 0);
                const totalCr = manualJournalForm.lines.reduce((s, l) => s + (Number(l.credit) || 0), 0);
                const balanced = Math.abs(totalDr - totalCr) < 0.01;
                return (
                  <div className={`mt-2 text-xs flex justify-between ${balanced ? 'text-green-600' : 'text-red-500'}`}>
                    <span>Dr: {formatCurrency(totalDr)}</span>
                    <span>Cr: {formatCurrency(totalCr)}</span>
                    <span>{balanced ? '✓ Balanced' : '✗ Unbalanced'}</span>
                  </div>
                );
              })()}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowManualJournalModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm">Cancel</button>
              <button type="submit" className="bg-black text-white px-4 py-2 rounded-lg text-sm">Post Journal</button>
            </div>
          </form>
        </ModalOverlay>
      )}
    </Layout>
  );
}

// ─── Reusable sub-components ────────────────────────────────
function SummaryCard({ icon: Icon, label, value, color }) {
  const colorMap = {
    green: 'text-green-600 bg-green-50',
    red: 'text-red-600 bg-red-50',
    blue: 'text-blue-600 bg-blue-50',
    yellow: 'text-yellow-600 bg-yellow-50',
    gray: 'text-gray-500 bg-gray-50',
  };
  const c = colorMap[color] || colorMap.gray;
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-start gap-3">
      <div className={`p-2 rounded-lg ${c}`}><Icon size={20} /></div>
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-xl font-bold text-black">{value}</p>
      </div>
    </div>
  );
}

function ModalOverlay({ children, onClose, title }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-black">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><FiX size={20} /></button>
        </div>
        <div className="px-6 py-4">{children}</div>
      </div>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
    </div>
  );
}

export default Accounting;
